<template>
  <div class="player-tactical-map">
    <canvas
      ref="canvas"
      class="tactical-canvas"
      :class="{ 'tactical-canvas--aiming': selectedAbility }"
      @mousedown="handleMouseDown"
      @mousemove="handleMouseMove"
      @mouseup="handleMouseUp"
      @mouseleave="handleMouseLeave"
      @wheel.prevent="handleWheel"
      @click="handleMapClick"
    ></canvas>

    <!-- The pedestals are real geometry, drawn between the floor and the figures
         standing on them. Each is placed from the four morphed corners of its own
         cell, so a plinth cannot drift from the ground it stands on.

         Deliberately NOT CSS-scaled by the zoom, unlike the token overlay. A canvas
         scaled by CSS is rendered once at 1x and then stretched, so at 270% the
         props were four real pixels blown up to nine - soft, crawling edges on a
         machined metal object sitting on a sharp painted floor, which is exactly
         what reads as a decal pasted over the map. The zoom goes into the geometry
         instead and every frame is drawn at the display's own resolution. -->
    <canvas ref="stageCanvas" class="pedestal-stage"></canvas>

    <!-- Impacts are cast onto the floor the props stand on, so this sits directly
         over them and under the tokens. Same contract as the stage above it: sized
         to the container, never CSS-scaled by the zoom - the zoom is folded into the
         four grid corners handed to the shader, so a cell drawn here and a cell
         painted on the 2D board are one function of one set of numbers. -->
    <canvas ref="effectsCanvas" class="impact-stage"></canvas>

    <!-- Character Tokens Overlay (pointer-events: none on container, auto on tokens) -->
    <div
      class="tokens-overlay"
      :class="{ 'tokens-overlay--panning': isPanning }"
      :style="{
        transform: `translate(var(--board-shake-x, 0px), var(--board-shake-y, 0px)) scale(${zoom})`,
        transformOrigin: 'center center'
      }"
    >
      <!-- Debug: Token count display. Under fog an empty board is the expected state,
           not a fault, so the warning stands down whenever sight explains it. -->
      <div v-if="visibleObjects.length === 0 && !fogActive" style="position: absolute; top: 100px; left: 20px; background: rgba(255,0,0,0.8); color: white; padding: 10px; z-index: 1000; pointer-events: none;">
        ⚠️ No tokens visible (spawned_objects: {{ tacticalMapData?.spawned_objects?.length || 0 }})
      </div>

      <div
        v-for="obj in visibleObjects"
        :key="obj.id"
        class="token-wrapper"
        :data-token-id="obj.id"
        :class="[
          objectRelationClass(obj),
          {
            'token-selected': selectedObjectId === obj.id,
            'token-hovered': hoveredObjectId === obj.id,
            'token-player': obj.type === 'character',
            'token-item': obj.type === 'worlditem',
            'token-aimed': aimedCharacterIds.has(obj.id),
            'token-out-of-reach': isOutOfCastReach(obj)
          }
        ]"
        :style="getTokenStyle(obj)"
      >
        <!-- Characters project as holograms standing on a pedestal in their cell.
             The pedestal itself is drawn by the 3D stage below, so the token asks
             for no plate of its own - see the `plate` prop for why one sprite and
             one prop cannot both be there. -->
        <HoloToken
          v-if="obj.type === 'character'"
          :art="tokenArt(obj)"
          :name="obj.name"
          matte="auto"
          :plate="pedestalPlate"
          :emit-y="holoEmitY(obj)"
          :color="tokenProjectionColor(obj)"
          :size="holoSize(obj)"
          :style="holoCounterScale()"
          :ground-tilt="holoGroundTilt(obj)"
          :marker="holoMarker(obj)"
          :side-color="sideRingColor(obj)"
          :side-emblem="sideEmblem(obj)"
          :signed="obj.game_master_mode"
          :quality="holoQuality"
          :state="holoState(obj)"
          :intensity="holo.intensity"
          :speed="holo.speed"
          :seed="objectSeed(obj)"
          :selected="selectedObjectId === obj.id"
        />
        <!-- Render ObjectToken for items (smaller) -->
        <ObjectToken
          v-else
          :image="obj.avatar"
        />
        <div class="token-label" :style="labelStyle">
          <div v-if="obj.relation === 'self'" class="token-self-marker">YOU</div>
          <div class="token-name" :class="{ 'item-name': obj.type === 'worlditem' }">{{ obj.name }}</div>
        </div>
      </div>

      <!--
        Where the move ends. The path traces the route but says nothing about the
        arrival, so the destination is marked with an emitter plate and nothing on
        it — waiting, unlabelled, carrying no marker.

        It used to stand the character's own projection there. That reads as the
        figure being in two places at once, which is exactly the question a player
        is using the preview to answer, and the copy competes with the real token
        for the eye while it does so. An empty plate cannot be misread: it is a
        place, not a person.
      -->
      <div
        v-if="movementGhost"
        class="token-wrapper token-ghost"
        :style="getTokenStyle(movementGhost)"
      >
        <HoloToken
          variant="plate"
          :color="relationCssColor(movementGhost.relation)"
          :size="holoSize(movementGhost)"
          :style="holoCounterScale()"
          :ground-tilt="holoGroundTilt(movementGhost)"
          :plate="pedestalPlate"
          :emit-y="holoEmitY(movementGhost)"
          marker="none"
          :quality="holoQuality"
          state="dimmed"
          :intensity="holo.intensity"
          :speed="holo.speed"
          :seed="objectSeed(movementGhost)"
        />
      </div>
    </div>

    <!-- The allegiance legend is deliberately gone. It restated what the board
         already says — a token is gold, green or red, and its own name label
         and ring say who it is — while occupying the left edge permanently and
         growing an entry every time a new highlight was added. Colour meaning
         belongs in the tokens, not in a key beside them. -->

    <!-- Grid Controls HUD (Top Left) -->
    <div class="grid-controls-hud hud-element" style="pointer-events: auto;">
      <button
        class="control-btn"
        :class="{ 'active': gridVisible }"
        @click="toggleGrid"
        title="Toggle Grid Visibility"
      >
        <span class="control-icon">▦</span>
        <span class="control-label">Grid</span>
      </button>
      <button
        class="control-btn passable-toggle"
        :class="{ 'active': passableHighlightVisible }"
        @click="togglePassableHighlight"
        title="Toggle Passable Cells Highlight"
      >
        <span class="control-icon">◉</span>
        <span class="control-label">Passable</span>
      </button>
      <button
        class="control-btn"
        :class="{ 'active': skipReelsPreference }"
        @click="toggleSkipReelsPreference"
        :title="$t('tacticalMap.skip.preference')"
      >
        <span class="control-icon">⏭</span>
        <span class="control-label">{{ $t('tacticalMap.skip.preference') }}</span>
      </button>
    </div>

    <!-- Reel skip pill - only while a phase's animation is actually in flight -->
    <transition name="hud-hint">
      <button
        v-if="reelInFlight"
        type="button"
        class="reel-skip-btn hud-element"
        style="pointer-events: auto;"
        @click="skipReel"
      >
        {{ $t('tacticalMap.skip.button') }}
      </button>
    </transition>

    <!-- Transient rejection / guidance hint -->
    <transition name="hud-hint">
      <div v-if="actionHint" class="action-hint hud-element" :class="`action-hint--${actionHint.tone}`" style="pointer-events: none;">
        {{ actionHint.text }}
      </div>
    </transition>

    <!-- Zoom HUD (HUD element - interactive buttons) -->
    <div class="zoom-hud hud-element" style="pointer-events: auto;">
      <button class="zoom-btn" @click="zoomOut" :disabled="zoom <= minZoom" title="Zoom Out">−</button>
      <span class="zoom-display">{{ zoomPercentage }}%</span>
      <button class="zoom-btn" @click="zoomIn" :disabled="zoom >= maxZoom" title="Zoom In">+</button>
      <button class="zoom-btn zoom-reset" @click="resetZoom" title="Reset Zoom">⟲</button>
    </div>


    <!-- Loading State (HUD element - blocks all interaction when visible) -->
    <div v-if="loading" class="loading-overlay hud-element" style="pointer-events: auto;">
      <span class="dx-loader" aria-hidden="true"></span>
      <p>Loading Tactical Map...</p>
    </div>
  </div>
</template>

<script>
import { TacticalmapApi } from '@/api/dx-backend';
import { GameApiConfig } from '@/api/backendService';
import { ensureConnection } from '@/api/dx-websocket/index.ts';
import HoloToken from '@/components/CharacterToken/HoloToken.vue';
import ObjectToken from '@/components/CharacterToken/ObjectToken.vue';
import {
  HOLO_BASE_CENTER_Y,
  HOLO_CONTACT_CENTER_Y,
  HOLO_CONTACT_DROP,
  HOLO_GEOMETRY,
  HOLO_GROUND,
  holoWidthForBase,
} from '@/components/CharacterToken/holo/geometry';
import { cellGroundBasis } from '@/components/CharacterToken/holo/groundPlane';
import { PEDESTAL_FOOTPRINT } from '@/services/model3d/pedestalMesh';
import { markRaw } from 'vue';
import PedestalStage from '@/services/model3d/PedestalStage';
import { ImpactBoardLayer, boardProjection, maskCellsToVision } from '@/services/impact/boardLayer';
import { playScheduled } from '@/services/impact/schedule';
import { resolveAim } from '@/services/impact/aim';
import { AimField, AIM_DENIED_COLOR } from '@/services/impact/aimField';
import { aimPalette } from '@/services/impact/aimMaterial';
import { BATTLE_RENDER_DEFAULTS, castPresentation } from '@/services/impact/presentation';
import { buildReel } from '@/services/impact/reel';
import {
  clearAllPendingActions as clearAllPending,
  clearPendingAction as clearPending,
  markPendingAction as markPending,
  previewEffect,
  reconcilePendingActions as reconcilePending,
} from '@/services/impact/pending';
import impactShake, { BOARD_SHAKE_KEY, GROUND_SHAKE_MS, groundShakeForce } from '@/services/impact/shake';
import {
  blockFits,
  footprintCells,
  footprintKeys,
  footprintCoversCell,
  occupiedKeys,
  spanOf,
} from '@/components/Player/footprint';
import {sideColorVar, resolveSideColor} from '@/utils/sideColor';
import {sideEmblemPath} from '@/utils/sideEmblems';
import deathAvatar from '@/assets/images/avatar/death-avatar.webp';
import {isSelfTargetAbility, selfTargetAims} from '@/utils/selfTarget';

/**
 * Tuning for the character holograms.
 *
 * `baseFit` is the only size knob: the pedestal is drawn this many cell widths
 * across, and the rest of the token follows from the component's own stage
 * geometry. At 0.75 the plate sits inside its cell with a margin all round, which
 * keeps neighbouring tokens from touching and leaves the grid readable underneath;
 * the token still stands 1.38 cells tall, so the vertical presence survives the
 * smaller footprint. Sizing the token box directly instead would overhang the
 * neighbouring cells, since the pedestal is only 0.84 of it.
 *
 * Everything else is deliberately restrained: a board full of glitching,
 * sweeping holograms is unreadable, and the effects are what cost frames.
 */
const HOLO = {
  baseFit: 0.75,
  /** How much of the cell's rake the pedestal takes on. 1 = lies flat in the grid. */
  groundTiltStrength: 1.0,
  /** Contact shadow: how far past the plate it spreads, and how dark at its centre. */
  shadowSpread: 1.35,
  shadowStrength: 0.5,
  /** Light thrown back onto the floor, as a multiple of the plate's width. */
  bounceSpread: 1.15,
  bounceStrength: 0.5,
  intensity: 0.92,
  speed: 7,
  /** Below this on-screen pedestal width the effects are noise; drop a tier. */
  lowQualityBelowPx: 26,
  mediumQualityBelowPx: 54,
};

/**
 * The side ring drawn under a real 3D pedestal.
 *
 * A second, independent layer from the pedestal's own relation tint: allegiance
 * (side) and hostility (relation) are two different facts, and the ring exists
 * so the board can show both without either one repainting the other. It has to
 * sit outside `HOLO_GROUND.rx` — the pedestal's own footprint — or the 3D prop's
 * opaque plate simply covers it.
 */
const SIDE_RING = {
  /** How far past the plate's own footprint the ring sits, as a multiple of it. */
  spread: 1.22,
  /** Stroke width, as a fraction of the plate's width. */
  thickness: 0.045,
  opacity: 0.85,
  /** The side's emblem, standing at the front of the ring: its height as a
   *  fraction of the plate's width, and the stroke it is drawn with in the
   *  emblem's own 24-unit space. */
  markSize: 0.3,
  markStroke: 2.1,
};

/**
 * How far a reach may run before it stops being worth shading.
 *
 * Past this the lit region covers the whole board and the shadow has nothing left
 * to say, so the band is dropped rather than drawn as a formality - and the veil,
 * which is the one full-screen surface in the aim, is never built for it.
 */
const CAST_REACH_MAX = 24;

/**
 * The lattice, in its two readings: the slate it is ruled in normally, the colder
 * and fainter one it drops to outside an armed action's reach, and the strength the
 * action's own colour is allowed inside it.
 */
const GRID_LINE = {
  base: 'rgba(120, 122, 175, 0.34)',
  // Ruled BEFORE the shadow, so what is written here is roughly halved by the veil
  // over it - which is why the quiet reading is stated louder than it looks. Set to
  // the value it appears to want, the lattice outside the reach vanished entirely and
  // the switch stopped meaning anything on the half of the board that most needs it.
  outside: 'rgba(120, 122, 175, 0.30)',
  insideAlpha: 0.22,
};

/**
 * The ground the action cannot be taken on, put in shadow.
 *
 * A cold blue-black rather than a grey: it is the same dark the fog is made of, so
 * a board carrying both reads as one lighting state instead of two washes. `feather`
 * is the soft edge, in cells, and it is what keeps the reach reading as a clearing
 * lit from within rather than a cut-out laid over the map.
 */
const CAST_SHADOW = { color: { r: 5, g: 7, b: 16 }, density: 0.52, feather: 0.45, resolution: 0.5 };

/**
 * The aim speaks in intent, not in school. A player mid-fight needs to read "this
 * hurts / this helps / this does something else" at a glance, so every harmful
 * ability shares one colour and every beneficial one another - the flavour of the
 * damage is carried by the firing line, which is already keyed to violation type.
 */
const AIM_COLORS = {
  harm: { r: 255, g: 45, b: 85 },
  boon: { r: 60, g: 255, b: 158 },
  neutral: { r: 0, g: 224, b: 255 },
};

/**
 * The dark the character cannot see into.
 *
 * It is depth, not a lid. One flat opacity over everything unseen reads as a sheet
 * of paint laid on the board, and the step from lit to unlit lands as a hard cut;
 * what the eye expects instead is the ground just past the torch still half there,
 * giving way to a void further out. So the veil is graded: `nearDensity` right at
 * the rim of sight, deepening to `density` once the murk has closed over. Nothing
 * is hidden by opacity anyway - the server withheld it before the payload was sent.
 *
 * `color` is a cold blue-black rather than a neutral one, and `rim` breathes the
 * board's own cyan into the band where sight gives out, so the darkness belongs to
 * this game rather than sitting on top of it.
 *
 * `edge` is where the reveal starts falling off, as a fraction of the sight radius;
 * short of 1 the rim of vision dims instead of ending at a drawn circle, which is
 * what stops the fog reading as a spotlight cut out of a sheet.
 */
const FOG = {
  color: { r: 5, g: 10, b: 20 },
  density: 0.96,
  /** What is left of the veil where the halo is at its strongest, at the rim of sight. */
  nearDensity: 0.35,
  edge: 0.62,
  /** How far the veil stays thin past the limit of sight, in cell widths. */
  haloSpread: 1.8,
  /** The cyan the murk catches where it meets the light, and how much of it. */
  rim: { r: 104, g: 226, b: 255 },
  rimStrength: 0.34,
  /**
   * How far the rim reaches out, in cell widths.
   *
   * Well short of the halo on purpose. Spread as wide as the veil thins, the tint
   * is a wash nobody reads as anything; kept to a band hugging the last lit ground
   * it becomes an edge, and the boundary of sight looks drawn rather than simply
   * unpainted.
   */
  rimSpread: 0.42,
  /** Grain: how strongly the murk mottles the veil, and how large its cells are. */
  noiseStrength: 0.5,
  noiseScale: 320,
  /** Cells per second the two noise layers drift, in opposite directions. */
  noiseDrift: 7,
  /** Resolution of the tile the murk is built from. Low frequency - this is plenty. */
  noiseTile: 128,
  /** How far the reveal is feathered past the lit cells, in cell widths. */
  feather: 0.45,
  /**
   * Fraction of the viewport the fog's working surfaces are built at.
   *
   * Every one of them is soft by construction - a blurred mask, a radial falloff,
   * low-frequency murk - so nothing in the fog carries detail a half-scale buffer
   * could lose, while the pixels touched each frame drop to a quarter. That is what
   * pays for the halo and the rim.
   */
  resolution: 0.5,
};

/** Milliseconds between fog redraws. The murk drifts slowly; 20 fps is plenty. */
const FOG_FRAME_INTERVAL = 50;

/** How far past its own edge the resolving cell keeps hold of the pointer, in
 *  cells. Only needs to exceed the jitter of a hand on a boundary. */
const AIM_CELL_HYSTERESIS = 0.08;

/**
 * The armed ability's own material, shown on the ground it would cover.
 *
 * One effect, held on the stage under a fixed id so a moving aim replaces it rather
 * than stacking, and declared `aim: true` so the stage drops everything that belongs
 * to the arrival — the ground flash, the body — and puts the ambient motes under the
 * gather's law of motion instead of the ember's.
 *
 * THIS LAYER LEADS. It used to sit under a contour field at less than half weight,
 * adding a faint wash nobody was looking at while the rings did the talking. The
 * rings are gone, and what a player reads now is this: the skill's own material,
 * lying on the ground it would cover, breathing. The 2D field above it is the
 * precision — the exact cells, held quiet — not the picture.
 *
 * Nothing here may run faster than that field's AIM_FLOW_PERIOD.
 */
const AIM_PREVIEW_ID = 'aim-preview';

/*
 * `actionLog` is refetched whole from `actionLogList()` each cycle, at the API's
 * default page size of 20 — reel.js's "rolling window over the last few cycles" is
 * that small. This bound only has to stay bigger than what one fetch can ever
 * return, so an id already retired can never reappear and replay; 200 is a 10x
 * margin over that window, not a tuned number.
 */
const PLAYED_ACTION_IDS_MAX = 200;

/**
 * How far a blow may carry its attacker, in cells.
 *
 * A lunge is the body closing the last of the ground, not a charge across the map.
 * `deliveryFor` routes anything whose NAME says it strikes up to six cells into a
 * melee, and a fighter crossing six cells in a quarter of a second is a teleport with
 * a sword. Past this the commit reads as a run-up that starts where the fighter is.
 */
const LUNGE_MAX_CELLS = 2.2;

/** A recoil is a fast event with a slow settle; this is the whole of it. */
const RECOIL_MS = 460;

/**
 * The ground never moves further than this, whatever the zoom.
 *
 * Every other displacement here is measured in cells, which is right for a body: a
 * figure and its cell grow together, so a stagger is the same fraction of a token at
 * 60% and at 270%. The board is not like that — it is the frame the player is
 * looking through, and a cell-sized shove at 270% zoom throws a quarter of the
 * screen sideways and shows the gutter behind the map.
 */
const BOARD_SHAKE_MAX_PX = 16;

/**
 * How hard a body is thrown compared with how hard the ground is.
 *
 * `impactForce` is one number describing the blow, and the two things that read it
 * are not at the same scale: a figure is about a cell and a half tall on screen, so a
 * displacement the board would find violent is a twitch on a person. At 1 a solid
 * punch moved its target about a tenth of its own width, which the eye takes for a
 * rendering wobble rather than for someone being hit.
 */
const RECOIL_GAIN = 1.8;

/** Where a blow pushes what it hit: away from wherever it came from. */
function blowDirection(origin, cell) {
  const dx = (cell.col ?? 0) - (origin?.col ?? cell.col ?? 0);
  const dy = (cell.row ?? 0) - (origin?.row ?? cell.row ?? 0);
  /* Struck where it stands — a self-cast, a blast centred on the body — has no line
     to be thrown along, so it takes the hit downward, into its own cell. */
  if (!dx && !dy) return { x: 0, y: 1 };
  return { x: dx, y: dy };
}

/** Both sets of hooks fire, in the order given, and the caller's answer is kept. */
function mergeHooks(base, extra) {
  const merged = { ...base };
  Object.keys(extra || {}).forEach(name => {
    const first = base[name];
    const second = extra[name];
    merged[name] = first
      ? (...args) => { first(...args); return second(...args); }
      : second;
  });
  return merged;
}

/**
 * Typical displacement the noise gives the rings, in cells - a standard deviation,
 * not a bound, and the warp field is normalised to hit it exactly. Stating it as
 * an amplitude was the bug: three octaves of noise summed in [0,1] cluster hard
 * around their mean rather than spanning it, so mapping the sum onto +/-1 landed
 * at about a third of the intended strength and the rings came out nearly true.
 *
 * Against a 0.32-cell band spacing this puts the typical pull at about a third of
 * a spacing and the extremes near a whole one - clearly morphed, while the warp's
 * gradient stays well under 1 so neighbouring rings undulate without crossing.
 */
const AIM_FLOW_WARP = 0.11;

/** Noise frequency. Lower is broader: this gives features about 1.7 cells across,
 *  so the rings sweep rather than fray. */
const AIM_FLOW_WARP_SCALE = 0.6;

/**
 * How much of the viewport a framed group of tokens may span.
 *
 * A token is drawn far taller than the cell it stands on, so the vertical share is
 * the tighter of the two: the span measured between cell centres has to leave room
 * for the figures rising out of them.
 */
const FRAME = {
  usableWidth: 0.7,
  usableHeight: 0.45
};

/**
 * Value noise, and three octaves of it. Deterministic from position alone, so the
 * warp it produces is a fixed property of the field rather than something that
 * shimmers between frames.
 */
function noiseHash(x, y) {
  const n = Math.sin(x * 127.1 + y * 311.7) * 43758.5453123;
  return n - Math.floor(n);
}

function valueNoise(x, y) {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  const a = noiseHash(xi, yi);
  const b = noiseHash(xi + 1, yi);
  const c = noiseHash(xi, yi + 1);
  const d = noiseHash(xi + 1, yi + 1);
  return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v;
}

/** Signed, so octaves cancel around zero instead of piling up around a half. */
function fractalNoise(x, y) {
  return (valueNoise(x, y) * 2 - 1) * 0.6
    + (valueNoise(x * 2.1 + 5.2, y * 2.1 + 1.3) * 2 - 1) * 0.3
    + (valueNoise(x * 4.3 + 9.1, y * 4.3 + 7.7) * 2 - 1) * 0.1;
}

export default {
  name: 'PlayerTacticalMap',
  components: {
    HoloToken,
    ObjectToken
  },
  props: {
    tacticalMapId: {
      type: String,
      required: false,
      default: null
    },
    playerCharacter: {
      type: Object,
      required: false,
      default: null
    },
    playerSkills: {
      type: Array,
      required: false,
      default: () => []
    },
    playerItems: {
      type: Array,
      required: false,
      default: () => []
    },
    playerSpecials: {
      type: Array,
      required: false,
      default: () => []
    },
    currentPhase: {
      type: String,
      required: false,
      default: 'preparation'
    },
    tacticalMovementDisabled: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * A fight runs on its own clock, so the world cycle stops driving the board
     * for as long as one lasts and the fight's phase tick drives it instead.
     * `fightId` scopes that tick — another fight's round must not refresh us.
     */
    inFight: {
      type: Boolean,
      required: false,
      default: false
    },
    fightId: {
      type: String,
      required: false,
      default: null
    },
    /**
     * The fight clock's own state — `services/PlayerFight.ts`'s `.state` — so the
     * board can tell which side a token belongs to. The tactical map payload has
     * no side field of its own and none is added for this: `spawned_object.id`
     * matches `participants[].character_id`, and that's the only join needed.
     * Null outside a fight, and while pending with no side chosen yet.
     */
    fightState: {
      type: Object,
      required: false,
      default: null
    },
    /**
     * THE SANDBOX SEAM.
     *
     * This board is the fight's board. It is also what /dev/duel mounts, so a skill
     * can be aimed and resolved against exactly the geometry, the aim field and the
     * impact layer a player will see — not against a lookalike.
     *
     * The seam is deliberately as thin as a seam can be: it is the NETWORK boundary
     * and nothing else. Two payloads normally fetched (`tacticalmapPlayerCurrent`
     * and `tacticalmapPlayerTacticalInfo`) are supplied instead, in exactly the shape
     * the API returns them, and no socket is opened. Every line after that — the
     * corner morph, `gridProjector`, the cell geometry, passability, the aim field,
     * the holo tokens, the impact layer — runs unchanged and unaware, because there
     * is nothing for it to be aware of. That is what stops the bench drifting: there
     * is no sandbox rendering path to drift, only a sandbox source of two objects.
     *
     * Rules for anyone extending this:
     *   - a sandbox payload must be VALID API DATA, never a convenience shape;
     *   - nothing below may branch on `sandboxed` for how it DRAWS or RESOLVES —
     *     only for whether it talks to the network;
     *   - fog is data, not a mode: omit `vision` for a lit board, supply a real
     *     `vision` block to see the fog.
     *
     * @type {{ tacticalMap: object, tacticalInfo: object } | null}
     */
    sandbox: {
      type: Object,
      required: false,
      default: null
    },
    /**
     * How much of the viewport's top and bottom edge is covered by the host's
     * own chrome, in CSS pixels. Framing centres tokens in what is left rather
     * than in the canvas, which is not the same rectangle once rails are laid
     * over it.
     */
    safeTop: {
      type: Number,
      required: false,
      default: 0
    },
    safeBottom: {
      type: Number,
      required: false,
      default: 0
    },
    /**
     * Which point of a spray matrix the pattern is pinned by. The server pins by the
     * matrix centre and so does a fight; the bench can ask for the map's `x` marker
     * instead, which is the disagreement it exists to show. Inert at its default.
     */
    aimAnchor: {
      type: String,
      required: false,
      default: 'center'
    },
    /** Whether the aim field is painted. Off, the bench sees the stage preview alone. */
    aimOverlay: {
      type: Boolean,
      required: false,
      default: true
    },
    /**
     * Strict battle rules, or the bench's looser cinematic shape. Null is the fight's
     * own answer, so a board mounted without this prop cannot render anything but
     * BATTLE_RENDER_DEFAULTS.
     */
    renderDefaults: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      canvas: null,
      ctx: null,
      tacticalMapData: null,
      tacticalCharacterInfo: null, // Tactical info from API (movement_range, vision_range, position)
      backgroundImage: null,
      loading: false,
      canvasWidth: 0,
      canvasHeight: 0,
      offsetX: 0,
      offsetY: 0,
      isPanning: false,
      panStartX: 0,
      panStartY: 0,
      panStartOffsetX: 0,
      panStartOffsetY: 0,
      hoveredCell: null,
      selectedObjectId: null,
      hoveredObjectId: null,
      zoom: 1.0,
      minZoom: 0.5,
      maxZoom: 3.0,
      zoomStep: 1.1,
      // Pathfinding cache for movement area
      reachableCells: new Map(), // Map of "x,y" -> { x, y, cost, path }
      pathToHovered: null,
      // Performance optimization
      lastHoveredKey: null,
      passableCellsCache: new Set(), // Cache of "x,y" strings for passable cells
      // HUD toggles
      gridVisible: false, // The board reads as a place, not a board game, until asked otherwise
      passableHighlightVisible: false,
      // Whether the resolution reel is actively drawing - gates the skip button
      reelInFlight: false,
      // Sticky "skip animations" preference, persisted in localStorage
      skipReelsPreference: false,
      // Event bus for tactical movement
      bus: null,
      /* A move published while the board was still being fetched. The snapshot in
         flight may predate it, so the board is reconciled once the load lands. */
      missedPositionEvent: false,
      // Animation overrides - maps character ID to temporary {x, y} for animation
      tokenPositionOverrides: {},
      // Routes announced ahead of the position change they belong to, by mover id.
      pendingRoutes: {},
      tokenAnimationFrame: null,
      /*
       * Whether the 3D stage is actually drawing. A flag and not the stage itself:
       * the stage must never be reactive - Vue's proxy wraps the scene graph and
       * three throws reading its own Matrix4 back through it - but the tokens have
       * to know, so a boolean crosses the line and the scene graph does not.
       */
      pedestalsAreReal: false,
      // Skill/Item/Special targeting
      selectedSkill: null,
      selectedItem: null,
      selectedSpecial: null,
      skillRotation: 0, // 0, 90, 180, 270
      skillTargetPosition: null, // { x, y } grid coordinates
      skillAffectedCells: [], // Array of { x, y, valid, color, isPivot } objects
      aimPoint: null, // Pointer position in fractional grid coords - the impact centre
      skillTargetDistance: null, // Distance from caster to target
      aimPreviewSignature: null, // Ground the stage preview was last built for
      // Rotation drag state
      isDraggingRotation: false,
      rotationDragStartAngle: 0,
      rotationDragStartRotation: 0,
      rotationDragCenter: { x: 0, y: 0 },
      // Animation for skill preview
      animationFrameId: null,
      // Animation for the fog's drifting murk
      fogAnimationFrameId: null,
      fogLastFrameAt: 0,
      prefersReducedMotion: false,
      /* `?record=1` keeps both WebGL buffers readable so a cast can be captured
         off the canvas. Costs a copy per frame — opt in, never on in play. */
      captureMode:
        typeof window !== 'undefined' &&
        new URLSearchParams(window.location.search).has('record'),
      // Multi-charge state
      chargeCount: 1,              // Total charges for selected ability
      chargesPlaced: 0,            // Number of charges placed so far
      chargeTargets: [],           // Array of { x, y, rotation, affectedCells[] }
      currentChargeRotation: 0,    // Rotation for current charge being placed
      actionHint: null,
      actionHintTimer: null,
      // A vision refresh in flight, and whether another was asked for while it ran.
      visionRefreshInFlight: false,
      visionRefreshQueued: false,
    };
  },
  computed: {
    /** Whether this board is running off supplied payloads instead of the API. */
    sandboxed() {
      return !!this.sandbox;
    },
    /** The render rules in force. See the `renderDefaults` prop. */
    activeRenderDefaults() {
      return this.renderDefaults || BATTLE_RENDER_DEFAULTS;
    },
    hoveredCellLabel() {
      if (!this.hoveredCell) return '—';
      return `X: ${this.hoveredCell.x}, Y: ${this.hoveredCell.y}`;
    },
    mapSizeLabel() {
      const grid = this.tacticalMapData && this.tacticalMapData.grid_settings;
      if (!grid) return '—';
      return `${grid.columns} × ${grid.rows}`;
    },
    gridSettings() {
      return this.tacticalMapData?.grid_settings || null;
    },
    zoomPercentage() {
      return Math.round(this.zoom * 100);
    },
    visibleObjects() {
      return this.tacticalMapData?.spawned_objects || [];
    },
    /**
     * The chests on the board.
     *
     * They arrive in `spawned_objects` like everything else, tagged by Django's
     * own polymorphic type — a Chest is a GameObject, so it needs no second
     * request and inherits fog of war for free.
     */
    visibleChests() {
      return this.visibleObjects.filter(obj => obj.type === 'chest');
    },
    /**
     * Loot lying on the floor.
     *
     * Tagged `worlditem` by Django's polymorphic type, same as chests are
     * `chest`. Only items nobody is holding reach the client — an item in a
     * pack or a chest is filtered out server-side, so everything here is
     * something a character could actually bend down and take.
     */
    visibleGroundItems() {
      return this.visibleObjects.filter(obj => obj.type === 'worlditem');
    },
    /**
     * What the party can see, as the server resolved it.
     *
     * The client never decides this. Sight is worked out where the map's walls and
     * the characters' effects are known, and everything under the fog is withheld
     * from the payload outright - so the darkness drawn here is a description of a
     * filter that has already happened, not the filter itself.
     */
    vision() {
      return this.tacticalMapData?.vision || null;
    },
    visibleCellKeys() {
      const cells = this.vision?.visible_cells;
      if (!cells) return null;
      return new Set(cells.map(([x, y]) => `${x},${y}`));
    },
    visionSources() {
      return this.vision?.sources || [];
    },
    /** Whether the board is under fog at all. A map with no vision block is fully lit. */
    fogActive() {
      return !!this.visibleCellKeys;
    },
    holo() {
      return HOLO;
    },
    /**
     * Drop effect tiers as the board zooms out. At a 25px pedestal the motes, sweep and
     * bloom are sub-pixel noise that still costs a raster per token every frame.
     */
    holoQuality() {
      const config = this.scaledGridConfig;
      if (!config) return 'medium';

      const onScreenBase =
        Math.min(config.cell_width, config.cell_height) * HOLO.baseFit * this.zoom;

      if (onScreenBase < HOLO.lowQualityBelowPx) return 'low';
      if (onScreenBase < HOLO.mediumQualityBelowPx) return 'medium';
      return 'high';
    },
    labelStyle() {
      // The whole overlay is scaled by zoom; undo it for labels so names stay legible at
      // low zoom and do not swamp the tokens at high zoom.
      return { transform: `translateX(-50%) scale(${1 / this.zoom})` };
    },
    scaledGridConfig() {
      if (!this.gridSettings) return null;

      // If no reference dimensions or no background image, use original values
      if (!this.gridSettings.reference_width || !this.gridSettings.reference_height || !this.backgroundImage) {
        return this.gridSettings;
      }

      // Calculate scale factors based on current image size vs reference size
      const scaleX = this.backgroundImage.width / this.gridSettings.reference_width;
      const scaleY = this.backgroundImage.height / this.gridSettings.reference_height;

      // Return scaled grid config
      return {
        ...this.gridSettings,
        cell_width: this.gridSettings.cell_width * scaleX,
        cell_height: this.gridSettings.cell_height * scaleY
      };
    },
    isPreparationPhase() {
      return this.currentPhase?.toLowerCase() === 'preparation';
    },
    isActionPhase() {
      return this.currentPhase?.toLowerCase() === 'action';
    },
    canMove() {
      // Movement is only allowed in PREPARATION phase and when tactical movement is not disabled
      return this.isPreparationPhase &&
             !this.selectedSkill &&
             !this.selectedItem &&
             !this.tacticalMovementDisabled;
    },
    canUseSkillsItems() {
      // Skills/Items are only allowed in ACTION phase
      return this.isActionPhase;
    },
    selectedAbility() {
      return this.selectedSkill || this.selectedItem || this.selectedSpecial;
    },

    /**
     * Every cell the armed action could be committed on.
     *
     * The aim shows what ONE shot covers; this shows where a shot may be taken at
     * all, which is the question a player asks first and used to be answered by a
     * dashed square drawn at a whisper. It is the same rule `isWithinRange` and
     * `placeCharge` enforce - Chebyshev distance inside the ability's band, on ground
     * the map lets a cast land on - so the lit ground and the refusal cannot disagree.
     *
     * Null means there is nothing worth drawing, and that is deliberate in two cases.
     * An unbounded reach (no `max_range`, or one wider than the board is worth
     * shading) puts every cell in reach, so lighting them all states nothing while
     * costing a full-screen veil; and with no ability armed there is no reach at all.
     */
    castReach() {
      const ability = this.selectedAbility;
      if (!ability || !this.playerCharacter || !this.scaledGridConfig) return null;

      const skill = ability.spray_config ? ability : (ability.item?.skill || ability);
      const minRange = skill.min_range || 0;
      // Read rather than defaulted: a max range of 0 is a real band - the caster's own
      // ground and nothing else - and coercing it away would drop the reach for exactly
      // the abilities whose reach is most surprising.
      const maxRange = Number.isFinite(skill.max_range) ? skill.max_range : null;
      if (maxRange === null || maxRange < 0 || maxRange > CAST_REACH_MAX) return null;

      const casterX = this.tacticalCharacterInfo?.x ?? this.playerCharacter.x ?? 0;
      const casterY = this.tacticalCharacterInfo?.y ?? this.playerCharacter.y ?? 0;

      const keys = new Set();
      for (let dx = -maxRange; dx <= maxRange; dx++) {
        for (let dy = -maxRange; dy <= maxRange; dy++) {
          const distance = Math.max(Math.abs(dx), Math.abs(dy));
          if (distance < minRange || distance > maxRange) continue;
          const x = casterX + dx;
          const y = casterY + dy;
          if (!this.isCellPassable(x, y)) continue;
          keys.add(`${x},${y}`);
        }
      }
      return keys.size ? keys : null;
    },

    /** The reach, only when it is the board's business to draw it. */
    castReachVisible() {
      return this.aimOverlay !== false ? this.castReach : null;
    },

    /**
     * The side of the block the viewer's own body holds. Everything about a move -
     * where the route runs, which ground can be landed on, what the trace is drawn
     * through - is measured over this block rather than over a single cell.
     */
    moverSpan() {
      return spanOf(this.tacticalCharacterInfo);
    },

    /**
     * Which pedestal the figures stand on.
     *
     * Exactly one of the two draws. The sprite carries a camera baked into it at
     * one angle, so it is right on a single row of a raked grid and progressively
     * wrong everywhere else, and warping it to fit destroys the machined plate that
     * is the point of it. The prop has no baked camera to fight and is simply
     * correct wherever it stands. Both at once is two pedestals disagreeing about
     * where the floor is — so the sprite is what is left when there is no stage.
     */
    pedestalPlate() {
      return this.pedestalsAreReal ? 'none' : 'sprite';
    },

    /* A signed character is not bounded by reach: it pays nothing, walks through
       walls and ignores distance, so a shaded area would draw a limit that does
       not exist. */
    isSignedMover() {
      return this.tacticalCharacterInfo?.game_master_mode === true;
    },

    /**
     * The viewer's own figure, projected onto the cell a move would end on. Null
     * when there is nothing to preview - no path, no movement to spend, or a route
     * that ends where it started.
     */
    movementGhost() {
      if (!this.canMove || !this.pathToHovered || this.pathToHovered.length < 2) return null;

      const self = this.visibleObjects.find(
        obj => obj.type === 'character' && obj.relation === 'self'
      );
      if (!self) return null;

      const destination = this.pathToHovered[this.pathToHovered.length - 1];
      if (destination.x === self.x && destination.y === self.y) return null;

      // Keeps the real token's id, so the projection jitters on the same phase and
      // reads as the same hologram rather than a second, differently-breathing one.
      return { ...self, x: destination.x, y: destination.y };
    },

    /**
     * Everyone standing under the aim, previewed or already committed. The map
     * answers "who does this hit" on the figures themselves rather than leaving the
     * player to read cell outlines against tokens that sit above them.
     */
    aimedCharacterIds() {
      const ids = new Set();
      if (!this.selectedAbility) return ids;

      const characters = this.visibleObjects.filter(obj => obj.type === 'character');
      const mark = (cells) => {
        cells.forEach(cell => {
          characters.forEach(obj => {
            if (footprintCoversCell(obj.x, obj.y, spanOf(obj), cell.x, cell.y)) {
              ids.add(obj.id);
            }
          });
        });
      };

      this.chargeTargets.forEach(charge => mark(charge.affectedCells));
      mark(this.skillAffectedCells.filter(cell => cell.valid));

      return ids;
    }
  },
  watch: {
    playerCharacter: {
      async handler() {
        // Reload tactical character info when player character changes
        await this.loadTacticalCharacterInfo();

        // Recalculate reachable cells with updated info
        this.calculateReachableCells();

        // DON'T align camera - preserve user's camera position!
        // Camera alignment should only happen on initial map load
        // this.alignToPlayerCharacter();

        this.render();
      },
      deep: true
    },
    /*
     * A new sandbox payload is a new board. Compared by identity, not deeply: the
     * bench mutates figures inside the payload every time one is moved, and a deep
     * watcher would reload the whole map on each step.
     */
    async sandbox() {
      await this.loadTacticalCharacterInfo();
      await this.loadTacticalMap();
      this.calculateReachableCells();
      this.render();
    },
    // A map served without a vision block is fully lit and needs no drifting murk.
    fogActive(active) {
      if (active) this.startFogAnimation();
      else this.stopFogAnimation();
    }
  },
  created() {
    // The aim field is held off `data` deliberately. Its caches are megabytes of
    // canvases and typed arrays that Vue would deep-proxy for no benefit, and the
    // proxy also breaks identity: the frame scheduler compares the cache it was
    // handed against the current one, and a reactive wrapper is never `===` the raw
    // object it wraps, so every scheduled build bailed out after the first frame.
    this.aimField = new AimField();

    // The fog's scratch surfaces, off `data` for the same reasons.
    this.fogCanvas = null; // The darkness itself, punched through where sight reaches
    this.fogRevealCanvas = null; // The hole: lit cells, feathered by each source's falloff
    this.fogFalloffCanvas = null; // Per-source radial falloff, combined at their brightest
    this.fogHaloCanvas = null; // The reveal spread outward, thinning the veil past sight
    this.fogMaskCanvas = null; // Halo and reveal as one surface, subtracted in a single pass
    this.fogRimCanvas = null; // The cyan band the murk catches where it meets the light
    this.fogMaskCache = null; // The last two, with what they were built for
    this.fogNoiseTile = null; // One tile of drifting murk, built once
    this.castShadowCanvas = null; // The veil over ground the armed action cannot reach
    this.castShadowCache = null; // That veil, with the reach and camera it was built for
    this.impactLayer = null; // The WebGL effects stage over the board
    this.playedActionIds = new Set(); // Actions already drawn, so a refresh cannot repeat them
    this.reelRunning = null; // The reel in flight, so two refreshes queue instead of overlapping
    this.reelBatchCount = 0; // Batches of queueCasts still settling, so reelInFlight covers all of them
    this.reelSkipRequested = false; // Set by the skip button; consumed once per batch by the scheduler
    this.loadSkipReelsPreference();
    this.pendingActionEffects = new Map(); // Submitted action id -> { ids, cycle } of the aim held for it
    this.lungeRuns = new Map(); // Attacker id -> the blow it is currently closing on
    this.choreographyLive = false; // Whether the last frame wrote any displacement at all
  },
  async mounted() {
    this.prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

    /*
     * The board listens before it loads, not after.
     *
     * Subscribing at the end of this hook left a window - the canvas, the
     * character info, the map itself and its background image, every one of them
     * a round trip - in which a move published by anyone was heard by nobody, and
     * the token stayed on the cell the snapshot had put it on until the page was
     * reloaded. A character switch re-mounts this component, so that window opened
     * on exactly the action that follows a switch.
     *
     * It also settles ownership of the handlers: an instance torn down mid-load
     * used to reach `beforeUnmount` with no `bus` to unsubscribe from and then
     * register its handlers anyway once the awaits resumed, leaving a dead
     * component listening to the world for the rest of the session.
     */
    this.subscribeToWorld();

    await this.initCanvas();

    // Load tactical character info FIRST to get proper position and movement ranges
    await this.loadTacticalCharacterInfo();

    // Then load tactical map
    await this.loadTacticalMap();

    // Calculate reachable cells using tactical character info
    if (this.tacticalCharacterInfo || this.playerCharacter) {
      this.calculateReachableCells();
    }

    if (this.fogActive) {
      this.startFogAnimation();
    }

    window.addEventListener('resize', this.handleResize);
    window.addEventListener('keydown', this.handleKeyDown);

    // Subscribe to tactical position change events for smooth animation. A sandbox
    // board has no campaign behind it, so there is nothing for a socket to carry —
    // and opening one would put a dev bench on the live event bus.
    if (!this.sandboxed) {
      const { ensureConnection } = await import('@/api/dx-websocket/index.ts');
      this.bus = ensureConnection();
      this.bus.on("world::game_object_tactical_position_changed", this.handleGameObjectPositionChanged);
      this.bus.on("world::tactical_movement", this.rememberRoute);
      this.bus.on("world::character_changed", this.handleCharacterChanged);
      this.bus.on("world::new_cycle", this.handleNewCycle);
    }
  },
  beforeUnmount() {
    if (this.pedestalStage) {
      this.pedestalStage.dispose();
      this.pedestalStage = null;
    }
    if (this.impactLayer) {
      this.impactLayer.dispose();
      this.impactLayer = null;
    }
    if (import.meta.env.DEV && window.__tacticalBoard === this) delete window.__tacticalBoard;
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('keydown', this.handleKeyDown);
    this.stopPanning();

    // Stop skill animation
    this.stopSkillAnimation();
    this.stopFogAnimation();

    this.clearAllPendingActions();
    this.aimField.dispose();
    this.castShadowCanvas = null;
    this.castShadowCache = null;

    if (this.actionHintTimer) clearTimeout(this.actionHintTimer);
    if (this.tokenAnimationFrame) cancelAnimationFrame(this.tokenAnimationFrame);

    this.unsubscribeFromWorld();
  },
  methods: {
    /** The world events the board is drawn from, and their handlers. */
    worldSubscriptions() {
      return [
        ["world::game_object_tactical_position_changed", this.handleGameObjectPositionChanged],
        ["world::tactical_movement", this.rememberRoute],
        ["world::character_changed", this.handleCharacterChanged],
        ["world::new_cycle", this.handleNewCycle],
        ["character::fight_round_advanced", this.handleFightRoundAdvanced],
      ];
    },

    subscribeToWorld() {
      this.bus = ensureConnection();
      this.worldSubscriptions().forEach(([event, handler]) => {
        this.bus.off(event, handler);
        this.bus.on(event, handler);
      });
    },

    unsubscribeFromWorld() {
      if (!this.bus) return;
      this.worldSubscriptions().forEach(([event, handler]) => this.bus.off(event, handler));
      this.bus = null;
    },

    // Camera state persistence - save/restore per map ID
    getCameraStateKey() {
      // Use tactical map ID for camera state persistence
      const mapId = this.tacticalMapData?.id || this.tacticalMapId || 'default';
      return `dx_tactical_camera_${mapId}`;
    },

    saveCameraState() {
      const key = this.getCameraStateKey();
      const state = {
        offsetX: this.offsetX,
        offsetY: this.offsetY,
        zoom: this.zoom,
        timestamp: Date.now()
      };
      try {
        localStorage.setItem(key, JSON.stringify(state));
        console.log('[Camera] Saved camera state for map:', key, state);
      } catch (error) {
        console.warn('[Camera] Failed to save camera state:', error);
      }
    },

    restoreCameraState() {
      const key = this.getCameraStateKey();
      try {
        const savedState = localStorage.getItem(key);
        if (savedState) {
          const state = JSON.parse(savedState);
          this.offsetX = state.offsetX;
          this.offsetY = state.offsetY;
          this.zoom = state.zoom;
          console.log('[Camera] Restored camera state for map:', key, state);
          return true;
        }
      } catch (error) {
        console.warn('[Camera] Failed to restore camera state:', error);
      }
      return false;
    },

    async initCanvas() {
      this.canvas = this.$refs.canvas;
      this.ctx = this.canvas.getContext('2d');
      // A lost WebGL context must not take the board with it: the floor, the
      // grid and the figures all still draw without the props.
      try {
        /*
         * Assigned straight onto the instance, never declared in data(). A scene
         * graph put in data() gets wrapped in Vue's reactive proxy, and three then
         * reads its own Matrix4 back through that proxy and throws on every frame —
         * the layer stays blank with no error anywhere near the cause.
         */
        this.pedestalStage = markRaw(
          // A prop lands long after the frame that asked for it, and the board only
          // redraws on input — so the arrival has to ask for a frame itself.
          new PedestalStage(this.$refs.stageCanvas, () => this.render(), {
            record: this.captureMode,
          })
        );
        this.pedestalsAreReal = true;
      } catch (error) {
        console.warn('[PlayerTacticalMap] no WebGL; falling back to the sprite plate:', error);
        this.pedestalStage = null;
        this.pedestalsAreReal = false;
      }

      /*
       * Assigned onto the instance for the same reason the pedestal stage is: a
       * three.js scene graph behind Vue's reactive proxy throws on every frame.
       */
      this.impactLayer = new ImpactBoardLayer(this.$refs.effectsCanvas, {
        projection: () => this.impactProjection(),
        quality: this.holoQuality === 'low' ? 'low' : 'high',
        record: this.captureMode,
        /*
         * The bodies ride the effects layer's own clock. It already runs exactly
         * while something is in flight and stops the frame after the last one leaves
         * — which is the same window a lunge, a recoil and a ground shake occupy —
         * so nothing here starts a second rAF behind an idle board.
         */
        onFrame: () => this.applyChoreography(),
      });

      /* Registration is a thing you check by firing something at a known cell and
         looking, so the board is reachable from the console while developing. */
      if (import.meta.env.DEV) window.__tacticalBoard = this;

      this.resizeCanvas();
    },

    resizeCanvas() {
      const container = this.canvas.parentElement;
      this.canvasWidth = container.clientWidth;
      this.canvasHeight = container.clientHeight;
      this.canvas.width = this.canvasWidth;
      this.canvas.height = this.canvasHeight;
      if (this.pedestalStage) {
        this.pedestalStage.resize(this.canvasWidth, this.canvasHeight);
      }
      this.impactLayer?.resize(this.canvasWidth, this.canvasHeight);
      this.render();
    },

    handleResize() {
      this.resizeCanvas();
    },

    /**
     * The board's two payloads, from wherever this board gets them.
     *
     * These two methods ARE the sandbox seam — the only places that know whether the
     * data came off the wire. Everything that consumes them is one code path.
     */
    async fetchTacticalMap() {
      if (this.sandbox) return this.sandbox.tacticalMap || null;
      const api = new TacticalmapApi(GameApiConfig);
      const response = await api.tacticalmapPlayerCurrentRetrieve();
      return response.data;
    },

    async fetchTacticalInfo() {
      if (this.sandbox) return this.sandbox.tacticalInfo || null;
      const api = new TacticalmapApi(GameApiConfig);
      const response = await api.tacticalmapPlayerTacticalInfoRetrieve();
      return response.data;
    },

    async loadTacticalMap() {
      this.loading = true;
      try {
        this.tacticalMapData = await this.fetchTacticalMap();
        console.log('[PlayerTacticalMap] ✅ Loaded tactical map:', this.tacticalMapData);
        console.log('[PlayerTacticalMap] Grid settings:', this.tacticalMapData.grid_settings);
        console.log('[PlayerTacticalMap] Spawned objects:', this.tacticalMapData.spawned_objects?.length || 0);
        console.log('[PlayerTacticalMap] Spawned objects details:', this.tacticalMapData.spawned_objects);
        console.log('[PlayerTacticalMap] Edges:', this.tacticalMapData.edges?.length || 0);
        console.log('[PlayerTacticalMap] Cells:', this.tacticalMapData.cells?.length || 0);

        // Initialize alive status for all spawned characters
        await this.initializeAliveStatus();

        // Load background image
        if (this.tacticalMapData.background_image_url) {
          await this.loadBackgroundImage(this.tacticalMapData.background_image_url);
        } else {
          console.warn('[PlayerTacticalMap] ⚠️ No background image URL');
        }

        // Build passable cells cache for performance
        this.buildPassableCellsCache();

        // Try to restore saved camera state first
        const cameraRestored = this.restoreCameraState();

        // Only align to player if no saved camera state exists (first visit)
        if (!cameraRestored) {
          this.alignToPlayerCharacter();
        }

        this.render();
        this.$emit('map-loaded', {objects: this.visibleObjects});

        /* A body moved while this was in flight. The response may have been built
           before that move committed, so ask again rather than draw a board that
           is one step behind and stays that way until the page is reloaded. */
        if (this.missedPositionEvent) {
          this.missedPositionEvent = false;
          await this.refreshVision();
        }
      } catch (error) {
        console.error('[PlayerTacticalMap] ❌ Error loading tactical map:', error);
      } finally {
        this.loading = false;
      }
    },

    async initializeAliveStatus() {
      // A sandbox board's figures carry their own `alive`; there is no campaign
      // position to reconcile them against.
      if (this.sandboxed) return;
      try {
        // Import WorldGameApi dynamically
        const { WorldGameApi } = await import('@/api/backendService');

        // Get current position with characters_on_position data
        const positionResponse = await WorldGameApi.worldPositionCurrentRetrieve();
        const position = positionResponse.data;

        if (!position || !position.characters_on_position) {
          console.log('[PlayerTacticalMap] No characters_on_position data available');
          return;
        }

        console.log('[PlayerTacticalMap] Initializing alive status from characters_on_position:', position.characters_on_position.length, 'characters');

        // Create a map of character ID -> alive status for quick lookup
        const aliveStatusMap = new Map();
        for (const char of position.characters_on_position) {
          aliveStatusMap.set(char.id, char.alive);
          console.log('[PlayerTacticalMap] Character', char.name, 'alive:', char.alive);
        }

        // Update spawned objects with alive status
        // Create new array to ensure Vue reactivity
        if (this.tacticalMapData && this.tacticalMapData.spawned_objects) {
          this.tacticalMapData.spawned_objects = this.tacticalMapData.spawned_objects.map(obj => {
            // Only update if we have data for this character
            if (aliveStatusMap.has(obj.id)) {
              const alive = aliveStatusMap.get(obj.id);
              console.log('[PlayerTacticalMap] Set alive status for', obj.name, ':', alive);
              return { ...obj, alive };
            } else {
              // Default to alive if not a character or no data
              return { ...obj, alive: true };
            }
          });
        }

        console.log('[PlayerTacticalMap] ✅ Alive status initialized');
      } catch (error) {
        console.error('[PlayerTacticalMap] ❌ Error initializing alive status:', error);
        // Not critical - characters will default to alive
      }
    },

    async loadTacticalCharacterInfo() {
      try {
        this.tacticalCharacterInfo = await this.fetchTacticalInfo();
        console.log('[PlayerTacticalMap] ✅ Loaded tactical character info:', this.tacticalCharacterInfo);
        console.log('[PlayerTacticalMap] Position:', this.tacticalCharacterInfo.x, this.tacticalCharacterInfo.y);
        console.log('[PlayerTacticalMap] Movement range:', this.tacticalCharacterInfo.movement_range);
        console.log('[PlayerTacticalMap] Movement remaining:', this.tacticalCharacterInfo.movement_remaining);
        console.log('[PlayerTacticalMap] Vision range:', this.tacticalCharacterInfo.vision_range);
      } catch (error) {
        console.error('[PlayerTacticalMap] ❌ Error loading tactical character info:', error);
        // Not critical - component can work without it (fallback to playerCharacter prop)
      }
    },

    /**
     * Re-ask the server what the party can see, without reloading the board.
     *
     * Sight is resolved server-side from where everyone stands, so every step
     * changes it - and what is under the fog is withheld from the response
     * entirely, which means a body walking into sight is not on the client at all
     * until the map is asked again. The background, edges and cells cannot move,
     * so only the vision block and the objects it gates are taken from the reply.
     *
     * Alive flags are kept: they arrive over the socket between loads and the
     * fog response carries no health.
     */
    async refreshVision() {
      if (this.visionRefreshInFlight) {
        this.visionRefreshQueued = true;
        return;
      }
      this.visionRefreshInFlight = true;

      try {
        const api = new TacticalmapApi(GameApiConfig);
        const response = await api.tacticalmapPlayerCurrentRetrieve();
        const data = response.data;

        // The viewer may have left the board while this was in flight.
        if (!data || !this.tacticalMapData || data.id !== this.tacticalMapData.id) {
          return;
        }

        const aliveById = new Map(
          (this.tacticalMapData.spawned_objects || []).map(obj => [obj.id, obj.alive])
        );

        this.tacticalMapData = {
          ...this.tacticalMapData,
          vision: data.vision,
          spawned_objects: (data.spawned_objects || []).map(obj => ({
            ...obj,
            alive: aliveById.has(obj.id) ? aliveById.get(obj.id) : true,
          })),
        };

        this.calculateReachableCells();
        this.render();
      } catch (error) {
        console.error('[PlayerTacticalMap] ❌ Error refreshing vision:', error);
      } finally {
        this.visionRefreshInFlight = false;
        if (this.visionRefreshQueued) {
          this.visionRefreshQueued = false;
          await this.refreshVision();
        }
      }
    },

    loadBackgroundImage(url) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          this.backgroundImage = img;
          this.buildTerrainSwatch();
          console.log('[PlayerTacticalMap] Background image loaded:', img.width, 'x', img.height);
          resolve();
        };
        img.onerror = (error) => {
          console.error('[PlayerTacticalMap] Error loading background image:', error);
          reject(error);
        };
        img.src = url;
      });
    },

    alignToPlayerCharacter() {
      if (!this.scaledGridConfig) return;

      let playerX, playerY;

      // PRIORITY 1: Use tactical character info (most accurate from API)
      if (this.tacticalCharacterInfo) {
        playerX = this.tacticalCharacterInfo.x;
        playerY = this.tacticalCharacterInfo.y;
        console.log('[PlayerTacticalMap] Aligning to player using tactical info at:', playerX, playerY);
      }
      // PRIORITY 2: Find player in spawned objects
      else if (this.playerCharacter && this.visibleObjects.length) {
        const playerObj = this.visibleObjects.find(obj =>
          obj.id === this.playerCharacter.id
        );
        if (playerObj) {
          playerX = playerObj.x;
          playerY = playerObj.y;
          console.log('[PlayerTacticalMap] Aligning to player using spawned object at:', playerX, playerY);
        }
      }

      if (playerX == null || playerY == null) return;

      const geometry = this.getMorphedCellGeometry(playerX, playerY, this.moverSpan);
      if (geometry) {
        this.frameCanvasPoints([geometry.center]);
      }
    },

    /**
     * Put the camera on a set of tokens, framing every one of them at once.
     *
     * Answers in the space the tokens are actually laid out in - their morphed cell
     * geometry - rather than re-deriving a position from the grid. The board is
     * drawn from the background image's centred position plus the offset, so a
     * camera derived from cell_width alone lands half the image away from the token
     * it was aiming at.
     *
     * @returns {number} how many of the requested tokens were found and framed
     */
    focusOnObjects(objectIds) {
      const points = (objectIds || [])
        .filter(Boolean)
        .map(id => this.visibleObjects.find(obj => obj.id === id))
        .filter(Boolean)
        .map(obj => {
          const override = this.tokenPositionOverrides[obj.id];
          const geometry = this.getMorphedCellGeometry(
            override ? override.x : obj.x,
            override ? override.y : obj.y,
            spanOf(obj)
          );
          return geometry ? geometry.center : null;
        })
        .filter(Boolean);

      if (!points.length) return 0;

      this.frameCanvasPoints(points);
      return points.length;
    },

    /**
     * Centre the camera on a group of canvas-space points, pulling the zoom back
     * until the whole group fits inside the viewport.
     *
     * The offset moves the board one for one in canvas space, so the correction is
     * the plain gap between where the group sits now and the viewport centre. Zoom
     * scales about that same centre, which is why it can be applied afterwards
     * without moving the group off it again.
     */
    frameCanvasPoints(points) {
      if (!points || !points.length) return;

      const xs = points.map(point => point.x);
      const ys = points.map(point => point.y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);

      const safeTop = Number(this.safeTop) || 0;
      const safeBottom = Number(this.safeBottom) || 0;
      const targetX = this.canvasWidth / 2;
      const targetY = (safeTop + (this.canvasHeight - safeBottom)) / 2;

      const spanX = (maxX - minX) * this.zoom;
      const spanY = (maxY - minY) * this.zoom;
      const roomX = this.canvasWidth * FRAME.usableWidth;
      const roomY = Math.max(0, this.canvasHeight - safeTop - safeBottom) * FRAME.usableHeight;

      if (spanX > roomX || spanY > roomY) {
        const scale = Math.min(
          spanX > 0 ? roomX / spanX : Infinity,
          spanY > 0 ? roomY / spanY : Infinity
        );
        this.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoom * scale));
      }

      /*
       * The zoom is decided first, and the offset then solves for it. Zoom scales
       * about the canvas centre, so an offset that put the group on a target other
       * than that centre would be undone by any zoom applied afterwards - which is
       * exactly what happens the moment a host reserves a safe area.
       */
      this.offsetX += (targetX - this.canvasWidth / 2) / this.zoom + this.canvasWidth / 2 - (minX + maxX) / 2;
      this.offsetY += (targetY - this.canvasHeight / 2) / this.zoom + this.canvasHeight / 2 - (minY + maxY) / 2;

      this.saveCameraState();
      this.render();
    },

    // Calculate where the background image is positioned
    getImagePosition() {
      if (!this.backgroundImage) {
        return { x: 0, y: 0, width: 0, height: 0 };
      }

      const width = this.backgroundImage.width;
      const height = this.backgroundImage.height;

      // Center the image in the canvas viewport, with pan offset
      const x = (this.canvasWidth - width) / 2 + this.offsetX;
      const y = (this.canvasHeight - height) / 2 + this.offsetY;

      return { x, y, width, height };
    },

    render() {
      if (!this.ctx) return;

      // Clear canvas
      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

      // Save context state and apply zoom transformation. The restore is in a
      // finally: a throw in any layer used to skip it, leaving the zoom transform
      // stacked on the context, so every later frame drew the map off screen and
      // the board stayed black long after the one bad frame.
      this.ctx.save();
      try {
      // Translate to center, apply zoom, translate back
      this.ctx.translate(this.canvasWidth / 2, this.canvasHeight / 2);
      this.ctx.scale(this.zoom, this.zoom);
      this.ctx.translate(-this.canvasWidth / 2, -this.canvasHeight / 2);

      // Draw layers
      this.drawBackground();

      // Sit the tokens into the scene before anything else is laid over it
      this.drawTokenGrounding();
      this.renderPedestals();

      // Draw passable cells highlight if enabled
      if (this.passableHighlightVisible) {
        this.drawPassableHighlight();
      }

      // The grid is the player's own switch and nothing else touches it: someone who
      // turned it on wants to read cells, and wanting that while aiming is exactly
      // when it matters most.
      //
      // It is ruled onto the floor, so everything cast as light onto that floor goes
      // over it. Drawn the other way round, a lattice across every cell of the map
      // sat on top of a reach lit at a twentieth of full strength and simply erased
      // it - turning the grid on made the movement area disappear.
      if (this.gridVisible) {
        this.drawGrid();
      }

      // Everything the armed action cannot reach, put in shadow, and the ground it
      // can, lit in that action's colour. Over the grid, because the lattice is ruled
      // onto the floor and the lamp is cast onto both.
      this.drawCastReach();

      // Draw movement area shadows if player character is selected (PREPARATION phase only)
      if (this.playerCharacter && this.reachableCells.size > 0 && this.canMove && !this.isSignedMover) {
        this.drawMovementArea();
      }

      // Draw skill preview if skill/item is selected (ACTION phase)
      if ((this.selectedSkill || this.selectedItem || this.selectedSpecial) && this.skillTargetPosition) {
        this.drawSkillPreview();
      }

      // Draw placed charges (persistent visual feedback for multi-charge skills)
      if (this.chargeTargets.length > 0) {
        this.drawPlacedCharges();
      }

      // Draw path to hovered cell (PREPARATION phase only)
      if (this.pathToHovered && this.pathToHovered.length > 0 && this.canMove) {
        this.drawPath(this.pathToHovered);
      }

      // No square is drawn under the pointer. It quantised the cursor to a cell in
      // every phase, which is the snap the aim was rebuilt to be rid of; where the
      // pointer is, is already shown by the pointer, and where a move would land is
      // shown by the path.

      // Character status rings are drawn by the token itself, on the pedestal's own
      // contact ellipse - see HoloToken's marker prop.

      } finally {
        this.ctx.restore();
      }

      // The fog is laid over the finished board, and in screen space: it has to cover
      // the whole viewport including the ground beyond the map's edge, which the
      // zoomed transform does not reach when the camera is pulled back.
      this.drawFogOfWar();

      // NO $forceUpdate() - it causes massive performance issues!
      // Token positions are updated via computed properties automatically
    },

    /**
     * The darkness beyond sight.
     *
     * Built as a veil over the whole viewport and then punched through where the
     * party can see, rather than painted cell by cell into the unlit parts: the lit
     * region is a handful of soft shapes while the unlit one is the rest of the
     * world, and only the first is cheap to describe.
     *
     * Four surfaces, in order. The falloff holds each source's radial dimming,
     * combined at their brightest so two characters standing together do not darken
     * each other's ground. The reveal holds the cells the server says are visible,
     * kept only where the falloff allows - so walls cut the light sharply while the
     * far rim of vision fades. The halo is that same reveal spread outward, which is
     * what thins the veil just past the limit of sight without letting it bleed
     * through a wall the reveal already stopped at. The fog itself is the tinted
     * veil, mottled with drifting murk, with the halo lifted out of it and then the
     * reveal cut clean through.
     *
     * The order of those two subtractions matters: the halo goes first and only
     * partially, so the ground beyond the torch keeps a thin veil, and the reveal
     * follows at full strength so what the party actually sees is never dimmed.
     *
     * Only the murk moves between frames. The shape of the darkness answers to the
     * party's sight and to the camera, and neither changes twenty times a second,
     * so the mask and the rim are built once per arrangement and kept - which is
     * what leaves the drifting haze costing a fill and three composites a frame.
     */
    drawFogOfWar() {
      if (!this.fogActive || !this.ctx || !this.canvasWidth || !this.canvasHeight) return;

      const { mask, rim } = this.fogMasks();

      // The veil is laid down opaque and only brought to its density afterwards. The
      // murk is opaque grain on opaque ground, so it deepens and thins the colour
      // without touching the alpha - built the other way round, every layer of grain
      // would push the darkness a little nearer a lid, and `density` would stop
      // describing anything.
      const fogCtx = this.fogLayer('fogCanvas');
      fogCtx.fillStyle = `rgb(${FOG.color.r}, ${FOG.color.g}, ${FOG.color.b})`;
      fogCtx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);
      this.drawFogMurk(fogCtx);

      fogCtx.globalCompositeOperation = 'destination-in';
      fogCtx.globalAlpha = FOG.density;
      fogCtx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);
      fogCtx.globalAlpha = 1;

      fogCtx.globalCompositeOperation = 'destination-out';
      this.drawFogLayer(fogCtx, mask);

      fogCtx.globalCompositeOperation = 'lighter';
      this.drawFogLayer(fogCtx, rim);
      fogCtx.globalCompositeOperation = 'source-over';

      this.ctx.drawImage(fogCtx.canvas, 0, 0, this.canvasWidth, this.canvasHeight);
    },

    /**
     * The two surfaces the veil is shaped by, rebuilt only when the shape changed.
     *
     * The cells the server reported are compared by identity rather than by content:
     * they arrive as one computed set that is replaced wholesale whenever the map
     * data does, so a new object IS a new arrangement and nothing has to walk the
     * cells to find that out.
     */
    fogMasks() {
      const signature = [
        this.canvasWidth, this.canvasHeight, this.zoom, this.offsetX, this.offsetY,
      ].join('|');

      if (
        this.fogMaskCache &&
        this.fogMaskCache.signature === signature &&
        this.fogMaskCache.cells === this.visibleCellKeys &&
        this.fogMaskCache.sources === this.visionSources
      ) {
        return this.fogMaskCache;
      }

      const cellSize = this.fogCellSize();
      const reveal = this.buildFogReveal(cellSize);

      // The rim is taken first, off a tight spread, and only then is the same
      // surface respread wide for the veil - two reaches out of one scratch canvas.
      const rim = this.buildFogRim(reveal, this.buildFogHalo(cellSize * FOG.rimSpread, reveal));
      const mask = this.buildFogMask(reveal, this.buildFogHalo(cellSize * FOG.haloSpread, reveal));

      this.fogMaskCache = {
        signature,
        cells: this.visibleCellKeys,
        sources: this.visionSources,
        mask,
        rim,
      };
      return this.fogMaskCache;
    },

    /**
     * Everything lifted out of the veil, as one surface.
     *
     * The halo at partial strength with the reveal laid over it: source-over on two
     * white shapes leaves `a + b - ab`, which is exactly what subtracting them one
     * after the other would have left - so the veil pays for one composite a frame
     * instead of two, and the sight edge still cuts through what the halo softened.
     */
    buildFogMask(reveal, halo) {
      const ctx = this.fogLayer('fogMaskCanvas');
      ctx.globalAlpha = 1 - FOG.nearDensity / FOG.density;
      this.drawFogLayer(ctx, halo);
      ctx.globalAlpha = 1;
      this.drawFogLayer(ctx, reveal);
      return ctx.canvas;
    },

    /**
     * The cyan the murk catches where it meets the light.
     *
     * The band between the halo and the reveal - everything the light reaches into
     * but does not reach through - tinted and added back over the veil. It is what
     * keeps the boundary of sight from being a mere absence of paint, and it is the
     * one place the fog carries the board's own colour.
     */
    buildFogRim(reveal, halo) {
      const ctx = this.fogLayer('fogRimCanvas');
      this.drawFogLayer(ctx, halo);

      ctx.globalCompositeOperation = 'destination-out';
      this.drawFogLayer(ctx, reveal);

      ctx.globalCompositeOperation = 'source-in';
      ctx.globalAlpha = FOG.rimStrength;
      ctx.fillStyle = `rgb(${FOG.rim.r}, ${FOG.rim.g}, ${FOG.rim.b})`;
      ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);

      return ctx.canvas;
    },

    /** A scratch surface covering the viewport, at the fog's own resolution. */
    fogSurface(slot) {
      const width = Math.max(1, Math.round(this.canvasWidth * FOG.resolution));
      const height = Math.max(1, Math.round(this.canvasHeight * FOG.resolution));

      let canvas = this[slot];
      if (!canvas) {
        canvas = document.createElement('canvas');
        this[slot] = canvas;
      }
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      return canvas;
    },

    /**
     * A cleared fog surface whose context takes screen coordinates.
     *
     * The surfaces are smaller than the viewport, so every one of them draws under a
     * scale transform and the rest of the fog can go on speaking in screen pixels.
     */
    fogLayer(slot) {
      const canvas = this.fogSurface(slot);
      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      ctx.filter = 'none';
      ctx.setTransform(FOG.resolution, 0, 0, FOG.resolution, 0, 0);
      return ctx;
    },

    /** One fog surface onto another, both being viewport-sized in screen terms. */
    drawFogLayer(ctx, canvas) {
      ctx.drawImage(canvas, 0, 0, this.canvasWidth, this.canvasHeight);
    },

    /**
     * How wide a blur reads on a fog surface.
     *
     * Canvas filters work in the surface's own pixels and ignore the transform, so a
     * radius meant in screen terms has to be brought down to the fog's resolution by
     * hand - and never below a pixel, or the blur stops doing anything at all.
     */
    fogBlur(screenRadius) {
      return Math.max(1, screenRadius * FOG.resolution);
    },

    /**
     * Where the camera puts a point on the board once the zoom is applied.
     *
     * The board's own layers draw inside the zoom transform; the fog draws outside
     * it, so it has to do that transform by hand.
     */
    fogToScreen(point) {
      const zoom = this.zoom || 1;
      const cx = this.canvasWidth / 2;
      const cy = this.canvasHeight / 2;
      return {
        x: cx + (point.x - cx) * zoom,
        y: cy + (point.y - cy) * zoom
      };
    },

    buildFogReveal(cellSize) {
      const falloff = this.buildFogFalloff(cellSize);

      const ctx = this.fogLayer('fogRevealCanvas');

      // Cells are inflated past their own edges and softened, so the union reads as
      // one lit area rather than a mosaic with seams between neighbouring cells.
      ctx.save();
      ctx.filter = `blur(${this.fogBlur(cellSize * FOG.feather * 0.5)}px)`;
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      this.visibleCellKeys.forEach(key => {
        const [x, y] = key.split(',').map(Number);
        const geometry = this.getMorphedCellGeometry(x, y);
        if (!geometry) return;
        const quad = this.inflatedQuad(geometry, cellSize * FOG.feather * 0.5 / (this.zoom || 1))
          .map(point => this.fogToScreen(point));
        ctx.moveTo(quad[0].x, quad[0].y);
        for (let i = 1; i < quad.length; i++) ctx.lineTo(quad[i].x, quad[i].y);
        ctx.closePath();
      });
      ctx.fill();
      ctx.restore();

      ctx.globalCompositeOperation = 'destination-in';
      this.drawFogLayer(ctx, falloff);
      ctx.globalCompositeOperation = 'source-over';

      return ctx.canvas;
    },

    /**
     * How far the light carries past what it actually lights.
     *
     * The reveal, spread outward - a blur and nothing more. Spreading the sight mask
     * rather than laying a second ring of radial gradients around each character
     * costs one pass instead of one per source, keeps the two shapes in step with
     * each other for free, and inherits the walls: light the reveal stopped at a
     * wall cannot glow around the far side of it, because there is nothing there to
     * spread from.
     *
     * `spread` is the reach in screen pixels, so the same surface serves both the
     * wide veil and the tight rim.
     */
    buildFogHalo(spread, reveal) {
      const ctx = this.fogLayer('fogHaloCanvas');
      ctx.filter = `blur(${this.fogBlur(spread)}px)`;
      this.drawFogLayer(ctx, reveal);
      ctx.filter = 'none';
      return ctx.canvas;
    },

    /**
     * Each source's light, dimming toward the limit of its own sight.
     *
     * Combined with `lighter` so overlapping sources take the brighter of the two:
     * added together, two allies standing close would blow their shared ground out,
     * and taken in order the later one would darken the earlier.
     */
    buildFogFalloff(cellSize) {
      const ctx = this.fogLayer('fogFalloffCanvas');
      ctx.globalCompositeOperation = 'lighter';

      this.visionSources.forEach(source => {
        const geometry = this.getMorphedCellGeometry(source.x, source.y);
        if (!geometry) return;
        const center = this.fogToScreen(geometry.center);
        // Even a blind character keeps their own footing lit. Nobody loses track of
        // the ground they are standing on, and a token floating on pure black reads
        // as a rendering fault rather than as darkness.
        const radius = Math.max(source.range + 0.75, 1.1) * cellSize;
        const wash = ctx.createRadialGradient(center.x, center.y, 0, center.x, center.y, radius);
        wash.addColorStop(0, 'rgba(255, 255, 255, 1)');
        wash.addColorStop(FOG.edge, 'rgba(255, 255, 255, 1)');
        wash.addColorStop(1, 'rgba(255, 255, 255, 0)');
        ctx.fillStyle = wash;
        ctx.fillRect(center.x - radius, center.y - radius, radius * 2, radius * 2);
      });

      ctx.globalCompositeOperation = 'source-over';
      return ctx.canvas;
    },

    /** One cell's on-screen size, which is what every fog distance is measured in. */
    fogCellSize() {
      const config = this.scaledGridConfig;
      const base = config ? Math.min(config.cell_width, config.cell_height) : 32;
      return base * (this.zoom || 1);
    },

    /**
     * The murk drifting through the dark.
     *
     * Two layers of the same tile at different scales, sliding against each other in
     * opposite directions, which is enough to keep the fog from reading as a flat
     * sheet of paint without ever resolving into a recognisable pattern. Composited
     * as `overlay` so it deepens and thins the veil that is already there rather than
     * adding a grey film of its own.
     */
    drawFogMurk(ctx) {
      const tile = this.fogNoise();
      if (!tile) return;

      const phase = this.prefersReducedMotion ? 0 : (performance.now() / 1000) * FOG.noiseDrift;

      ctx.save();
      ctx.globalCompositeOperation = 'overlay';
      [
        { scale: FOG.noiseScale, dx: phase, dy: phase * 0.35, alpha: FOG.noiseStrength },
        { scale: FOG.noiseScale * 0.45, dx: -phase * 0.6, dy: -phase * 0.2, alpha: FOG.noiseStrength * 0.7 }
      ].forEach(layer => {
        const pattern = ctx.createPattern(tile, 'repeat');
        if (!pattern) return;
        const ratio = layer.scale / FOG.noiseTile;
        pattern.setTransform(new DOMMatrix().translate(layer.dx, layer.dy).scale(ratio));
        ctx.globalAlpha = layer.alpha;
        ctx.fillStyle = pattern;
        ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);
      });
      ctx.restore();
    },

    /**
     * One tile of value noise, built once for the session.
     *
     * Three octaves is the point where the murk stops looking like static and starts
     * looking like something moving through air; the tile wraps, so it can be
     * repeated across the viewport without a visible seam.
     */
    fogNoise() {
      if (this.fogNoiseTile) return this.fogNoiseTile;

      const size = FOG.noiseTile;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;

      const ctx = canvas.getContext('2d');
      const image = ctx.createImageData(size, size);
      const lattice = (period) => {
        const values = new Float32Array(period * period);
        for (let i = 0; i < values.length; i++) values[i] = Math.random();
        return { period, values };
      };
      const sample = ({ period, values }, x, y) => {
        const fx = x * period;
        const fy = y * period;
        const x0 = Math.floor(fx);
        const y0 = Math.floor(fy);
        const tx = fx - x0;
        const ty = fy - y0;
        // Smoothstep between lattice points; linear interpolation shows the grid.
        const ease = (t) => t * t * (3 - 2 * t);
        const at = (ix, iy) => values[(iy % period + period) % period * period + (ix % period + period) % period];
        const top = at(x0, y0) + (at(x0 + 1, y0) - at(x0, y0)) * ease(tx);
        const bottom = at(x0, y0 + 1) + (at(x0 + 1, y0 + 1) - at(x0, y0 + 1)) * ease(tx);
        return top + (bottom - top) * ease(ty);
      };

      const octaves = [lattice(4), lattice(8), lattice(16)];
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          const u = x / size;
          const v = y / size;
          const value =
            sample(octaves[0], u, v) * 0.55 +
            sample(octaves[1], u, v) * 0.30 +
            sample(octaves[2], u, v) * 0.15;
          const index = (y * size + x) * 4;
          const level = Math.round(Math.max(0, Math.min(1, value)) * 255);
          image.data[index] = level;
          image.data[index + 1] = level;
          image.data[index + 2] = level;
          image.data[index + 3] = 255;
        }
      }
      ctx.putImageData(image, 0, 0);

      this.fogNoiseTile = canvas;
      return canvas;
    },

    drawBackground() {
      if (!this.backgroundImage) return;

      const imagePos = this.getImagePosition();

      // Draw with semi-transparency for better grid visibility
      this.ctx.globalAlpha = 0.5;
      this.ctx.drawImage(this.backgroundImage, imagePos.x, imagePos.y, imagePos.width, imagePos.height);
      this.ctx.globalAlpha = 1.0;
    },

    applyMorph(x, y, gridWidth, gridHeight, startX, startY) {
      const gridSettings = this.gridSettings;
      if (!gridSettings) return { x, y };

      const { corner_offsets } = gridSettings;

      // Use 4-point morphing if cornerOffsets are defined
      if (corner_offsets) {
        // Calculate normalized position within the grid (0 to 1)
        const u = (x - startX) / gridWidth;
        const v = (y - startY) / gridHeight;

        // Define the four corners of the grid in screen space
        // Default positions (no morph)
        const topLeftDefault = { x: startX, y: startY };
        const topRightDefault = { x: startX + gridWidth, y: startY };
        const bottomLeftDefault = { x: startX, y: startY + gridHeight };
        const bottomRightDefault = { x: startX + gridWidth, y: startY + gridHeight };

        // Apply corner offsets (scaled by grid dimensions)
        const topLeft = {
          x: topLeftDefault.x + (corner_offsets.topLeft?.x || 0) * gridWidth,
          y: topLeftDefault.y + (corner_offsets.topLeft?.y || 0) * gridHeight
        };
        const topRight = {
          x: topRightDefault.x + (corner_offsets.topRight?.x || 0) * gridWidth,
          y: topRightDefault.y + (corner_offsets.topRight?.y || 0) * gridHeight
        };
        const bottomLeft = {
          x: bottomLeftDefault.x + (corner_offsets.bottomLeft?.x || 0) * gridWidth,
          y: bottomLeftDefault.y + (corner_offsets.bottomLeft?.y || 0) * gridHeight
        };
        const bottomRight = {
          x: bottomRightDefault.x + (corner_offsets.bottomRight?.x || 0) * gridWidth,
          y: bottomRightDefault.y + (corner_offsets.bottomRight?.y || 0) * gridHeight
        };

        // Bilinear interpolation
        // Interpolate top edge
        const topX = topLeft.x * (1 - u) + topRight.x * u;
        const topY = topLeft.y * (1 - u) + topRight.y * u;

        // Interpolate bottom edge
        const bottomX = bottomLeft.x * (1 - u) + bottomRight.x * u;
        const bottomY = bottomLeft.y * (1 - u) + bottomRight.y * u;

        // Interpolate between top and bottom
        const finalX = topX * (1 - v) + bottomX * v;
        const finalY = topY * (1 - v) + bottomY * v;

        return {
          x: finalX,
          y: finalY
        };
      }

      // No morphing applied
      return { x, y };
    },

    inverseMorph(screenX, screenY, gridWidth, gridHeight, startX, startY) {
      const gridSettings = this.gridSettings;
      if (!gridSettings) return { x: screenX, y: screenY };

      const { corner_offsets } = gridSettings;

      // Check if we're using 4-point morphing
      const using4Point = corner_offsets && (
        (corner_offsets.topLeft?.x || 0) !== 0 || (corner_offsets.topLeft?.y || 0) !== 0 ||
        (corner_offsets.topRight?.x || 0) !== 0 || (corner_offsets.topRight?.y || 0) !== 0 ||
        (corner_offsets.bottomLeft?.x || 0) !== 0 || (corner_offsets.bottomLeft?.y || 0) !== 0 ||
        (corner_offsets.bottomRight?.x || 0) !== 0 || (corner_offsets.bottomRight?.y || 0) !== 0
      );

      // If no morphing is applied, return as-is
      if (!using4Point) {
        return { x: screenX, y: screenY };
      }

      // Use iterative Newton's method to find the inverse
      let x = screenX;
      let y = screenY;

      for (let i = 0; i < 10; i++) {
        const morphed = this.applyMorph(x, y, gridWidth, gridHeight, startX, startY);
        const errorX = morphed.x - screenX;
        const errorY = morphed.y - screenY;

        // If error is small enough, we're done
        if (Math.abs(errorX) < 0.1 && Math.abs(errorY) < 0.1) {
          break;
        }

        x -= errorX;
        y -= errorY;
      }

      return { x, y };
    },

    /**
     * A region of floor, drawn as one piece.
     *
     * Every overlay on this board describes an area of ground - what can be walked
     * on, what is within reach - and each was drawing itself cell by cell, fill and
     * border, which ruled a lattice of seams across ground that is continuous and,
     * with the grid also on, put a second grid over the first. Here the cells are
     * filled once as a single shape and the only line drawn is the boundary, where
     * the region meets ground outside it, which is the whole of what an area has to
     * say. That line is held at a constant optical weight so it stays a hairline at
     * any zoom, and the fill is composited additively so it lights the floor rather
     * than tinting it.
     *
     * `source` optionally pulls the light toward one cell, for a region that is cast
     * from somewhere rather than merely observed.
     */
    drawGroundRegion(keys, options) {
      if (!this.scaledGridConfig || !keys.size) return;

      const { color, fill, fillEdge = fill, edge, source = null } = options;
      const ctx = this.ctx;
      const px = (value) => value / (this.zoom || 1);
      const rgb = `${color.r}, ${color.g}, ${color.b}`;

      const geometries = new Map();
      const bounds = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
      keys.forEach(key => {
        const [x, y] = key.split(',').map(Number);
        const geometry = this.getMorphedCellGeometry(x, y);
        if (!geometry) return;
        geometries.set(key, { geometry, x, y });
        [geometry.topLeft, geometry.topRight, geometry.bottomRight, geometry.bottomLeft].forEach(point => {
          bounds.minX = Math.min(bounds.minX, point.x);
          bounds.minY = Math.min(bounds.minY, point.y);
          bounds.maxX = Math.max(bounds.maxX, point.x);
          bounds.maxY = Math.max(bounds.maxY, point.y);
        });
      });
      if (!geometries.size) return;

      ctx.save();
      ctx.beginPath();
      geometries.forEach(({ geometry }) => {
        const quad = this.inflatedQuad(geometry, px(0.5));
        ctx.moveTo(quad[0].x, quad[0].y);
        for (let i = 1; i < quad.length; i++) ctx.lineTo(quad[i].x, quad[i].y);
        ctx.closePath();
      });
      ctx.clip();
      ctx.globalCompositeOperation = 'lighter';
      ctx.shadowBlur = 0;

      if (source && fillEdge !== fill) {
        const spread = Math.max(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY) || 1;
        const wash = ctx.createRadialGradient(
          source.center.x, source.center.y, 0,
          source.center.x, source.center.y, spread * 0.75
        );
        wash.addColorStop(0, `rgba(${rgb}, ${fill})`);
        wash.addColorStop(1, `rgba(${rgb}, ${fillEdge})`);
        ctx.fillStyle = wash;
      } else {
        ctx.fillStyle = `rgba(${rgb}, ${fill})`;
      }
      ctx.fillRect(bounds.minX, bounds.minY, bounds.maxX - bounds.minX, bounds.maxY - bounds.minY);
      ctx.restore();

      ctx.save();
      ctx.beginPath();
      geometries.forEach(({ geometry, x, y }) => {
        const { topLeft, topRight, bottomLeft, bottomRight } = geometry;
        if (!keys.has(`${x},${y - 1}`)) {
          ctx.moveTo(topLeft.x, topLeft.y);
          ctx.lineTo(topRight.x, topRight.y);
        }
        if (!keys.has(`${x + 1},${y}`)) {
          ctx.moveTo(topRight.x, topRight.y);
          ctx.lineTo(bottomRight.x, bottomRight.y);
        }
        if (!keys.has(`${x},${y + 1}`)) {
          ctx.moveTo(bottomRight.x, bottomRight.y);
          ctx.lineTo(bottomLeft.x, bottomLeft.y);
        }
        if (!keys.has(`${x - 1},${y}`)) {
          ctx.moveTo(bottomLeft.x, bottomLeft.y);
          ctx.lineTo(topLeft.x, topLeft.y);
        }
      });
      ctx.globalCompositeOperation = 'lighter';
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.lineWidth = px(1);
      ctx.strokeStyle = `rgba(${rgb}, ${edge})`;
      ctx.shadowBlur = px(5);
      ctx.shadowColor = `rgba(${rgb}, ${Math.min(1, edge + 0.1)})`;
      ctx.stroke();
      ctx.restore();

      ctx.shadowBlur = 0;
    },

    /**
     * Ground that can be walked at all. An inspection aid, so it is the quietest
     * thing on the board: it states where the floor ends and nothing else, leaving
     * the cell divisions to the grid and the foreground to the reach drawn over it.
     */
    drawPassableHighlight() {
      this.drawGroundRegion(this.passableCellsCache, {
        color: { r: 64, g: 255, b: 140 },
        fill: 0.028,
        edge: 0.3
      });
    },

    drawGrid() {
      const config = this.scaledGridConfig;
      if (!config) return;

      const { cell_width, cell_height, columns, rows } = config;

      // Position grid relative to the background image
      const imagePos = this.getImagePosition();
      const gridWidth = columns * cell_width;
      const gridHeight = rows * cell_height;
      const startX = imagePos.x;
      const startY = imagePos.y;

      // One path for every cell, stroked once.
      //
      // Each cell used to be stroked on its own, so every edge between two cells was
      // drawn twice and composited twice - the interior of the grid came out at
      // double the weight it was set to, against single-strength lines around the
      // rim. Gathered into one path the whole lattice composites once and the shared
      // edges stop doubling, and it is one stroke instead of three hundred.
      //
      // The width is held at a constant optical weight, like every other line on the
      // board, so the lattice stays a hairline when the map is zoomed in rather than
      // thickening into a cage over it.
      this.ctx.lineWidth = 1 / (this.zoom || 1);

      // PERFORMANCE: Iterate ONLY over passable cells (not entire grid)
      // This is much faster when grid is large but few cells are passable
      const trace = (accept) => {
        this.ctx.beginPath();
        this.passableCellsCache.forEach(cellKey => {
          if (accept && !accept(cellKey)) return;
          const [col, row] = cellKey.split(',').map(Number);

          const x = startX + col * cell_width;
          const y = startY + row * cell_height;

          // Apply morph to all 4 corners of the cell
          const topLeft = this.applyMorph(x, y, gridWidth, gridHeight, startX, startY);
          const topRight = this.applyMorph(x + cell_width, y, gridWidth, gridHeight, startX, startY);
          const bottomLeft = this.applyMorph(x, y + cell_height, gridWidth, gridHeight, startX, startY);
          const bottomRight = this.applyMorph(x + cell_width, y + cell_height, gridWidth, gridHeight, startX, startY);

          // Add the morphed cell as a quadrilateral
          this.ctx.moveTo(topLeft.x, topLeft.y);
          this.ctx.lineTo(topRight.x, topRight.y);
          this.ctx.lineTo(bottomRight.x, bottomRight.y);
          this.ctx.lineTo(bottomLeft.x, bottomLeft.y);
          this.ctx.closePath();
        });
        this.ctx.stroke();
      };

      // The lattice answers to the aim rather than ignoring it.
      //
      // One colour over both the ground a skill can be cast on and the ground it
      // cannot makes the grid argue with the reach underneath it - the eye reads the
      // strongest edge in a region, and a uniform lattice is stronger than a field lit
      // at a twentieth. So while an action is armed the rule inside the reach carries
      // the action's own colour and the rest drops to a cold whisper: the same
      // lattice, still readable cell by cell, saying which cells are in play.
      //
      // Both stay well under the aim field's own weight. A grid that competes with
      // the thing it is orienting is the annoying kind.
      const reach = this.castReachVisible;
      if (!reach) {
        this.ctx.strokeStyle = GRID_LINE.base;
        trace(null);
        return;
      }

      const { r, g, b } = this.aimColor(this.selectedAbility);
      this.ctx.strokeStyle = GRID_LINE.outside;
      trace(key => !reach.has(key));
      this.ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${GRID_LINE.insideAlpha})`;
      trace(key => reach.has(key));
    },

    /**
     * Ground the character can reach, drawn in the board's own idiom rather than as
     * shaded squares with a dashed border round them.
     *
     * The region is filled once as a single shape - painting each cell drew a lattice
     * of seams through what is one continuous piece of floor - and composited
     * additively, so it lights the ground instead of laying a wash over it. The one
     * line in it is the boundary, held at a constant optical weight so it stays a
     * hairline at any zoom instead of thickening into a ribbon.
     *
     * It is the viewer's own colour, and so is the path, because the two are one
     * statement: this is your reach, and this is your route through it.
     */
    /** A cell quad pushed out from its own centre, so neighbours overlap slightly
     *  and a region filled from many of them shows no seams where they meet. */
    inflatedQuad(geometry, pad) {
      const push = (point) => {
        const dx = point.x - geometry.center.x;
        const dy = point.y - geometry.center.y;
        const length = Math.hypot(dx, dy) || 1;
        return { x: point.x + (dx / length) * pad, y: point.y + (dy / length) * pad };
      };
      return [push(geometry.topLeft), push(geometry.topRight), push(geometry.bottomRight), push(geometry.bottomLeft)];
    },

    /**
     * Ground the character can reach. Carries the viewer's own colour, and so does
     * the route through it, because the two are one statement.
     *
     * The floor someone is standing on is still floor: an occupied cell adjoining
     * the reach is drawn as part of it and the boundary runs straight past, rather
     * than notching the outline around every body on the board. It simply cannot be
     * reached - no path, no arrival - which is what the rule actually is.
     */
    /**
     * Where the armed action may be taken, and everywhere it may not.
     *
     * Two statements, and they are one gesture: the ground in reach is lit in the
     * action's own colour, and everything else is put in shadow. Either alone is
     * weaker than both - lighting without shadowing leaves a faint wash a player has
     * to hunt for on a busy map, and shadowing without lighting says only "not here".
     * Together the board reads as a room with one lamp in it.
     *
     * The reach borrows the movement area's idiom exactly - one filled region, graded
     * away from the caster, with a hairline boundary - because reach-to-move and
     * reach-to-cast are the same kind of fact and should not need two visual
     * languages. What separates them is colour alone: a move is the viewer's own
     * blue, a cast is what the cast will do.
     */
    drawCastReach() {
      const reach = this.castReachVisible;
      if (!reach) return;

      this.drawCastShadow(reach);

      const casterX = this.tacticalCharacterInfo?.x ?? this.playerCharacter?.x ?? 0;
      const casterY = this.tacticalCharacterInfo?.y ?? this.playerCharacter?.y ?? 0;

      this.drawGroundRegion(reach, {
        color: this.aimColor(this.selectedAbility),
        fill: 0.05,
        fillEdge: 0.013,
        edge: 0.26,
        source: this.getMorphedCellGeometry(casterX, casterY, this.moverSpan),
      });
    },

    /**
     * The shadow over everything out of reach.
     *
     * It is a veil punched through where the action can land, not a wash painted onto
     * the cells it cannot - the unreachable part of a board is almost all of it, and
     * only the small half is cheap to describe. Punching means `destination-out`,
     * which would eat the map if it were done on the board itself, so the veil is
     * composed on its own surface and blitted over the finished floor.
     *
     * That surface is built at half resolution and kept: the reach changes when an
     * ability is armed or the caster moves, not when the pointer does, so aiming
     * across a board costs one `drawImage` a frame and nothing else.
     */
    drawCastShadow(reach) {
      const surface = this.castShadowSurface(reach);
      if (!surface) return;

      this.ctx.save();
      this.ctx.setTransform(1, 0, 0, 1, 0, 0);
      this.ctx.globalCompositeOperation = 'source-over';
      this.ctx.drawImage(surface, 0, 0, this.canvasWidth, this.canvasHeight);
      this.ctx.restore();
    },

    castShadowSurface(reach) {
      if (!this.canvasWidth || !this.canvasHeight) return null;

      const signature = [this.canvasWidth, this.canvasHeight, this.zoom, this.offsetX, this.offsetY].join('|');
      if (this.castShadowCache && this.castShadowCache.signature === signature && this.castShadowCache.reach === reach) {
        return this.castShadowCache.canvas;
      }

      const { resolution } = CAST_SHADOW;
      const width = Math.max(1, Math.round(this.canvasWidth * resolution));
      const height = Math.max(1, Math.round(this.canvasHeight * resolution));

      let canvas = this.castShadowCanvas;
      if (!canvas) {
        canvas = document.createElement('canvas');
        this.castShadowCanvas = canvas;
      }
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }

      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, width, height);
      ctx.globalCompositeOperation = 'source-over';
      ctx.filter = 'none';

      // The surface speaks the board's own coordinates - its resolution and the
      // camera's zoom folded into one transform - so the cell quads that describe the
      // hole are the same ones every other layer is drawn from.
      const zoom = this.zoom || 1;
      ctx.setTransform(resolution, 0, 0, resolution, 0, 0);
      ctx.translate(this.canvasWidth / 2, this.canvasHeight / 2);
      ctx.scale(zoom, zoom);
      ctx.translate(-this.canvasWidth / 2, -this.canvasHeight / 2);

      const { color, density, feather } = CAST_SHADOW;
      ctx.fillStyle = `rgba(${color.r}, ${color.g}, ${color.b}, ${density})`;
      // Generously beyond the viewport: pulled back, the camera shows world well
      // outside the canvas rectangle, and a veil that stopped at its edge would leave
      // a lit band around a zoomed-out board.
      ctx.fillRect(
        -this.canvasWidth * 2, -this.canvasHeight * 2,
        this.canvasWidth * 5, this.canvasHeight * 5
      );

      const carve = () => {
        ctx.beginPath();
        reach.forEach(key => {
          const [x, y] = key.split(',').map(Number);
          const geometry = this.getMorphedCellGeometry(x, y);
          if (!geometry) return;
          // Inflated so neighbouring cells overlap: butted quads leave hairline seams
          // the veil shows through, and a grid of them reads as a mesh over the floor.
          const quad = this.inflatedQuad(geometry, 0.75 / zoom);
          ctx.moveTo(quad[0].x, quad[0].y);
          for (let i = 1; i < quad.length; i++) ctx.lineTo(quad[i].x, quad[i].y);
          ctx.closePath();
        });
        ctx.fill();
      };

      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = '#fff';

      // The soft pass first, then the same shape solid over it. The blur alone leaves
      // the reach itself veiled at half strength; the solid pass alone lands as a
      // stencil cut with scissors. Canvas filters work in surface pixels and ignore
      // the transform, so the feather is converted out of cells by hand.
      const cell = (this.scaledGridConfig?.cell_width || 50) * zoom * resolution;
      const blur = Math.min(40, Math.max(2, cell * feather));
      ctx.filter = `blur(${blur}px)`;
      carve();
      ctx.filter = 'none';
      carve();

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalCompositeOperation = 'source-over';

      this.castShadowCache = { signature, reach, canvas };
      return canvas;
    },

    drawMovementArea() {
      if (!this.scaledGridConfig || this.reachableCells.size === 0) return;

      // The reach is keyed by the anchor a move would end on, but what the player is
      // being shown is where their BODY can stand. For a standard creature the two
      // are the same cell; for a boss the anchor set is the block's north-west
      // corners and lighting only those draws a region shifted a cell and a half up
      // and to the left of the ground it actually describes.
      const span = this.moverSpan;
      const keys = new Set();
      this.reachableCells.forEach(cell => {
        footprintKeys(cell.x, cell.y, span).forEach(key => keys.add(key));
      });

      const reach = new Set(keys);
      this.visibleObjects.forEach(obj => {
        footprintCells(obj.x, obj.y, spanOf(obj)).forEach(cell => {
          const key = `${cell.x},${cell.y}`;
          if (reach.has(key)) return;
          const adjoins = [[1, 0], [-1, 0], [0, 1], [0, -1]].some(
            ([dx, dy]) => reach.has(`${cell.x + dx},${cell.y + dy}`)
          );
          if (adjoins) keys.add(key);
        });
      });
      if (keys.size < 2) return;

      const source = this.getMorphedCellGeometry(
        this.tacticalCharacterInfo?.x ?? this.playerCharacter?.x ?? 0,
        this.tacticalCharacterInfo?.y ?? this.playerCharacter?.y ?? 0,
        span
      );

      this.drawGroundRegion(keys, {
        color: this.relationColor('self'),
        fill: 0.055,
        fillEdge: 0.014,
        edge: 0.34,
        source
      });
    },

    /**
     * The route, in the same colour and at the same optical weight as the reach it
     * runs through: a hairline over its own glow, with a mark on each cell it steps
     * through. The old trace was a two-pixel dashed line in world units, so it grew
     * into a ribbon as the map zoomed and its dashes landed wherever they fell.
     *
     * The route is stepped in anchors but drawn through the centres of the BLOCK the
     * body holds, which is where the token itself is drawn. Measured on the anchor
     * cell instead, a boss's trace ran a cell up and to the left of the figure it
     * belonged to and appeared to leave from its shoulder.
     */
    drawPath(path) {
      if (!this.scaledGridConfig || path.length < 2) return;

      const span = this.moverSpan;
      const points = path
        .map(cell => this.getMorphedCellGeometry(cell.x, cell.y, span))
        .filter(Boolean)
        .map(geometry => geometry.center);
      if (points.length < 2) return;

      const ctx = this.ctx;
      const px = (value) => value / (this.zoom || 1);
      const { r, g, b } = this.relationColor('self');
      const rgb = `${r}, ${g}, ${b}`;

      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';

      const trace = () => {
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
        ctx.stroke();
      };

      ctx.strokeStyle = `rgba(${rgb}, 0.16)`;
      ctx.lineWidth = px(3.5);
      trace();

      ctx.strokeStyle = `rgba(${rgb}, 0.85)`;
      ctx.lineWidth = px(1);
      trace();

      // A tick on each cell the route passes through, so the number of steps can be
      // counted off the line itself.
      ctx.fillStyle = `rgba(${rgb}, 0.75)`;
      for (let i = 1; i < points.length - 1; i++) {
        ctx.beginPath();
        ctx.arc(points[i].x, points[i].y, px(1.4), 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
      ctx.shadowBlur = 0;
    },

    relationColor(relation) {
      switch (relation) {
        case 'self': return { r: 255, g: 209, b: 71 };
        case 'ally': return { r: 74, g: 222, b: 128 };
        case 'enemy': return { r: 255, g: 82, b: 82 };
        default: return { r: 170, g: 178, b: 196 };
      }
    },

    /**
     * Bed the tokens into the map.
     *
     * Both passes are composite-only - they derive from the pixels already on the
     * canvas rather than being painted over them, which is the whole difference
     * between reading as part of the scene and reading as a decal. The emissive
     * floor glow this replaces used plus-lighter, and so lit a black shadow and a
     * lit plate by exactly the same amount.
     *
     * Both are ellipses carrying the PEDESTAL's foreshortening and ground rotation,
     * not the cell's: the plate is drawn from a lower camera than the map, so a
     * shadow shaped to the cell could never sit under it.
     */
    /**
     * Hand the pedestals to the 3D stage.
     *
     * They are placed in cells, not pixels: the stage takes the grid's own four
     * morphed corners and derives its projection from them, so the props go through
     * the same world-to-screen the floor did rather than a camera fitted by eye.
     */
    renderPedestals() {
      const config = this.scaledGridConfig;
      const stage = this.pedestalStage;
      if (!stage || !config) return;

      /*
       * The props are scenery, and scenery is never worth the board.
       *
       * This runs inside the map's own render, so a throw here - a lost WebGL
       * context, a driver refusing a shader - propagated out through mounted() and
       * took the whole tactical canvas down with it, leaving a component that had
       * unmounted for reasons visible nowhere near the cause. One failure now
       * retires the layer and the floor, the grid and the figures carry on.
       */
      try {
        this.drawPedestals(stage);
      } catch (error) {
        console.warn('[PlayerTacticalMap] pedestal stage failed; retiring it:', error);
        try {
          stage.dispose();
        } catch (disposeError) {
          console.warn('[PlayerTacticalMap] pedestal stage would not dispose:', disposeError);
        }
        this.pedestalStage = null;
        // The figures put their own plate back, so nobody ends up standing on air.
        this.pedestalsAreReal = false;
      }
    },

    drawPedestals(stage) {
      // The map's geometry is in unzoomed canvas units - the 2D context carries the
      // zoom itself, as a transform about the canvas centre. The WebGL layer has no
      // such transform, so the same one is applied to the geometry on the way in and
      // the props are drawn at the display's real resolution at every zoom.
      const zoom = this.zoom || 1;
      const cx = this.canvasWidth / 2;
      const cy = this.canvasHeight / 2;
      const zoomed = p => ({ x: (p.x - cx) * zoom + cx, y: (p.y - cy) * zoom + cy });

      stage.render(
        this.pedestalBodies()
          .map(body => {
            const geometry = this.getMorphedCellGeometry(body.x, body.y, body.span);
            if (!geometry) return null;
            /*
             * A figure that lunges takes its plate with it.
             *
             * The hologram is projected FROM the plate, so a body carried a cell and
             * a half forward while its plinth stayed behind reads as the projection
             * coming loose rather than as a fighter closing. Applied before `zoomed`
             * so it scales exactly as the token overlay's own shift does — the two
             * layers move by one number, not by two that agree today.
             *
             * The recoil is deliberately NOT here: being hit moves a body on its
             * stand, and a plinth that flinched with it would be the ground moving,
             * which is what the board shake is for and only heavy casts earn.
             */
            const step = this.lungeShift(body.id);
            const stepped = point => ({ x: point.x + step.x, y: point.y + step.y });
            return {
              ...body,
              quad: {
                topLeft: zoomed(stepped(geometry.topLeft)),
                topRight: zoomed(stepped(geometry.topRight)),
                bottomRight: zoomed(stepped(geometry.bottomRight)),
                bottomLeft: zoomed(stepped(geometry.bottomLeft)),
              },
              // The block's own on-screen cell width, so a prop one cell tall reads
              // as one cell tall at the near edge and at the far one alike.
              heightScale: (geometry.footprintWidth / body.span) * zoom,
              // The light of the ground it is standing on, so the plate belongs to
              // the terrain instead of being lit by a studio somewhere else.
              ambient: this.terrainLightAt(body.x + body.span / 2, body.y + body.span / 2),
              // Nearer the viewer is further down the board, and a big body is
              // ranked by the front edge it stands on, as the tokens are.
              depth: body.y + body.span,
            };
          })
          .filter(Boolean)
      );
    },

    /**
     * The colour of the ground at a point on the grid.
     *
     * A prop lit by a fixed studio rig sits on the map rather than in it: the board
     * is a night scene with warm sodium pools, cold spill and long black shadows,
     * and a plate lit identically everywhere reads as pasted on wherever the terrain
     * disagrees. Feeding the floor's own colour back into the prop's light is what
     * makes it belong — it goes dark in the dark and picks up the lamps it is
     * standing under.
     *
     * Sampled from a thumbnail of the background built once, not from the live
     * canvas: reading pixels back from the canvas stalls the pipeline, and this is
     * asked once per token per frame.
     */
    terrainLightAt(gridX, gridY) {
      const swatch = this.terrainSwatch;
      const config = this.scaledGridConfig;
      if (!swatch || !config) return null;

      const x = Math.min(swatch.width - 1, Math.max(0,
        Math.round((gridX / config.columns) * swatch.width)));
      const y = Math.min(swatch.height - 1, Math.max(0,
        Math.round((gridY / config.rows) * swatch.height)));
      const at = (y * swatch.width + x) * 4;
      return {
        r: swatch.data[at] / 255,
        g: swatch.data[at + 1] / 255,
        b: swatch.data[at + 2] / 255,
      };
    },

    /**
     * A thumbnail of the terrain, read once, for lighting props from the floor.
     *
     * Deliberately tiny. Anything a plate picks up off the ground is an average over
     * an area, not a pixel, and a small image is both the correct blur and cheap to
     * sample; it also keeps the whole thing off the render path.
     */
    buildTerrainSwatch() {
      this.terrainSwatch = null;
      const image = this.backgroundImage;
      if (!image?.width) return;

      try {
        const width = 48;
        const height = Math.max(1, Math.round((image.height / image.width) * width));
        const scratch = document.createElement('canvas');
        scratch.width = width;
        scratch.height = height;
        const ctx = scratch.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(image, 0, 0, width, height);
        this.terrainSwatch = ctx.getImageData(0, 0, width, height);
      } catch (error) {
        // A cross-origin background taints the canvas and cannot be read back. The
        // props then light from the room alone, which is the old behaviour.
        console.warn('[PlayerTacticalMap] terrain light unavailable:', error);
      }
    },

    /**
     * Everything the 3D stage draws, in cells.
     *
     * Two kinds share the layer. Plates go under the figures; chests stand on
     * their own cell as scenery the player can walk up to and open. Both are
     * placed identically — from the four morphed corners of the cell they are on
     * — so the only thing a body has to declare is which prop it is.
     *
     * The move preview is in here too. It is a figure standing somewhere, and once
     * the plate is a prop in the scene rather than part of the sprite, leaving it
     * out would stand the preview on bare ground.
     */
    pedestalBodies() {
      /* A downed fighter keeps its plate. The exclusion here was the other half
         of hiding the figure; with the projection dimmed rather than gone, a
         missing pedestal would leave it standing on nothing. */
      const bodies = this.visibleObjects
        .filter(obj => obj.type === 'character')
        .map(obj => {
          const override = this.tokenPositionOverrides[obj.id];
          return {
            kind: 'pedestal',
            id: obj.id,
            x: override ? override.x : obj.x,
            y: override ? override.y : obj.y,
            span: spanOf(obj),
            color: this.tokenProjectionColor(obj),
            marker: this.holoMarker(obj),
          };
        });

      const ghost = this.movementGhost;
      if (ghost) {
        bodies.push({
          kind: 'pedestal',
          id: `${ghost.id}:ghost`,
          x: ghost.x,
          y: ghost.y,
          span: spanOf(ghost),
          color: this.relationCssColor(ghost.relation),
          marker: 'none',
        });
      }

      for (const chest of this.visibleChests) {
        bodies.push({
          kind: 'chest',
          id: chest.id,
          x: chest.x,
          y: chest.y,
          // A chest is one cell by the brief, whatever its tactical_size says.
          span: 1,
          color: null,
          marker: this.chestMarker(chest),
        });
      }

      return bodies;
    },

    /** Which chests are on the board, straight off the fog-filtered payload. */
    chestMarker(chest) {
      if (this.selectedObjectId === chest.id) return 'selected';
      if (this.hoveredObjectId === chest.id) return 'hover';
      return 'none';
    },

    drawTokenGrounding() {
      if (!this.scaledGridConfig || !this.backgroundImage) return;

      for (const obj of this.visibleObjects) {
        if (obj.type !== 'character') continue;

        const geometry = this.objectCellGeometry(obj);
        if (!geometry) continue;

        // The width of whatever is actually standing there, so the contact shadow
        // is the shadow of that and not of the sprite it replaced.
        const plateWidth =
          geometry.footprintWidth
          * (this.pedestalsAreReal ? PEDESTAL_FOOTPRINT : HOLO.baseFit);

        /*
         * The shadow lies in the cell's own floor, not in the pedestal's.
         *
         * The plate keeps the proportions it was rendered with — warping a solid
         * object to match a cell that is up to 2.8x deeper only makes it look
         * broken. So the sprite is left alone and the contact is carried here
         * instead: this ellipse takes the cell's exact rotation, foreshortening and
         * shear, and being a soft shadow it can do that invisibly. An object whose
         * shadow sits correctly in the ground reads as standing on it, which is the
         * thing the plate's own silhouette cannot say on its own.
         */
        const ground = cellGroundBasis(geometry);
        if (!ground) continue;

        /*
         * Where the plate actually meets the floor.
         *
         * The sprite hangs from the token's box centre and its contact ellipse sits
         * lower down in the artwork, so the shadow has to be dropped to find it. The
         * 3D prop has no such offset: it stands on the ground plane at the centre of
         * its own block, which is the point the cell geometry already gives.
         */
        const drop = this.pedestalsAreReal ? 0 : plateWidth * HOLO_CONTACT_DROP;

        /* The contact shadow belongs to the body, not to the cell it left: a figure
           carried forward by its own blow drags its shadow and its bounce with it,
           exactly as it drags the plate they are cast by. */
        const step = this.lungeShift(obj.id);

        this.ctx.save();
        this.ctx.translate(geometry.center.x + step.x, geometry.center.y + drop + step.y);
        // In the cell's basis a circle is already the right ellipse — the old
        // scale(1, squash) would foreshorten it a second time, to the wrong camera.
        this.ctx.transform(ground.a, ground.b, ground.c, ground.d, 0, 0);

        // Contact shadow: multiply, so it darkens what is already there instead of
        // laying grey over it, and a little wider than the plate as contact
        // shadows are. This is what stops the pedestal reading as floating.
        const shadowR = plateWidth * HOLO_GROUND.rx * HOLO.shadowSpread;
        const shadow = this.ctx.createRadialGradient(0, 0, 0, 0, 0, shadowR);
        shadow.addColorStop(0, `rgba(0, 0, 0, ${HOLO.shadowStrength})`);
        shadow.addColorStop(0.55, `rgba(0, 0, 0, ${HOLO.shadowStrength * 0.55})`);
        shadow.addColorStop(1, 'rgba(0, 0, 0, 0)');
        this.ctx.globalCompositeOperation = 'multiply';
        this.ctx.fillStyle = shadow;
        this.ctx.beginPath();
        this.ctx.arc(0, 0, shadowR, 0, Math.PI * 2);
        this.ctx.fill();

        // The light the projection throws back down. soft-light scales with each
        // pixel's own luminance, so a lit floor plate picks the colour up while a
        // shadowed gap stays dark - which is what makes it read as cast rather
        // than drawn.
        const { r, g, b } = this.relationColor(obj.relation);
        const bounceR = plateWidth * HOLO.bounceSpread;
        const bounce = this.ctx.createRadialGradient(0, 0, 0, 0, 0, bounceR);
        bounce.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${HOLO.bounceStrength})`);
        bounce.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, ${HOLO.bounceStrength * 0.45})`);
        bounce.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
        this.ctx.globalCompositeOperation = 'soft-light';
        this.ctx.fillStyle = bounce;
        this.ctx.beginPath();
        this.ctx.arc(0, 0, bounceR, 0, Math.PI * 2);
        this.ctx.fill();

        this.ctx.restore();
      }

      this.drawSideRings();
    },

    /**
     * The side ring, for the 3D pedestal path.
     *
     * `drawPedestals()` stands a real prop in the cell and tints it by relation and
     * marker (`tintPedestal`) — there is no second material slot on that mesh for an
     * independent fact, and there must not be: side is allegiance, not hostility, and
     * baking it into the same emission the relation colour already owns would be
     * exactly the conflation this task exists to avoid. So the ring is drawn here
     * instead, as its own pass on the 2D floor layer, in the cell's own ground basis —
     * the same technique `drawTokenGrounding` uses for the contact shadow — sized
     * clear of the plate's footprint so the opaque 3D prop drawn over it doesn't hide
     * it.
     *
     * The sprite path draws its own equivalent ring inside `HoloToken.vue`, gated the
     * same way the status ring already is: `plate === 'sprite'` only. Never both —
     * `pedestalsAreReal` selects exactly one of the two the way it already selects
     * `pedestalPlate`.
     *
     * The side's emblem rides the ring on both paths, for the same reason: a thin
     * coloured hairline says "a side", not "which side", and is the first thing a
     * lit board washes out.
     */
    drawSideRings() {
      if (!this.pedestalsAreReal || !this.scaledGridConfig || !this.backgroundImage) return;

      for (const obj of this.visibleObjects) {
        if (obj.type !== 'character') continue;

        const slot = this.characterSideSlot(obj);
        if (slot == null) continue;

        const geometry = this.objectCellGeometry(obj);
        if (!geometry) continue;

        const ground = cellGroundBasis(geometry);
        if (!ground) continue;

        const plateWidth = geometry.footprintWidth * PEDESTAL_FOOTPRINT;
        const ringR = plateWidth * HOLO_GROUND.rx * SIDE_RING.spread;
        const ringWidth = Math.max(1, plateWidth * SIDE_RING.thickness);
        const color = resolveSideColor(slot, this.$el);

        this.ctx.save();
        this.ctx.translate(geometry.center.x, geometry.center.y);
        this.ctx.transform(ground.a, ground.b, ground.c, ground.d, 0, 0);
        this.ctx.globalCompositeOperation = 'source-over';
        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = ringWidth;
        this.ctx.globalAlpha = SIDE_RING.opacity;
        this.ctx.beginPath();
        this.ctx.arc(0, 0, ringR, 0, Math.PI * 2);
        this.ctx.stroke();
        this.ctx.restore();

        this.drawSideMark(geometry.center, ground, ringR, plateWidth, slot, color);
      }
    },

    /**
     * The side's emblem, at the front of the ring it belongs to.
     *
     * Upright, deliberately: the ring lies on the floor and takes the cell's own
     * basis, but the emblem is a label and a foreshortened one is a smudge at the
     * size a board draws a token. So only its ANCHOR goes through the ground
     * basis — the ring's forward point, which is where the ring is closest to the
     * viewer and least likely to end up behind the prop — and the mark itself is
     * drawn in screen space from there.
     *
     * The path is the same 24-unit `utils/sideEmblems.js` string the rail and the
     * establishment screen draw, through `Path2D`, so the sign is identical on
     * every surface rather than re-authored per renderer.
     */
    drawSideMark(center, ground, ringR, plateWidth, slot, color) {
      if (typeof Path2D === 'undefined') return;

      const path = sideEmblemPath(slot);
      if (!path) return;

      /* (0, ringR) in the cell's ground basis — the ring's near edge. */
      const frontX = ground.c * ringR;
      const frontY = ground.d * ringR;
      const size = plateWidth * SIDE_RING.markSize;
      const scale = size / 24;

      this.ctx.save();
      this.ctx.translate(center.x + frontX, center.y + frontY);
      this.ctx.scale(scale, scale);
      this.ctx.translate(-12, -12);
      this.ctx.strokeStyle = color;
      this.ctx.lineWidth = SIDE_RING.markStroke;
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
      this.ctx.globalAlpha = SIDE_RING.opacity;
      this.ctx.shadowColor = color;
      this.ctx.shadowBlur = 6 / scale;
      this.ctx.stroke(new Path2D(path));
      this.ctx.restore();
    },

    /**
     * Fractional grid coordinates to an on-screen point, through the same morph the
     * cells go through. Returned as a closure because the aim traces hundreds of
     * points per frame and must not re-derive the grid for each one.
     */
    gridProjector() {
      const config = this.scaledGridConfig;
      if (!config) return null;

      const { cell_width, cell_height, columns, rows } = config;
      const imagePos = this.getImagePosition();
      const startX = imagePos.x;
      const startY = imagePos.y;
      const gridWidth = columns * cell_width;
      const gridHeight = rows * cell_height;

      return (gridX, gridY) => this.applyMorph(
        startX + gridX * cell_width,
        startY + gridY * cell_height,
        gridWidth,
        gridHeight,
        startX,
        startY
      );
    },

    /**
     * The same geometry, for the effects layer.
     *
     * Read fresh on every frame the layer draws rather than pushed on pan, zoom and
     * resize: those three are changed from a dozen places here and any one of them
     * forgetting to announce itself would leave the effects sitting on the ground
     * the board used to be on.
     */
    impactProjection() {
      if (!this.gridSettings) return null;
      const image = this.getImagePosition();
      return boardProjection({
        gridSettings: this.gridSettings,
        image: this.backgroundImage,
        origin: { x: image.x, y: image.y },
        zoom: this.zoom,
        viewport: { width: this.canvasWidth, height: this.canvasHeight },
      });
    },

    /**
     * Play one impact, clipped to what the party can see.
     *
     * The clip happens here and not in the renderer because the fog is painted into
     * the 2D canvas BELOW this layer: a cell the server withheld cannot be covered
     * up afterwards, so it must never reach the stage in the first place.
     */
    playImpactCast(cast, hooks = {}) {
      if (!this.impactLayer || !cast) return Promise.resolve(null);
      const cells = maskCellsToVision(cast.cells, this.visibleCellKeys);
      if (!cells.length) return Promise.resolve(null);
      const played = { ...cast, cells };
      const choreography = this.castChoreography?.(played);
      return this.impactLayer.playCast(played, choreography ? mergeHooks(choreography, hooks) : hooks);
    },

    /**
     * The half of a cast that is bodies rather than light.
     *
     * The effects layer says WHAT happened and how hard; nothing in it knows where a
     * token is drawn, what the board's morph does to the line between two cells, or
     * that there is a DOM at all. So it offers three moments — the attacker closing,
     * the blow landing, the cast leaving — and this turns them into displacement.
     *
     * Null under `prefers-reduced-motion`: the light, the trace and the arrival all
     * still play, and nothing on the board moves an inch.
     */
    castChoreography(cast) {
      if (this.prefersReducedMotion || !cast) return null;
      const hooks = {
        onImpact: struck => this.recoilFromCast(struck),
        onFinish: finished => this.endChoreography(finished),
      };
      if (cast.lunge > 0 && cast.caster?.id && cast.target) {
        hooks.onLunge = (running, amount) => this.setLungeAmount(running, amount);
      }
      return hooks;
    },

    /**
     * Where the attacker's body is along its blow, 0 at home and 1 on its target.
     *
     * Keyed by the body, tagged with the blow. One fighter can only be in one place,
     * so the key is the body — but a cast ending must only ever put down the body IT
     * moved: two blows by the same attacker overlapping (a queue drained out of
     * order, a stage stopped mid-flight) had the first one's exit snap the second
     * one's lunge home in the middle of its swing.
     */
    setLungeAmount(cast, amount) {
      const id = cast?.caster?.id;
      if (!id || !this.lungeRuns) return;
      if (amount > 0) {
        this.lungeRuns.set(id, {
          castId: cast.id,
          from: cast.caster,
          to: cast.target,
          fraction: cast.lunge,
          amount,
        });
      } else if (this.lungeRuns.get(id)?.castId === cast.id) {
        this.lungeRuns.delete(id);
      }
      this.applyChoreography();
    },

    /**
     * The blow landing on the figures standing in it.
     *
     * Direction comes from the line the blow travelled, not from a seed: everything
     * caught by one strike is thrown the same way, which is what makes a hit read as
     * having come FROM somewhere rather than as several people shivering at once.
     */
    recoilFromCast(cast) {
      if (this.prefersReducedMotion || !cast) return;
      const struck = new Set((cast.cells || []).map(cell => `${cell.col},${cell.row}`));
      const origin = cast.caster || cast.target;
      (this.visibleObjects || []).forEach(object => {
        const cell = this.tokenCellOf(object.id);
        if (!cell || !struck.has(`${cell.col},${cell.row}`)) return;
        impactShake.hit(object.id, cast.impactForce * RECOIL_GAIN, RECOIL_MS, blowDirection(origin, cell));
      });
      /* Only a cataclysm moves the world. See `groundShakeForce` for where the line is. */
      const ground = groundShakeForce(cast);
      /* Directed, like a recoil and unlike a rattle: the world is shoved along the
         line the blow travelled and heaves back, at half the frequency an undirected
         shudder rings at. A board that buzzed instead reads as a loose camera. */
      if (ground > 0) {
        const at = cast.target || cast.cells?.[0] || cast.caster;
        impactShake.hit(BOARD_SHAKE_KEY, ground, GROUND_SHAKE_MS, blowDirection(origin, at));
      }
      this.applyChoreography();
    },

    /** Whatever ended the cast, the attacker THIS cast moved is home. */
    endChoreography(cast) {
      const id = cast?.caster?.id;
      if (id && this.lungeRuns?.get(id)?.castId === cast.id) this.lungeRuns.delete(id);
      this.applyChoreography();
    },

    /**
     * One frame of choreography, written straight onto the elements.
     *
     * Deliberately not through `data`: a displacement recomputed reactively would
     * re-run `getTokenStyle` — and with it the four-corner morph — for every token on
     * every frame, and a body that has been shaken would land back on a position
     * Vue re-derived rather than on the one it left. Two custom properties composed
     * after the anchor transform mean the ONLY way home is both of them at zero,
     * which is what `endChoreography` and a rung-out shake both produce.
     */
    applyChoreography(now = performance.now()) {
      if (this.prefersReducedMotion) return false;
      const root = this.$el;
      if (!root || typeof root.querySelectorAll !== 'function') return false;
      const config = this.scaledGridConfig;
      const cellPx = config
        ? ((Number(config.cell_width) || 0) + (Number(config.cell_height) || 0)) / 2
        : 0;

      /* The canvases are not zoom-scaled and the token overlay is, so the ground
         shake is stated in screen pixels and the overlay's own scale is undone. */
      const board = cellPx ? impactShake.offset(BOARD_SHAKE_KEY, now) : null;
      const zoom = this.zoom || 1;
      const clamp = value => Math.max(-BOARD_SHAKE_MAX_PX, Math.min(BOARD_SHAKE_MAX_PX, value));
      root.style.setProperty('--board-shake-x', `${board ? clamp(board.x * cellPx * zoom) : 0}px`);
      root.style.setProperty('--board-shake-y', `${board ? clamp(board.y * cellPx * zoom) : 0}px`);

      let live = !!board;
      root.querySelectorAll('.token-wrapper[data-token-id]').forEach(element => {
        const id = element.dataset?.tokenId;
        const recoil = cellPx && id ? impactShake.offset(id, now) : null;
        const lunge = this.lungeShift(id);
        const x = (recoil ? recoil.x * cellPx : 0) + lunge.x;
        const y = (recoil ? (recoil.y - recoil.lift) * cellPx : 0) + lunge.y;
        if (!x && !y) {
          element.style.removeProperty('--token-shift-x');
          element.style.removeProperty('--token-shift-y');
          return;
        }
        live = true;
        element.style.setProperty('--token-shift-x', `${x}px`);
        element.style.setProperty('--token-shift-y', `${y}px`);
      });
      this.choreographyLive = live;
      /* The plinths are drawn into the board's own canvas, which repaints on demand
         — so a body on the move has to ask for the frame its plate is drawn in. */
      if (this.lungeRuns?.size) this.render();
      return live;
    },

    /**
     * How far one token has been carried by its own blow, in overlay pixels.
     *
     * Measured between the two cells' morphed centres rather than along the grid, so
     * an attacker on a heavily raked board closes along the ground it is standing on
     * and not along a direction the floor does not have.
     */
    lungeShift(tokenId) {
      const run = tokenId ? this.lungeRuns?.get(tokenId) : null;
      if (!run || !(run.amount > 0)) return { x: 0, y: 0 };
      const from = this.getMorphedCellGeometry(run.from.col, run.from.row);
      const to = this.getMorphedCellGeometry(run.to.col, run.to.row);
      if (!from || !to) return { x: 0, y: 0 };
      const dx = to.center.x - from.center.x;
      const dy = to.center.y - from.center.y;
      const travel = Math.hypot(dx, dy);
      if (!travel) return { x: 0, y: 0 };
      const reach = Math.min(travel, from.footprintWidth * LUNGE_MAX_CELLS) * run.fraction * run.amount;
      return { x: (dx / travel) * reach, y: (dy / travel) * reach };
    },

    /** Where a token stands right now, as the effects layer counts cells. */
    tokenCellOf(objectId) {
      if (!objectId) return null;
      const object = this.visibleObjects.find(obj => obj.id === objectId);
      if (!object) return null;
      const override = this.tokenPositionOverrides[object.id];
      return {
        col: override ? override.x : object.x,
        row: override ? override.y : object.y,
        id: object.id,
      };
    },

    /**
     * The round, drawn.
     *
     * Sequential on purpose: `playCast` resolves when its cast has finished leaving,
     * and two blasts at once on a small board read as noise rather than as two
     * things having happened. The ids are retired BEFORE the first frame, because a
     * second log refresh can land while the reel is still running and must diff
     * against a log that already counts as seen.
     */
    async playResolutionReel(log, { trusted = false } = {}) {
      if (!this.impactLayer) return;
      const { casts, actionIds } = buildReel(log, this.playedActionIds, {
        positionOf: id => this.tokenCellOf(id),
      });
      actionIds.forEach(id => this.playedActionIds.add(id));
      this.sweepPlayedActionIds();
      this.reconcilePendingActions(log, actionIds, trusted);
      if (!casts.length) return;

      await this.queueCasts(casts);
    },

    /** Bound `playedActionIds` — see `PLAYED_ACTION_IDS_MAX` for why the number is safe. */
    sweepPlayedActionIds() {
      const overflow = this.playedActionIds.size - PLAYED_ACTION_IDS_MAX;
      if (overflow <= 0) return;
      [...this.playedActionIds].slice(0, overflow).forEach(id => this.playedActionIds.delete(id));
    },

    /**
     * One scheduler for every cast the board draws — the player's own and the
     * round's reel alike, so a boundary landing mid-swing lines up behind it, not
     * over it. Casts on disjoint cells play together; casts sharing a cell still
     * resolve in the order the server gave them.
     *
     * The sticky preference short-circuits before any of that: board state comes
     * from `refreshForRound`, never from this queue, so skipping here only drops
     * decoration. The caller has already marked every action id seen.
     */
    queueCasts(casts) {
      if (this.skipReelsPreference) return Promise.resolve(null);

      this.reelBatchCount += 1;
      this.reelInFlight = true;
      const shouldSkip = () => this.reelSkipRequested;
      const run = playScheduled(casts, cast => this.playImpactCast(cast), this.reelRunning, shouldSkip);
      this.reelRunning = run;
      run.finally(() => {
        this.reelBatchCount = Math.max(0, this.reelBatchCount - 1);
        if (this.reelBatchCount === 0) {
          this.reelInFlight = false;
          this.reelSkipRequested = false;
        }
      });
      return run;
    },

    /** Force the reel in flight to its resolved state, dropping whatever has not started yet. */
    skipReel() {
      if (!this.reelInFlight) return;
      this.reelSkipRequested = true;
      this.impactLayer?.stopCasts();
    },

    getSkipReelsPreferenceKey() {
      return 'dx_tactical_skip_reels';
    },

    loadSkipReelsPreference() {
      try {
        this.skipReelsPreference = localStorage.getItem(this.getSkipReelsPreferenceKey()) === '1';
      } catch (error) {
        console.warn('[ReelSkip] Failed to read the skip-reels preference:', error);
      }
    },

    setSkipReelsPreference(enabled) {
      this.skipReelsPreference = !!enabled;
      try {
        localStorage.setItem(this.getSkipReelsPreferenceKey(), this.skipReelsPreference ? '1' : '0');
      } catch (error) {
        console.warn('[ReelSkip] Failed to persist the skip-reels preference:', error);
      }
    },

    toggleSkipReelsPreference() {
      this.setSkipReelsPreference(!this.skipReelsPreference);
    },

    /** Count a log as already drawn — for the first load, which arrives mid-round. */
    markReelSeen(log) {
      (Array.isArray(log) ? log : []).forEach(action => {
        if (action?.id) this.playedActionIds.add(action.id);
      });
      this.sweepPlayedActionIds();
    },

    /**
     * The held aim, bound to this board. `services/impact/pending` owns what a pending
     * marker is and when absence from the log may retire one; all that is added here is
     * the stage to draw into, where the tokens stand and what the party can see.
     */
    markActionPending(action, ability = null, { cycle = null } = {}) {
      return markPending({
        stage: this.impactLayer,
        pending: this.pendingActionEffects,
        action,
        ability,
        cycle,
        positionOf: id => this.tokenCellOf(id),
        maskCells: cells => maskCellsToVision(cells, this.visibleCellKeys),
      });
    },

    clearActionPending(actionId) {
      return clearPending({ stage: this.impactLayer, pending: this.pendingActionEffects, actionId });
    },

    clearAllPendingActions() {
      clearAllPending({ stage: this.impactLayer, pending: this.pendingActionEffects });
    },

    reconcilePendingActions(log, drawnIds = [], trusted = false) {
      reconcilePending({
        stage: this.impactLayer,
        pending: this.pendingActionEffects,
        log,
        drawnIds,
        trusted,
      });
    },

    /**
     * The aim field is not the component's. `services/impact/aimField` owns the rule
     * — gaussian body, warped contour flow, denied state and all three caches — and
     * this board and the duel bench both draw through it, so what is tuned on the
     * bench is what the fight shows. All that stays here is the board's own state:
     * the context to draw into and the projector that carries the map's morph.
     *
     * These delegates are kept because two call sites and the specs reach for them
     * by name; they add nothing of their own.
     */
    aimFlowFrames(color, valid) {
      return this.aimField.flowFrames(color, valid);
    },

    primeAimFlow(set) {
      this.aimField.prime(set);
    },

    drawAimFootprint(cells, options = {}) {
      this.aimField.draw(this.ctx, cells, { ...options, project: this.gridProjector() });
    },

    drawSkillPreview() {
      const config = this.scaledGridConfig;
      if (!config || !this.skillAffectedCells.length) return;

      const { cell_width, cell_height, columns, rows } = config;
      const imagePos = this.getImagePosition();
      const gridWidth = columns * cell_width;
      const gridHeight = rows * cell_height;
      const startX = imagePos.x;
      const startY = imagePos.y;

      const pivotCell = this.skillAffectedCells.find(cell => cell.isPivot) || this.skillAffectedCells[0];

      const ability = this.selectedAbility;
      const aimColor = this.aimColor(ability);
      // Blocked ground refuses the shot exactly as being out of range does, so the
      // field has to say the same thing about it.
      const inRange = this.skillAffectedCells.some(cell => cell.valid)
        && this.isCellPassable(this.skillTargetPosition.x, this.skillTargetPosition.y);
      /** Screen pixels, undone from the canvas zoom. See drawAimFootprint. */
      const px = (value) => value / (this.zoom || 1);

      if (this.aimOverlay !== false) {
        this.drawAimFootprint(this.skillAffectedCells, {
          palette: this.aimMaterial(ability, inRange),
          color: inRange ? aimColor : AIM_DENIED_COLOR,
          pivot: pivotCell,
          point: this.aimPoint,
          valid: inRange
        });
      }

      // Range boundary, for a reach too wide to shade.
      //
      // Where `drawCastReach` runs, the lit ground and its own hairline already say
      // where the shot may be taken, and a dashed square over the top of them is a
      // second outline disagreeing with the first. This is what is left for the case
      // the reach declines: an unbounded or board-wide range, where a boundary is
      // still orientation even though there is nothing to shadow.
      if (this.playerCharacter && this.skillTargetPosition && !this.castReachVisible) {
        const playerX = this.tacticalCharacterInfo?.x ?? this.playerCharacter.x ?? 0;
        const playerY = this.tacticalCharacterInfo?.y ?? this.playerCharacter.y ?? 0;

        let maxRange = ability?.max_range || Infinity;
        if (ability?.item && ability.item.skill) {
          maxRange = ability.item.skill.max_range || Infinity;
        }

        if (maxRange !== Infinity) {
          const rangeSet = new Set();
          for (let dx = -maxRange; dx <= maxRange; dx++) {
            for (let dy = -maxRange; dy <= maxRange; dy++) {
              if (Math.max(Math.abs(dx), Math.abs(dy)) <= maxRange) {
                rangeSet.add(`${playerX + dx},${playerY + dy}`);
              }
            }
          }

          this.ctx.strokeStyle = `rgba(${aimColor.r}, ${aimColor.g}, ${aimColor.b}, 0.18)`;
          this.ctx.lineWidth = px(1);
          this.ctx.setLineDash([px(4), px(6)]);
          this.ctx.shadowBlur = 0;
          this.ctx.beginPath();

          rangeSet.forEach(cellKey => {
            const [x, y] = cellKey.split(',').map(Number);
            const cellX = startX + x * cell_width;
            const cellY = startY + y * cell_height;

            const topLeft = this.applyMorph(cellX, cellY, gridWidth, gridHeight, startX, startY);
            const topRight = this.applyMorph(cellX + cell_width, cellY, gridWidth, gridHeight, startX, startY);
            const bottomLeft = this.applyMorph(cellX, cellY + cell_height, gridWidth, gridHeight, startX, startY);
            const bottomRight = this.applyMorph(cellX + cell_width, cellY + cell_height, gridWidth, gridHeight, startX, startY);

            if (!rangeSet.has(`${x},${y - 1}`)) {
              this.ctx.moveTo(topLeft.x, topLeft.y);
              this.ctx.lineTo(topRight.x, topRight.y);
            }
            if (!rangeSet.has(`${x + 1},${y}`)) {
              this.ctx.moveTo(topRight.x, topRight.y);
              this.ctx.lineTo(bottomRight.x, bottomRight.y);
            }
            if (!rangeSet.has(`${x},${y + 1}`)) {
              this.ctx.moveTo(bottomLeft.x, bottomLeft.y);
              this.ctx.lineTo(bottomRight.x, bottomRight.y);
            }
            if (!rangeSet.has(`${x - 1},${y}`)) {
              this.ctx.moveTo(topLeft.x, topLeft.y);
              this.ctx.lineTo(bottomLeft.x, bottomLeft.y);
            }
          });

          this.ctx.stroke();
          this.ctx.setLineDash([]);
        }
      }

      /*
       * THE FIRING LINE IS NOT DRAWN ANY MORE.
       *
       * It used to be three stroked polylines with a shadowBlur under each and five
       * circles sliding along them, and it had two problems that no amount of tuning
       * reached. It carried no identity — every skill in the game fired the same neon
       * tube, tinted — and being a 2D stroke it lay ON the board rather than in it, so
       * on a raked map it read as a line over a photograph.
       *
       * What crosses the gap now is the element itself: `ImpactEffect.addTrail` puts
       * particles in the stage's own 3D space, leaving the caster on a ballistic arc
       * and deviating the way the violation deviates, so the flight takes the board's
       * rake and corner morph for free at any zoom and any bearing.
       *
       * The stroke survives for exactly one case. A refused aim builds no stage effect
       * at all — there are no cells for it to be built from — so there would be
       * nothing at all joining the caster to a cell they cannot reach. That one is
       * still a line, and deliberately a plain one: dashed, unlit, no glow, no motes.
       * A refusal is not an effect and should not be dressed as one.
       */
      drawRefusedLine: if (this.playerCharacter && this.skillTargetPosition) {
        const distance = this.skillTargetDistance;
        let minRange = ability?.min_range || 0;
        let maxRange = ability?.max_range || Infinity;
        if (ability?.item && ability.item.skill) {
          minRange = ability.item.skill.min_range || 0;
          maxRange = ability.item.skill.max_range || Infinity;
        }
        if (distance >= minRange && distance <= maxRange) break drawRefusedLine;

        const playerX = this.tacticalCharacterInfo?.x ?? this.playerCharacter.x ?? 0;
        const playerY = this.tacticalCharacterInfo?.y ?? this.playerCharacter.y ?? 0;
        const casterGeometry = this.getMorphedCellGeometry(playerX, playerY);
        const project = this.gridProjector();
        if (!casterGeometry || !project) break drawRefusedLine;

        const focus = this.aimPoint || {
          x: this.skillTargetPosition.x + 0.5,
          y: this.skillTargetPosition.y + 0.5
        };
        const toPoint = project(focus.x, focus.y);
        const denied = AIM_DENIED_COLOR;

        this.ctx.save();
        this.ctx.lineCap = 'round';
        this.ctx.setLineDash([px(5), px(7)]);
        this.ctx.lineWidth = px(1.1);
        this.ctx.shadowBlur = 0;
        this.ctx.strokeStyle = `rgba(${denied.r}, ${denied.g}, ${denied.b}, 0.38)`;
        this.ctx.beginPath();
        this.ctx.moveTo(casterGeometry.center.x, casterGeometry.center.y);
        this.ctx.lineTo(toPoint.x, toPoint.y);
        this.ctx.stroke();
        this.ctx.setLineDash([]);
        this.ctx.restore();
      }
    },

    drawPlacedCharges() {
      if (!this.scaledGridConfig || this.chargeTargets.length === 0) return;

      const ability = this.selectedAbility;
      if (!ability) return;

      // Committed charges are the same field, held steady: no drift on the scan, a
      // touch more emission. Order is marked only when there is an order to read -
      // as a bare numeral in the field's own colour, since a badge disc would put
      // back the HUD the field exists to avoid.
      const color = this.aimColor(ability);
      const numbered = this.chargeTargets.length > 1;

      const material = this.aimMaterial(ability, true);
      this.chargeTargets.forEach((charge, chargeIndex) => {
        this.drawAimFootprint(charge.affectedCells, {
          palette: material,
          color,
          pivot: { x: charge.x, y: charge.y },
          valid: true,
          locked: true
        });

        if (!numbered) return;

        const geometry = this.getMorphedCellGeometry(charge.x, charge.y);
        if (!geometry) return;

        this.ctx.save();
        this.ctx.font = `600 ${11 / (this.zoom || 1)}px Orbitron, sans-serif`;
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.shadowBlur = 6 / (this.zoom || 1);
        this.ctx.shadowColor = `rgba(${color.r}, ${color.g}, ${color.b}, 0.9)`;
        this.ctx.fillStyle = `rgba(${color.r}, ${color.g}, ${color.b}, 0.9)`;
        this.ctx.fillText(
          String(chargeIndex + 1),
          geometry.center.x,
          geometry.center.y - geometry.footprintDepth * 0.42
        );
        this.ctx.restore();
      });

      this.ctx.shadowBlur = 0;
    },

    /**
     * Which ground can be stood on.
     *
     * `cells` is a list of EXCEPTIONS, not an inventory of the floor. A map that
     * only marks its four spawn strips ships twelve entries for a hundred cells,
     * and the rest is ordinary passable grass — that is what the server answers
     * (`_read_cell_passable`, PRIORITY 3) and what the payload documents. Reading
     * the list as an allowlist walled the player into whatever the editor happened
     * to have annotated, so the board drew moves the server would have accepted
     * and the client refused to send.
     *
     * So the cache is the grid minus the cells explicitly marked impassable.
     * Blocked edges are walls BETWEEN cells and do not close either side; the
     * pathfinder already refuses to cross them in `getEdgesFromCell`.
     */
    buildPassableCellsCache() {
      this.passableCellsCache.clear();

      const cells = this.tacticalMapData?.cells || [];
      const columns = this.gridSettings?.columns;
      const rows = this.gridSettings?.rows;

      if (!columns || !rows) {
        // No grid to enumerate. Fall back to whatever the map does name, so a
        // payload without grid_settings still moves rather than freezing solid.
        cells.forEach(cell => {
          if (cell.passable !== false && cell.x != null && cell.y != null) {
            this.passableCellsCache.add(`${cell.x},${cell.y}`);
          }
        });
        console.warn(
          `[Cache] No grid dimensions - fell back to the ${this.passableCellsCache.size} cells named by the map`
        );
        return;
      }

      const blocked = new Set();
      cells.forEach(cell => {
        if (cell.passable === false && cell.x != null && cell.y != null) {
          blocked.add(`${cell.x},${cell.y}`);
        }
      });

      for (let x = 0; x < columns; x++) {
        for (let y = 0; y < rows; y++) {
          const key = `${x},${y}`;
          if (!blocked.has(key)) this.passableCellsCache.add(key);
        }
      }

      console.log(
        `[Cache] Passable cells: ${this.passableCellsCache.size} of ${columns * rows} (${blocked.size} blocked by the map)`
      );
    },

    isCellPassable(x, y) {
      // Use cached Set for O(1) lookup instead of O(n) array iteration
      return this.passableCellsCache.has(`${x},${y}`);
    },

    /**
     * Is this figure down — knocked out, rolling death saves, or comatose?
     *
     * The fight clock is asked first. `standing` is the fight's own word for it and
     * it arrives with every phase tick, whereas `obj.alive` is stitched onto the
     * board payload from a separate roster call at load and patched over the socket:
     * a body that went down while the map was showing anything else keeps the flag
     * it was born with. `FightStatusRail` reads the same field the same way, which is
     * what keeps the rail's dot and the token on the board from disagreeing.
     *
     * `alive` remains the fallback for a knockout outside a fight, where there is no
     * clock to ask.
     */
    isObjectKnockedOut(obj) {
      const participant = (this.fightState?.participants || [])
        .find(p => p.character_id === obj.id);
      if (participant?.standing !== undefined) {
        return participant.standing === false;
      }
      if (participant?.alive !== undefined) {
        return participant.alive === false;
      }

      if (obj.alive !== undefined) {
        return obj.alive === false;
      }

      return false;
    },

    /**
     * The portrait a figure projects — its own, or a skull once it is down.
     *
     * The whole knockout treatment is this one substitution. The token keeps every
     * other property it had: the relation tint, the dimming, the side ring, the beam,
     * the pedestal. A downed body is not a different kind of thing on the board, it is
     * the same projection showing a different picture, and the picture is shot to the
     * published-avatar contract precisely so it can stand where a character's own
     * portrait stood without the token noticing.
     */
    tokenArt(obj) {
      return this.isObjectKnockedOut(obj) ? deathAvatar : obj.avatar;
    },

    /**
     * Screen-space geometry of one grid cell after the 4-point morph.
     *
     * The morph shears cells heavily (footprint width varies by ~35% across the grid and
     * the vertical step can invert), so anything drawn per-cell must be measured locally
     * rather than derived from the unmorphed cell_width/cell_height.
     */
    getMorphedCellGeometry(gridX, gridY, span = 1) {
      const config = this.scaledGridConfig;
      if (!config) return null;

      const { cell_width, cell_height, columns, rows } = config;
      const imagePos = this.getImagePosition();
      const gridWidth = columns * cell_width;
      const gridHeight = rows * cell_height;
      const startX = imagePos.x;
      const startY = imagePos.y;

      // A big body is measured over the whole block it holds, not over its anchor
      // cell, so its centre, its ground line and its width all belong to the block.
      const side = Math.max(1, span);
      const blockWidth = cell_width * side;
      const blockHeight = cell_height * side;

      const cellX = startX + gridX * cell_width;
      const cellY = startY + gridY * cell_height;

      const morph = (x, y) => this.applyMorph(x, y, gridWidth, gridHeight, startX, startY);
      // Kept so callers can sample arbitrary points inside this block through the same morph.
      const morphLocal = (u, v) => morph(cellX + u * blockWidth, cellY + v * blockHeight);
      const topLeft = morph(cellX, cellY);
      const topRight = morph(cellX + blockWidth, cellY);
      const bottomLeft = morph(cellX, cellY + blockHeight);
      const bottomRight = morph(cellX + blockWidth, cellY + blockHeight);

      const center = {
        x: (topLeft.x + topRight.x + bottomLeft.x + bottomRight.x) / 4,
        y: (topLeft.y + topRight.y + bottomLeft.y + bottomRight.y) / 4
      };

      // Ground line = the cell's front (bottom) edge; tokens stand on its midpoint.
      const ground = {
        x: (bottomLeft.x + bottomRight.x) / 2,
        y: (bottomLeft.y + bottomRight.y) / 2
      };

      const edgeLength = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);
      const footprintWidth = (edgeLength(topLeft, topRight) + edgeLength(bottomLeft, bottomRight)) / 2;
      const footprintDepth = (edgeLength(topLeft, bottomLeft) + edgeLength(topRight, bottomRight)) / 2;

      return {
        topLeft, topRight, bottomLeft, bottomRight,
        center, ground, footprintWidth, footprintDepth, morphLocal
      };
    },


    getTokenStyle(obj) {
      const config = this.scaledGridConfig;
      if (!config) {
        console.warn('[PlayerTacticalMap] No scaled grid config for token:', obj.name);
        return { display: 'none' };
      }

      // Check for animation override
      const override = this.tokenPositionOverrides[obj.id];
      const gridX = override ? override.x : obj.x;
      const gridY = override ? override.y : obj.y;

      const span = spanOf(obj);
      const geometry = this.getMorphedCellGeometry(gridX, gridY, span);
      if (!geometry) return { display: 'none' };

      // Size follows the real on-screen footprint of the block the body holds, so a
      // token shrinks and grows with the perspective instead of staying one fixed
      // pixel size everywhere - and a boss standing on 3x3 is drawn three cells wide,
      // centred on the middle of its own bulk rather than on its north-west corner.
      const isCharacter = obj.type === 'character';
      const width = isCharacter
        ? holoWidthForBase(geometry.footprintWidth * HOLO.baseFit)
        : geometry.footprintWidth * 0.55;

      const style = {
        position: 'absolute',
        left: `${geometry.center.x}px`,
        top: `${geometry.center.y}px`,
        width: `${width}px`,
        // Tokens further down the map are nearer the viewer and must overlap those
        // above. A big body is ranked by its front edge, which is where it stands.
        zIndex: isCharacter ? Math.max(1, Math.round(gridY) + span) : 0,
        // Figures take no input at all. A hologram's box is far larger than the cell
        // it stands in - it is drawn rising well above its own pedestal - so while it
        // was clickable there were two answers to "which cell is the pointer over":
        // the cell under the pointer, and the cell of whatever sprite it happened to
        // be inside. Grazing a figure's edge alternated between them and took the
        // whole aim with it. The floor is now the only thing that answers, and who
        // is standing on it is resolved from the cell.
        pointerEvents: 'none'
      };

      if (isCharacter) {
        // HoloToken sets its own height from --ht-size. Anchor the pedestal PLATE - not
        // the token box - on the cell centre, so the figure rises out of the cell it
        // actually occupies. The offset is derived from the component's stage geometry
        // rather than eyeballed, so it survives changes to the pedestal art.
        /*
         * The box the token OCCUPIES, stated rather than inferred.
         *
         * The figure inside is rendered at the zoom's real pixel count and scaled
         * back down (see holoSize), and a transform changes nothing about layout —
         * left to size itself around its content the wrapper would take the full
         * unscaled height, and `--token-anchor-y`, a percentage of exactly that
         * height, would put the token a multiple of the zoom away from its cell.
         * Height is the token's own aspect ratio; it is what `auto` used to arrive at.
         */
        style.height = `${width * HOLO_GEOMETRY.tokenAr}px`;
        /*
         * Which point of the token box lands on the cell.
         *
         * With the sprite plate it is the plate's box centre, because the sprite IS
         * the thing standing in the cell. With the 3D prop it is the point the plate
         * touches the floor, because the prop stands on the ground plane at the
         * centre of the block and the figure has to meet it there — anchored by the
         * box centre instead, the figure floats a plate's-worth above its own prop.
         */
        style['--token-anchor-y'] = `${
          -(this.pedestalsAreReal ? HOLO_CONTACT_CENTER_Y : HOLO_BASE_CENTER_Y) * 100
        }%`;
        if (this.aimedCharacterIds.has(obj.id)) {
          style['--token-aim-color'] = this.tokenProjectionColor(obj);
        }
      } else {
        style.height = `${width}px`;
        style['--token-anchor-y'] = '-92%';
      }

      return style;
    },

    /**
     * Token box width for a character, in REAL SCREEN PIXELS.
     *
     * The overlay is CSS-scaled by the zoom, so the obvious thing is to size the token
     * in overlay units and let the transform enlarge it. That is what this did, and it
     * was throwing most of the figure away: the token is composited — four mask layers
     * intersected, then blended — at its layout size, and the transform then stretches
     * that finished raster. At 300% a 38px composite was being blown up to 114px, so
     * every pedestal in the scene was sharp (the props are drawn by WebGL at device
     * resolution, outside the transform) and every character was a smear. The figures
     * did not look cheap because they were badly designed; they looked cheap because
     * two thirds of them had never been drawn.
     *
     * So the zoom is folded into the size here, and `holoCounterScale` divides it back
     * out. Layout px then equals screen px, which also repairs everything downstream
     * that reasons in pixels — the scanline ruling, the quality tiers, the board/card
     * budget — all of which were being asked about one number and answered about
     * another.
     */
    holoSize(obj) {
      const geometry = this.objectCellGeometry(obj);
      if (!geometry) return 0;
      return holoWidthForBase(geometry.footprintWidth * HOLO.baseFit) * (this.zoom || 1);
    },

    /**
     * Undo the zoom the token was rendered at, so it lands back in the overlay's units.
     *
     * About the centre because the wrapper centres its child: any other origin would
     * slide the figure off its own pedestal as the board zoomed.
     */
    holoCounterScale() {
      const zoom = this.zoom || 1;
      return { transform: `scale(${1 / zoom})`, transformOrigin: 'center center' };
    },

    /**
     * On-screen angle of the cell's own x axis. The grid is raked, and the rake
     * varies across it - from about -14 degrees at the far edge to -29 at the near
     * one - so the pedestal has to be told which way the floor runs beneath it.
     */
    holoGroundTilt(obj) {
      const geometry = this.objectCellGeometry(obj);
      if (!geometry) return 0;

      const angle = Math.atan2(
        geometry.topRight.y - geometry.topLeft.y,
        geometry.topRight.x - geometry.topLeft.x
      );
      return (angle * 180) / Math.PI * HOLO.groundTiltStrength;
    },



    /**
     * Where this figure's projection is struck from, as a fraction of the token's
     * height up from its bottom edge.
     *
     * With the plate drawn as a prop, the emitter is the prop's own lens - the top of
     * the head standing on the plate, not the plate itself. The plate is half way up
     * the object, so a light struck from there comes out of the housing's waist while
     * the lens that is visibly producing it sits above, unlit. The token is anchored
     * so the plate's contact point lands on the cell, so the lens is that point plus
     * the emitter's height - converted here from cells into the token box's own
     * units, which is what HoloToken lays out in.
     */
    holoEmitY(obj) {
      if (!this.pedestalsAreReal) return undefined;

      const geometry = this.objectCellGeometry(obj);
      const emitterTop = this.pedestalStage?.emitterTop;
      if (!geometry || !emitterTop) return undefined;

      const span = spanOf(obj);
      const cellPx = geometry.footprintWidth / span;
      const boxHeight =
        holoWidthForBase(geometry.footprintWidth * HOLO.baseFit) * HOLO_GEOMETRY.tokenAr;
      if (!(boxHeight > 0)) return undefined;

      return (1 - HOLO_CONTACT_CENTER_Y) + (emitterTop * cellPx) / boxHeight;
    },

    /**
     * Selection outranks hover, and both outrank "this is you" - a transient state
     * the player just caused should never be shouted down by a permanent one.
     */
    holoMarker(obj) {
      if (this.aimedCharacterIds.has(obj.id)) return 'targeted';
      if (this.selectedObjectId === obj.id) return 'selected';
      if (this.hoveredObjectId === obj.id) return 'hover';
      if (obj.relation === 'self') return 'own';
      return 'none';
    },

    /**
     * The fight side a board object belongs to, as its fixed 1-5 slot, or null for
     * an object with none — a bystander, an item, or a pending joiner who has not
     * chosen yet.
     *
     * The tactical map payload carries no side field of its own; the join is by
     * character id against `fightState.participants`, which is exactly what the
     * fight clock already tracks. This is deliberately NOT `relationColor` and
     * must never feed it: side is allegiance, relation is hostility, and a token
     * that borrowed its side's colour to answer "friend or foe" would be lying
     * about the one thing that actually keeps a player alive.
     */
    characterSideSlot(obj) {
      if (!obj || obj.type !== 'character' || !this.fightState) return null;
      const participant = (this.fightState.participants || [])
        .find(p => p.character_id === obj.id);
      if (!participant || !participant.side_id) return null;
      const side = (this.fightState.sides || [])
        .find(s => s.id === participant.side_id);
      return side ? side.slot : null;
    },

    /** The side's mark for the sprite render path — the same emblem the rail and
     *  the establishment screen draw. Null wherever `sideRingColor` is null:
     *  the mark rides the ring. */
    sideEmblem(obj) {
      const slot = this.characterSideSlot(obj);
      return slot == null ? null : sideEmblemPath(slot);
    },
    /** The side ring's colour for the sprite render path — see `HoloToken.vue`'s
     *  `sideColor` prop. Null draws no ring at all, sideless or not in a fight. */
    sideRingColor(obj) {
      const slot = this.characterSideSlot(obj);
      return slot == null ? null : sideColorVar(slot);
    },

    /**
     * A figure under the aim is re-lit in the ability's own colour. Recolouring the
     * projection is the hologram's idiom for "this concerns you" - a border drawn
     * around the token would be a UI badge pasted over the scene.
     */
    tokenProjectionColor(obj) {
      if (this.aimedCharacterIds.has(obj.id)) {
        const { r, g, b } = this.aimColor(this.selectedAbility);
        return `rgb(${r}, ${g}, ${b})`;
      }
      return this.relationCssColor(obj.relation);
    },

    objectCellGeometry(obj) {
      const override = this.tokenPositionOverrides[obj.id];
      return this.getMorphedCellGeometry(
        override ? override.x : obj.x,
        override ? override.y : obj.y,
        spanOf(obj)
      );
    },

    relationCssColor(relation) {
      const { r, g, b } = this.relationColor(relation);
      return `rgb(${r}, ${g}, ${b})`;
    },

    /*
     * A knockout used to end the fight, so a downed figure was a figure that had
     * left: `offline` hid it entirely, then `dimmed` half-hid it. Both were standing
     * in for a mark the board did not have — the only way to say "down" was to take
     * light away, and at the far end of the zoom that took the body away with it.
     *
     * The projection carries the skull now, which says it outright. So a body is lit
     * like anything else on the board: the winner has to be able to find it to rob it,
     * and a fight is not over because one side is on the floor.
     */
    holoState() {
      return 'normal';
    },

    /** Stable per-token jitter so a row of holograms does not breathe in lockstep. */
    objectSeed(obj) {
      let hash = 0;
      const id = String(obj.id);
      for (let i = 0; i < id.length; i++) {
        hash = (hash * 31 + id.charCodeAt(i)) | 0;
      }
      return Math.abs(hash % 997);
    },

    objectRelationClass(obj) {
      if (obj.type !== 'character') return null;
      return `token-relation-${obj.relation || 'neutral'}`;
    },

    handleMouseDown(event) {
      const rect = this.canvas.getBoundingClientRect();

      // Priority 1: Start rotation drag if action selected (left click)
      if ((this.selectedSkill || this.selectedItem || this.selectedSpecial) && event.button === 0) {
        this.isDraggingRotation = true;
        this.rotationDragCenter = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2
        };
        const dx = event.clientX - this.rotationDragCenter.x;
        const dy = event.clientY - this.rotationDragCenter.y;
        this.rotationDragStartAngle = Math.atan2(dy, dx) * 180 / Math.PI;
        this.rotationDragStartRotation = this.skillRotation;
        return;
      }

      // Priority 2: Middle mouse button (wheel press) - enter pan mode
      if (event.button === 1) {
        event.preventDefault();
        this.startPanning(event);
      }
    },

    startPanning(event) {
      this.isPanning = true;
      this.panStartX = event.clientX;
      this.panStartY = event.clientY;
      this.panStartOffsetX = this.offsetX;
      this.panStartOffsetY = this.offsetY;
      this.canvas.style.cursor = 'grabbing';

      // Track on window so the drag survives the cursor crossing tokens or HUD panels
      // and still ends when the button is released outside the canvas.
      window.addEventListener('mousemove', this.handlePanMove);
      window.addEventListener('mouseup', this.handlePanEnd);
    },

    handlePanMove(event) {
      if (!this.isPanning) return;

      // offset is in unscaled world units and the canvas renders it through scale(zoom),
      // so the raw cursor delta has to be divided by zoom for the map to track the cursor 1:1.
      this.offsetX = this.panStartOffsetX + (event.clientX - this.panStartX) / this.zoom;
      this.offsetY = this.panStartOffsetY + (event.clientY - this.panStartY) / this.zoom;

      this.saveCameraState();
      this.render();
    },

    handlePanEnd(event) {
      if (!this.isPanning || (event && event.button !== 1)) return;
      this.stopPanning();
    },

    stopPanning() {
      if (!this.isPanning) return;
      this.isPanning = false;
      if (this.canvas) this.canvas.style.cursor = 'default';
      window.removeEventListener('mousemove', this.handlePanMove);
      window.removeEventListener('mouseup', this.handlePanEnd);
    },

    handleMouseMove(event) {
      // Priority 1: Handle rotation drag
      if (this.isDraggingRotation) {
        const dx = event.clientX - this.rotationDragCenter.x;
        const dy = event.clientY - this.rotationDragCenter.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Ignore small movements (30px threshold)
        if (distance < 30) return;

        const currentAngle = Math.atan2(dy, dx) * 180 / Math.PI;
        let deltaAngle = currentAngle - this.rotationDragStartAngle;

        // Normalize angle
        if (deltaAngle > 180) deltaAngle -= 360;
        if (deltaAngle < -180) deltaAngle += 360;

        // Snap to 90° increments
        const targetRotation = this.rotationDragStartRotation + deltaAngle;
        const snappedRotation = Math.round(targetRotation / 90) * 90;
        const normalizedRotation = ((snappedRotation % 360) + 360) % 360;

        if (normalizedRotation !== this.skillRotation) {
          this.skillRotation = normalizedRotation;
          if (this.skillTargetPosition) {
            this.calculateSkillAffectedCells(this.skillTargetPosition.x, this.skillTargetPosition.y);
          }
          this.render();
        }
        return;
      }

      // Priority 2: Panning is driven by window listeners (see startPanning)
      if (this.isPanning) return;

      // Priority 3: Update hovered cell
      const prevHoveredCell = this.hoveredCell;
      this.updateHoveredCell(event);

      // Only update path if hovered cell actually changed
      const currentKey = this.hoveredCell ? `${this.hoveredCell.x},${this.hoveredCell.y}` : null;

      const cellChanged = currentKey !== this.lastHoveredKey;
      this.lastHoveredKey = currentKey;

      if (this.selectedAbility) {
        if (!this.hoveredCell) {
          this.clearAim();
          this.render();
          return;
        }

        // The aim follows the pointer inside a cell, not just across cells: the cells
        // it resolves against only change on a boundary, but the light is centred on
        // the exact position and has to be redrawn for every move. Recomputing the
        // affected cells is the expensive half, so that still waits for a boundary -
        // but only once there is an aim to keep. Arming clears the target while
        // leaving the last hovered key behind, so taking the cheap path on the
        // strength of the key alone left the field undrawn until the pointer
        // happened to cross into another cell.
        if (cellChanged || !this.skillTargetPosition) {
          this.aimAtCell(this.hoveredCell.x, this.hoveredCell.y, this.aimPoint);
        } else {
          this.render();
        }
        return;
      }

      if (!cellChanged) return;

      // Calculate path to hovered cell if it's reachable (PREPARATION phase)
      if (this.playerCharacter) {
        this.refreshHoveredPath();
      } else {
        this.pathToHovered = null;
      }
      this.updateCellHover();
      this.render();
    },

    handleMouseUp(event) {
      // Priority 1: End rotation drag
      if (this.isDraggingRotation && event.button === 0) {
        this.isDraggingRotation = false;
        return;
      }

      // Priority 2: Exit pan mode if middle button was released
      if (this.isPanning && event.button === 1) {
        this.stopPanning();
      }
    },

    handleMouseLeave() {
      // Panning continues past the canvas edge - only window mouseup ends it.
      if (this.isPanning) return;

      this.hoveredCell = null;
      this.aimPoint = null;
      this.pathToHovered = null;
      this.lastHoveredKey = null;
      this.hoveredObjectId = null;
      this.$emit('cell-hovered', []); // Clear character highlighting
      this.render();
    },

    handleWheel(event) {
      // If skill/item selected, rotate instead of zoom
      if (this.selectedSkill || this.selectedItem || this.selectedSpecial) {
        const direction = event.deltaY < 0 ? 90 : -90;
        this.rotateSkillPattern(direction);
        return;
      }

      // Otherwise zoom (existing behavior)
      const delta = event.deltaY;
      if (delta < 0) {
        this.zoomIn();
      } else {
        this.zoomOut();
      }
    },

    zoomIn() {
      this.setZoom(this.zoom * this.zoomStep);
    },

    zoomOut() {
      this.setZoom(this.zoom / this.zoomStep);
    },

    resetZoom() {
      this.setZoom(1.0);
    },

    setZoom(newZoom) {
      // Clamp zoom to min/max range
      this.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, newZoom));

      // Save camera state when zooming
      this.saveCameraState();

      this.render();
    },

    /**
     * Where the pointer actually is on the floor, in fractional grid coordinates.
     *
     * The cell it falls in drives the mechanics, but the light must not be quantised
     * to it: snapping the impact to a cell centre makes the aim jump between tiles
     * and gives away that a grid is being consulted. The exact point is what the
     * field is drawn around.
     */
    pointerGridPoint(event) {
      const config = this.scaledGridConfig;
      if (!config || !this.canvas) return null;

      const rect = this.canvas.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;

      const centerX = this.canvasWidth / 2;
      const centerY = this.canvasHeight / 2;
      const zoomedMouseX = centerX + (mouseX - centerX) / this.zoom;
      const zoomedMouseY = centerY + (mouseY - centerY) / this.zoom;

      const imagePos = this.getImagePosition();
      const { cell_width, cell_height, columns, rows } = config;
      const startX = imagePos.x;
      const startY = imagePos.y;

      const unmorphed = this.inverseMorph(
        zoomedMouseX, zoomedMouseY,
        columns * cell_width, rows * cell_height,
        startX, startY
      );

      return {
        x: (unmorphed.x - startX) / cell_width,
        y: (unmorphed.y - startY) / cell_height
      };
    },

    updateHoveredCell(event) {
      const config = this.scaledGridConfig;
      if (!config) return;

      const point = this.pointerGridPoint(event);
      if (!point) return;

      const { columns, rows } = config;
      let gridX = Math.floor(point.x);
      let gridY = Math.floor(point.y);

      // The cell the pointer resolves against holds on slightly past its own edge.
      // Sitting exactly on a boundary - and worse on a corner, where four cells meet
      // - the pointer would otherwise cross back and forth under a fraction of a
      // pixel of movement, and the whole shape of the aim flipped with it, several
      // times a second. The band is far below what anyone can aim within, so it
      // changes nothing about which cell you can pick.
      const held = this.hoveredCell;
      if (held
        && point.x > held.x - AIM_CELL_HYSTERESIS && point.x < held.x + 1 + AIM_CELL_HYSTERESIS
        && point.y > held.y - AIM_CELL_HYSTERESIS && point.y < held.y + 1 + AIM_CELL_HYSTERESIS
      ) {
        gridX = held.x;
        gridY = held.y;
      }

      // Any cell on the map counts, passable or not. Refusing to resolve a blocked
      // cell meant the aim had nothing to sit on and was cleared outright, so
      // sweeping over rubble - which is most of this map - switched the entire field
      // off and on again. Blocked ground is refused where it matters instead: the
      // aim reads as denied over it, and neither a charge nor a move will take it.
      if (gridX >= 0 && gridX < columns && gridY >= 0 && gridY < rows) {
        this.hoveredCell = { x: gridX, y: gridY };
        this.aimPoint = point;
      } else {
        this.hoveredCell = null;
        this.aimPoint = null;
      }
    },

    handleMapClick(event) {
      // Handle map click based on current phase and selection
      if (this.isPanning) return;

      const abilityArmed = !!(this.selectedSkill || this.selectedItem || this.selectedSpecial);

      if (!this.hoveredCell) {
        if (abilityArmed) {
          this.showActionHint('That cell is outside the map or blocked.', 'warn');
        }
        return;
      }

      // If skill or item is selected (ACTION phase), place charge
      if (abilityArmed) {
        this.attemptCharge(this.hoveredCell.x, this.hoveredCell.y);
        return;
      }

      // The move is committed by its anchor, but every rule about it is asked of the
      // whole block. Checking the pointed-at cell alone let a big body be sent at a
      // gap it could never fit, and the server's refusal was swallowed - the click
      // simply did nothing.
      const span = this.moverSpan;
      const anchor = this.moveAnchorFor(this.hoveredCell.x, this.hoveredCell.y);
      const standing = this.objectsAtCell(this.hoveredCell.x, this.hoveredCell.y);

      /*
       * A figure standing on the clicked cell is what the click is for - unless the
       * figure is you and the cell is somewhere you could step to.
       *
       * A big body overlaps nearly all the ground within one move of it: a 2x2 at
       * (10, 2) can step to (9, 2) or (11, 2), and both of those anchors are under
       * its own bulk. Reading "you are standing here" as a selection therefore ate
       * every short move a medium or superior creature could make - the click did
       * nothing at all, and nothing said why. Selecting yourself is what is given up,
       * and you are the one figure on the board already named on the HUD.
       *
       * The sprites take no input themselves, so who is on a cell is resolved from
       * the cell rather than from whichever oversized token box the pointer was in.
       */
      const stepping =
        this.canMove
        && (anchor.x !== this.tacticalCharacterInfo?.x || anchor.y !== this.tacticalCharacterInfo?.y);
      const character = standing.find(
        obj => obj.type === 'character' && !(stepping && obj.relation === 'self')
      );
      if (character) {
        this.handleTokenClick(character);
        return;
      }

      /*
       * A chest or a piece of loot on the clicked cell is what the click is for.
       *
       * Checked after characters and before movement: a figure standing on a
       * chest is still the more interesting target, but a chest must not be
       * something you walk over without ever being offered it. The reach test
       * lives on the server; refusing here as well is only so the player is told
       * why before an action is spent.
       */
      const interactable = standing.find(
        obj => obj.type === 'chest' || obj.type === 'worlditem'
      );
      if (interactable) {
        this.handleInteractableClick(interactable);
        return;
      }

      // A signed character arrives rather than walks: no phase, no route, no
      // blocked ground and no distance. Every gate below states a limit the
      // engine will not enforce for it, so refusing here would refuse a move
      // the server accepts.
      if (this.isSignedMover) {
        this.$emit('cell-clicked', anchor);
        return;
      }

      // PREPARATION phase - emit movement click only if tactical movement not disabled
      if (this.isPreparationPhase) {
        if (this.tacticalMovementDisabled) {
          this.showActionHint('You already ended tactical movement this turn.', 'warn');
          return;
        }

        const occupied = occupiedKeys(this.visibleObjects, { id: this.playerCharacter?.id });
        const fits = blockFits(
          anchor.x,
          anchor.y,
          span,
          (x, y) => this.isCellPassable(x, y) && !occupied.has(`${x},${y}`)
        );
        if (!fits) {
          this.showActionHint(
            span > 1 ? 'Your body does not fit there.' : 'That cell is blocked.',
            'warn'
          );
          return;
        }
        if (!this.reachableCells.has(`${anchor.x},${anchor.y}`)) {
          this.showActionHint('That is further than you can move this turn.', 'warn');
          return;
        }
        this.$emit('cell-clicked', anchor);
        return;
      }

      this.showActionHint('Movement is only available during the preparation phase.', 'info');
    },

    attemptCharge(cellX, cellY) {
      if (!this.isCellPassable(cellX, cellY)) {
        this.showActionHint('That cell is blocked.', 'warn');
        return;
      }
      this.placeCharge(cellX, cellY);
    },

    showActionHint(text, tone = 'info') {
      if (this.actionHintTimer) clearTimeout(this.actionHintTimer);
      this.actionHint = { text, tone };
      this.actionHintTimer = setTimeout(() => {
        this.actionHint = null;
        this.actionHintTimer = null;
      }, 2600);
    },

    /**
     * The anchor a move onto this cell would end on.
     *
     * A body is anchored at its north-west cell but drawn centred on its block, so
     * pointing at a cell has to mean "stand here", not "put your corner here" - a
     * superior creature aimed by its corner lands a full cell down and to the right
     * of where the player pointed. Odd spans have a centre cell and use it; an even
     * span has none, so the pointer stays in its north-west quadrant.
     */
    moveAnchorFor(cellX, cellY) {
      const back = Math.floor((this.moverSpan - 1) / 2);
      return { x: cellX - back, y: cellY - back };
    },

    /**
     * Whoever is standing on a cell. The figures no longer report themselves, and a
     * big body answers from anywhere under it, not only from its anchor corner.
     */
    objectsAtCell(cellX, cellY) {
      return this.visibleObjects.filter(obj =>
        footprintCoversCell(obj.x, obj.y, spanOf(obj), cellX, cellY)
      );
    },

    handleTokenClick(obj) {
      this.selectedObjectId = obj.id;
      this.$emit('object-selected', obj);
      this.render();
    },

    /**
     * How far the character is from a thing on the floor, in cells.
     *
     * Anchor to anchor, Chebyshev — matching the server exactly. Measuring
     * footprint-to-footprint would read better for a big chest, but the server
     * compares anchors, so a kinder rule here only promises reaches it will
     * then refuse.
     */
    interactDistance(obj) {
      const me = this.tacticalCharacterInfo;
      if (!me) return null;
      return Math.max(Math.abs(obj.x - me.x), Math.abs(obj.y - me.y));
    },

    /**
     * The cheapest cell within reach of a thing that the character could stand on.
     *
     * Searched over the cells movement already solved rather than over the ring
     * around the object, so the answer is a cell that is genuinely walkable,
     * genuinely affordable this turn, and reached by a route the pathfinder
     * agrees exists. Returns null when nothing qualifies — which is the honest
     * answer for loot behind a wall.
     */
    approachAnchorFor(obj) {
      const span = this.moverSpan;
      const occupied = occupiedKeys(this.visibleObjects, { id: this.playerCharacter?.id });

      let best = null;
      for (const cell of this.reachableCells.values()) {
        const distance = Math.max(Math.abs(obj.x - cell.x), Math.abs(obj.y - cell.y));
        if (distance > 1) continue;
        if (!blockFits(
          cell.x,
          cell.y,
          span,
          (x, y) => this.isCellPassable(x, y) && !occupied.has(`${x},${y}`)
        )) continue;
        if (!best || (cell.cost ?? 0) < (best.cost ?? 0)) best = cell;
      }
      return best ? { x: best.x, y: best.y } : null;
    },

    /**
     * Ask to open a chest or pick up loot.
     *
     * Standing next to it, the interaction happens on the spot in either phase.
     * Standing away from it during movement, the click means "go and do it" —
     * the character walks to the nearest cell within reach and the interaction
     * follows immediately, because clicking a thing you can see and being told
     * only that you are too far away is a dead end the player has to solve by
     * hand. In the action phase there is no walking left, so being out of reach
     * is simply a refusal, and it is refused here rather than server-side so no
     * AP is spent finding out.
     */
    handleInteractableClick(obj) {
      this.selectedObjectId = obj.id;
      this.render();

      const distance = this.interactDistance(obj);
      if (distance === null || distance <= 1) {
        this.emitInteraction(obj);
        return;
      }

      const noun = obj.type === 'chest' ? 'the chest' : 'it';
      if (!this.canMove) {
        this.showActionHint(
          this.isPreparationPhase
            ? `You already ended tactical movement this turn — too far from ${noun}.`
            : `Step next to ${noun} during the preparation phase.`,
          'warn'
        );
        return;
      }

      const anchor = this.approachAnchorFor(obj);
      if (!anchor) {
        this.showActionHint(`You cannot reach ${noun} this turn.`, 'warn');
        return;
      }

      this.$emit('interact-after-move', { object: obj, anchor });
    },

    emitInteraction(obj) {
      if (obj.type === 'chest') {
        this.$emit('chest-clicked', obj);
      } else {
        this.$emit('item-clicked', obj);
      }
    },

    /**
     * Is this body standing where the armed action cannot be taken?
     *
     * The shadow only reaches the floor - the tokens are DOM over the canvas and stay
     * fully lit through it, which leaves a figure out of range looking as available as
     * one in range, on visibly darker ground. So they recede with the ground they
     * stand on, and by the same rule: any cell of the body inside the reach counts,
     * because a large creature half in range is in range.
     */
    isOutOfCastReach(obj) {
      const reach = this.castReachVisible;
      if (!reach) return false;
      return !footprintCells(obj.x, obj.y, spanOf(obj))
        .some(cell => reach.has(`${cell.x},${cell.y}`));
    },

    /** Point the aim at a cell and refresh everything derived from it. */
    aimAtCell(cellX, cellY, point = null) {
      this.hoveredCell = { x: cellX, y: cellY };
      this.lastHoveredKey = `${cellX},${cellY}`;
      this.aimPoint = point || { x: cellX + 0.5, y: cellY + 0.5 };
      this.skillTargetPosition = { x: cellX, y: cellY };
      this.calculateSkillAffectedCells(cellX, cellY);
      this.$emit('cell-hovered', [...this.aimedCharacterIds]);
      this.render();
    },

    clearAim() {
      this.skillTargetPosition = null;
      this.skillAffectedCells = [];
      this.skillTargetDistance = null;
      this.aimPoint = null;
      this.clearAimPreview();
      this.$emit('cell-hovered', []);
    },

    /** Highlight whoever stands on the hovered cell, with no ability armed. */
    updateCellHover() {
      const standing = this.hoveredCell
        ? this.objectsAtCell(this.hoveredCell.x, this.hoveredCell.y)
        : [];
      const character = standing.find(obj => obj.type === 'character') || null;

      this.hoveredObjectId = character ? character.id : null;
      this.$emit('cell-hovered', character ? [character.id] : []);
    },

    /** Select a token from outside the map (e.g. the character card strip). */
    selectObjectById(objectId) {
      const obj = this.visibleObjects.find(candidate => candidate.id === objectId);
      this.selectedObjectId = obj ? obj.id : null;
      this.render();
      return !!obj;
    },

    /** Highlight a token from outside the map without selecting it. */
    highlightObjectById(objectId) {
      this.hoveredObjectId = objectId;
      this.render();
    },

    /*
     * A world cycle only moves the board when the character is not sealed in a
     * fight. Inside one, `character::fight_round_advanced` is the tick that
     * matters and a world cycle arriving alongside it would refresh the board
     * against a clock it no longer runs on.
     */
    async handleNewCycle(data) {
      if (this.inFight) {
        return;
      }
      return this.refreshForRound(data);
    },

    /** The fight clock's own tick, scoped to the fight this board belongs to. */
    async handleFightRoundAdvanced(data) {
      if (this.fightId && data?.fight_id && data.fight_id !== this.fightId) {
        return;
      }
      const me = this.playerCharacter?.id;
      if (me && data?.character_id && data.character_id !== me) {
        return;
      }
      return this.refreshForRound(data);
    },

    // Refresh alive status, tactical info, vision and reach for a new round
    async refreshForRound(data) {
      try {
        console.log('[PlayerTacticalMap] New round event received:', data);

        // Re-initialize alive status for all characters
        await this.initializeAliveStatus();

        // Reload tactical character info (movement remaining, etc.)
        await this.loadTacticalCharacterInfo();

        // Effects tick on the cycle boundary, and blindness or a sight buff changes
        // the veil without anybody moving.
        await this.refreshVision();

        // Recalculate reachable cells
        this.calculateReachableCells();

        // Force re-render
        this.render();

        console.log('[PlayerTacticalMap] ✅ Updated for new cycle');
      } catch (error) {
        console.error('[PlayerTacticalMap] Error handling new cycle event:', error);
      }
    },

    // Handle character health/status changes from WebSocket
    async handleCharacterChanged(data) {
      try {
        if (!data || !this.tacticalMapData) {
          return;
        }

        console.log('[PlayerTacticalMap] Character changed event received:', data);

        // Update alive status if health data is provided
        if (data.current_health !== undefined || data.alive !== undefined) {
          const isAlive = data.alive !== undefined ? data.alive : (data.current_health > 0);

          // Update spawned objects array to trigger reactivity
          if (this.tacticalMapData.spawned_objects) {
            this.tacticalMapData.spawned_objects = this.tacticalMapData.spawned_objects.map(obj => {
              if (obj.id === data.character_id) {
                console.log('[PlayerTacticalMap] Updated alive status for', obj.name, ':', isAlive);
                return { ...obj, alive: isAlive };
              }
              return obj;
            });
          }

          // Force re-render
          this.render();
        }
      } catch (error) {
        console.error('[PlayerTacticalMap] Error handling character changed event:', error);
      }
    },

    async handleGameObjectPositionChanged(eventData) {
      try {
        if (!eventData) {
          return;
        }

        console.log('[PlayerTacticalMap] Game object position changed event received:', eventData);

        /* Nothing to move yet: the board is still on its way. Whoever moved may
           already have been at rest when the snapshot was taken, or may not - and
           the difference is not knowable here, so the load reconciles. */
        if (!this.tacticalMapData) {
          this.missedPositionEvent = true;
          return;
        }

        // Verify this event is for our current tactical map
        if (eventData.tactical_map_id !== this.tacticalMapData.id) {
          console.log('[PlayerTacticalMap] Event is for different tactical map, ignoring');
          return;
        }

        // Find the game object in spawned objects by game_object_id
        const movingObject = this.tacticalMapData.spawned_objects.find(
          obj => obj.id === eventData.game_object_id
        );

        if (!movingObject) {
          // Not "not on this map" - the fog withheld it, and the step it just took
          // may have brought it into sight. Ask the server what is visible now.
          console.log('[PlayerTacticalMap] Mover not currently visible, refreshing vision');
          await this.refreshVision();
          return;
        }

        console.log('[PlayerTacticalMap] Animating position change for:', movingObject.name || movingObject.id);

        // Animate the movement (runs concurrently with other animations)
        // Don't await - allow multiple animations to run in parallel
        this.animateTokenToPosition(
          movingObject,
          eventData.destination_x,
          eventData.destination_y
        ).then(async () => {
          console.log('[PlayerTacticalMap] Animation complete for:', movingObject.name || movingObject.id);

          // Update the spawned_objects array immutably to preserve reactivity
          // Find the index of the object
          const objectIndex = this.tacticalMapData.spawned_objects.findIndex(
            obj => obj.id === eventData.game_object_id
          );

          if (objectIndex !== -1) {
            // Create a new array with the updated object
            const updatedObjects = [...this.tacticalMapData.spawned_objects];
            updatedObjects[objectIndex] = {
              ...updatedObjects[objectIndex],
              x: eventData.destination_x,
              y: eventData.destination_y
            };

            // Replace the entire array to trigger reactivity
            this.tacticalMapData.spawned_objects = updatedObjects;

            console.log(
              `[PlayerTacticalMap] Updated ${movingObject.name || movingObject.id} position to (${eventData.destination_x}, ${eventData.destination_y})`
            );
          }

          // The viewer's own move also changes what they have left to spend, so the
          // AP has to come back from the server before the reachable set is redrawn.
          if (movingObject.id === this.playerCharacter?.id) {
            console.log('[PlayerTacticalMap] Player moved, updating tactical info');
            await this.loadTacticalCharacterInfo();

            // DON'T align camera to player - preserve user's camera position!
            // Camera state is persisted and should not reset on movement
          }

          // Anyone moving changes which cells can be landed on, not only the viewer:
          // someone stepping aside frees their cell, and stepping into one takes it
          // away. Leaving this to the viewer's own moves left the reachable set
          // holding cells that had since been taken.
          this.calculateReachableCells();
          this.render();

          // Sight is resolved from where everyone stands, and it is shared across the
          // party, so any mover changes the fog - not only the viewer. The server owns
          // that calculation, so the veil is only correct once it has been re-asked.
          await this.refreshVision();

          // No need to reload entire map - position already updated immutably
        });

      } catch (error) {
        console.error('[PlayerTacticalMap] Error handling game object position changed event:', error);
      }
    },

    /**
     * Walk a token from where it stands to where it now is.
     *
     * The route matters. A straight slide from A to B cuts the corner off every turn
     * and takes the figure through walls and through whoever it was walking around,
     * which is precisely the geometry the rest of the board is built to respect - so
     * a route recorded by `rememberRoute` is followed cell by cell when there is one,
     * and only an unannounced move falls back to a straight line.
     */
    async animateTokenToPosition(obj, destinationX, destinationY) {
      const route = this.routeFor(obj.id, destinationX, destinationY);
      const waypoints = route || [
        { x: obj.x, y: obj.y },
        { x: destinationX, y: destinationY }
      ];

      // One pace per cell, so a long walk reads as a longer walk, within reason.
      const legs = Math.max(1, waypoints.length - 1);
      const duration = Math.min(1200, Math.max(260, legs * 170));
      const started = performance.now();

      await new Promise(resolve => {
        const step = (now) => {
          const progress = Math.min(1, (now - started) / duration);

          // Where along the whole route we are, in legs.
          const travelled = progress * legs;
          const leg = Math.min(legs - 1, Math.floor(travelled));
          const within = travelled - leg;
          const from = waypoints[leg];
          const to = waypoints[leg + 1];

          this.tokenPositionOverrides = {
            ...this.tokenPositionOverrides,
            [obj.id]: {
              x: from.x + (to.x - from.x) * within,
              y: from.y + (to.y - from.y) * within
            }
          };

          // The figure is a DOM sprite and its contact shadow is painted on the
          // canvas. Moving one without redrawing the other leaves the shadow behind
          // on the cell the token has left, and the token reads as flying.
          this.render();

          if (progress < 1) {
            this.tokenAnimationFrame = requestAnimationFrame(step);
          } else {
            resolve();
          }
        };
        this.tokenAnimationFrame = requestAnimationFrame(step);
      });

      // Clear the override after animation - create new object without this character
      const { [obj.id]: removed, ...rest } = this.tokenPositionOverrides;
      this.tokenPositionOverrides = rest;
      this.render();
    },

    /**
     * Keep the route a mover was reported to have walked.
     *
     * The position-changed event says only where a body ended up. The route is
     * announced separately and usually arrives first, so it is held until the
     * position change it belongs to turns up, and dropped once it has been used.
     */
    rememberRoute(data) {
      if (!data || !Array.isArray(data.path) || data.path.length < 2) return;
      const id = data.character_id;
      if (!id) return;

      this.pendingRoutes = {
        ...this.pendingRoutes,
        [id]: data.path.map(([x, y]) => ({ x, y }))
      };
    },

    /** The remembered route for a mover, if it ends where the move ended. */
    routeFor(objectId, destinationX, destinationY) {
      const route = this.pendingRoutes[objectId];
      if (!route) return null;

      const { [objectId]: used, ...rest } = this.pendingRoutes;
      this.pendingRoutes = rest;

      const end = route[route.length - 1];
      if (end.x !== destinationX || end.y !== destinationY) return null;
      return route;
    },

    toggleGrid() {
      this.gridVisible = !this.gridVisible;
      this.render();
    },

    togglePassableHighlight() {
      this.passableHighlightVisible = !this.passableHighlightVisible;
      this.render();
    },

    // Dijkstra pathfinding to calculate all reachable cells from player
    calculateReachableCells() {
      this.reachableCells.clear();

      if (!this.tacticalMapData || !this.scaledGridConfig) {
        return;
      }

      // Get player position and movement range from tactical info (priority)
      let startX, startY, maxCost;

      if (this.tacticalCharacterInfo) {
        // PRIORITY 1: Use tactical character info from API (most accurate)
        startX = this.tacticalCharacterInfo.x;
        startY = this.tacticalCharacterInfo.y;
        maxCost = this.tacticalCharacterInfo.movement_remaining;
        console.log(`[Calculate] Using tactical info: position (${startX}, ${startY}), movement remaining: ${maxCost}`);
      } else if (this.playerCharacter) {
        // PRIORITY 2: Fallback to playerCharacter prop (less accurate)
        startX = this.playerCharacter.x || 0;
        startY = this.playerCharacter.y || 0;
        // The server spends MOVE_AP_PER_CELL = 2 per cell and reports the reach as
        // `current_ap // 2` (CharacterService.get_tactical_info). Subtracting one
        // here cost the player their last cell whenever the tactical payload was
        // still in flight.
        const actionPoints = this.playerCharacter.action_points || 10;
        maxCost = Math.floor(actionPoints / 2);
        console.log(`[Calculate] Using playerCharacter fallback: position (${startX}, ${startY}), calculated movement: ${maxCost} from ${actionPoints} AP`);
      } else {
        console.warn('[Calculate] No character info available for pathfinding');
        return;
      }

      console.log(`[Calculate] Starting pathfinding from (${startX}, ${startY}) with max cost ${maxCost}`);

      // Anyone standing in the way stops the route there. Occupied ground is simply
      // not walked: it is neither entered nor crossed, so the reach ends at whoever
      // is in it and has to go round. Excluding it only as a destination left the
      // area with a hole punched in it and routes threading straight through people.
      // A body closes every cell it stands on, not just the one it is anchored to,
      // and the mover's own bulk is not a wall to itself.
      const moverSpan = this.moverSpan;
      const ownKeys = new Set(footprintKeys(startX, startY, moverSpan));
      const occupied = new Set();
      this.visibleObjects.forEach(obj => {
        footprintKeys(obj.x, obj.y, spanOf(obj)).forEach(key => {
          if (!ownKeys.has(key)) occupied.add(key);
        });
      });

      // Priority queue: [cost, x, y, path]
      const queue = [[0, startX, startY, []]];
      const visited = new Set();

      while (queue.length > 0) {
        // Sort by cost (simple priority queue)
        queue.sort((a, b) => a[0] - b[0]);
        const [cost, x, y, path] = queue.shift();

        const key = `${x},${y}`;
        if (visited.has(key)) continue;
        visited.add(key);

        // Store reachable cell with its cost and path
        this.reachableCells.set(key, { x, y, cost, path: [...path, { x, y }] });

        // Don't expand if we've reached max cost
        if (cost >= maxCost) continue;

        // Get all valid edges from this cell
        const edges = this.getEdgesFromCell(x, y);

        edges.forEach(edge => {
          const toKey = `${edge.toX},${edge.toY}`;
          if (visited.has(toKey)) return;

          // IMPORTANT: only step where the whole body fits. A standard creature
          // asks about one cell; a boss asks about every cell under its bulk, so
          // it will not squeeze through a gap its shoulders cannot clear.
          const fits = blockFits(
            edge.toX,
            edge.toY,
            moverSpan,
            (x, y) => this.isCellPassable(x, y) && !occupied.has(`${x},${y}`)
          );
          if (!fits) {
            return;
          }

          // Calculate movement cost (simplified - could be terrain-based)
          const cellCost = 1;
          const newCost = cost + cellCost;

          if (newCost <= maxCost) {
            queue.push([newCost, edge.toX, edge.toY, [...path, { x, y }]]);
          }
        });
      }

      console.log(`[Calculate] ✅ Pathfinding complete! Found ${this.reachableCells.size} reachable cells`);

      // Every route drawn from the old anchor is now a lie. The reach is rebuilt
      // whenever the body moves or its AP changes, so the cached hover route has to
      // be re-derived here rather than waiting for the pointer to cross a cell
      // boundary — after a move the pointer is usually exactly where it was, no
      // mousemove ever arrives, and the trace hangs on the board pointing at the
      // cell the character has already left.
      this.refreshHoveredPath();
    },

    /**
     * The route to whatever the pointer is over, against the current reach.
     *
     * Null whenever there is nothing to draw: no hover, no right to move, or a
     * hovered cell the body can no longer afford.
     */
    refreshHoveredPath() {
      if (!this.hoveredCell || !this.canMove) {
        this.pathToHovered = null;
        return;
      }

      const anchor = this.moveAnchorFor(this.hoveredCell.x, this.hoveredCell.y);
      const reachable = this.reachableCells.get(`${anchor.x},${anchor.y}`);
      this.pathToHovered = reachable ? reachable.path : null;
    },

    // Get all valid edges from a cell
    getEdgesFromCell(x, y) {
      if (!this.tacticalMapData || !this.tacticalMapData.edges) {
        return [];
      }

      // NEW API STRUCTURE: Use flat fields (from_x, from_y, to_x, to_y)
      return this.tacticalMapData.edges
        .filter(edge =>
          edge &&
          edge.from_x === x &&
          edge.from_y === y &&
          !edge.blocked
        )
        .map(edge => ({
          toX: edge.to_x,
          toY: edge.to_y
        }));
    },

    // ========== SKILL/ITEM TARGETING METHODS ==========

    handleKeyDown(event) {
      // Keyboard controls for skill rotation
      if (!this.selectedAbility) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.rotateSkillPattern(-90);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.rotateSkillPattern(90);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.cancelSkillSelection();
      }
    },

    selectSkill(skill) {
      this.selectedSkill = skill;
      this.selectedItem = null;
      this.selectedSpecial = null;
      this.skillRotation = 0;
      this.skillTargetPosition = null;
      this.skillAffectedCells = [];
      this.skillTargetDistance = null;

      // Initialize multi-charge system
      this.chargeCount = this.getAbilityChargeCount(skill);
      this.chargesPlaced = 0;
      this.chargeTargets = [];
      this.currentChargeRotation = 0;

      // Emit initial charge state
      this.$emit('charges-updated', {
        chargesPlaced: this.chargesPlaced,
        chargeCount: this.chargeCount
      });

      console.log('[Skill] Selected skill:', skill.name, 'with', this.chargeCount, 'charges');
      if (this.castOnSelf(skill)) return;
      this.startSkillAnimation();
    },

    selectItem(item) {
      this.selectedItem = item;
      this.selectedSkill = null;
      this.selectedSpecial = null;
      this.skillRotation = 0;
      this.skillTargetPosition = null;
      this.skillAffectedCells = [];
      this.skillTargetDistance = null;

      // Initialize multi-charge system
      this.chargeCount = this.getAbilityChargeCount(item);
      this.chargesPlaced = 0;
      this.chargeTargets = [];
      this.currentChargeRotation = 0;

      console.log('[Item] Selected item:', item.name, 'with', this.chargeCount, 'charges');
      if (this.castOnSelf(item)) return;
      this.startSkillAnimation();
    },

    selectSpecial(special) {
      this.selectedSpecial = special;
      this.selectedSkill = null;
      this.selectedItem = null;
      this.skillRotation = 0;
      this.skillTargetPosition = null;
      this.skillAffectedCells = [];
      this.skillTargetDistance = null;

      // Initialize multi-charge system
      this.chargeCount = this.getAbilityChargeCount(special);
      this.chargesPlaced = 0;
      this.chargeTargets = [];
      this.currentChargeRotation = 0;

      console.log('[Special] Selected special action:', special.name || special.action_type, 'with', this.chargeCount, 'charges');
      if (this.castOnSelf(special)) return;
      this.startSkillAnimation();
    },

    /**
     * An ability done to its own user is cast the moment it is picked.
     *
     * Its aim is the ground the caster already stands on, so there is nothing
     * to point at — and with a reach of zero every other cell on the board is
     * out of range, which left the player armed with something that refused
     * every click and never fired. Returns whether it handled the cast.
     */
    castOnSelf(ability) {
      if (!isSelfTargetAbility(ability)) return false;
      const cell = this.casterCell();
      if (!cell) return false;

      this.chargeTargets = selfTargetAims(cell, this.chargeCount).map(aim => ({
        ...aim,
        affectedCells: this.calculateChargeCells(aim.x, aim.y, 0),
      }));
      this.chargesPlaced = this.chargeTargets.length;
      this.$emit('charges-updated', {
        chargesPlaced: this.chargesPlaced,
        chargeCount: this.chargeCount,
      });
      this.castAbility();
      return true;
    },

    casterCell() {
      const x = this.tacticalCharacterInfo?.x
          ?? this.playerCharacter?.tactical_position?.x
          ?? this.playerCharacter?.x;
      const y = this.tacticalCharacterInfo?.y
          ?? this.playerCharacter?.tactical_position?.y
          ?? this.playerCharacter?.y;
      if (typeof x !== 'number' || typeof y !== 'number') return null;
      return {x, y};
    },

    cancelSkillSelection() {
      this.selectedSkill = null;
      this.selectedItem = null;
      this.selectedSpecial = null;
      this.skillRotation = 0;
      this.skillTargetPosition = null;
      this.skillAffectedCells = [];
      this.skillTargetDistance = null;
      this.clearAimPreview();
      this.stopSkillAnimation();
      this.render();
      console.log('[Skill] Cancelled skill/item selection');
    },

    /**
     * Keeps the murk moving.
     *
     * Held to a fraction of the display's rate on purpose: the fog drifts slowly and
     * nothing else on the board changes between these frames, so redrawing it at full
     * refresh would spend the frame budget of the whole map on a haze. It also stands
     * down entirely while an ability is armed, since that loop is already redrawing.
     */
    startFogAnimation() {
      if (this.fogAnimationFrameId || this.prefersReducedMotion) return;
      this.animateFog();
    },

    stopFogAnimation() {
      if (this.fogAnimationFrameId) {
        cancelAnimationFrame(this.fogAnimationFrameId);
        this.fogAnimationFrameId = null;
      }
    },

    animateFog() {
      const now = performance.now();
      if (!this.selectedAbility && now - this.fogLastFrameAt >= FOG_FRAME_INTERVAL) {
        this.fogLastFrameAt = now;
        this.render();
      }

      this.fogAnimationFrameId = this.fogActive && !this.prefersReducedMotion
        ? requestAnimationFrame(() => this.animateFog())
        : null;
    },

    startSkillAnimation() {
      // Start animation loop for pulsing skill preview
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
      }

      // Warm the field before the player starts aiming with it. The shared lattice
      // and the first frame of each range state cost about 35 ms the first time;
      // paid here it lands on the arming transition, where a beat is expected,
      // instead of on the first pointer move across maximum range.
      const ability = this.selectedAbility;
      if (ability) {
        this.primeAimFlow(this.aimFlowFrames(this.aimColor(ability), true));
        this.primeAimFlow(this.aimFlowFrames(AIM_DENIED_COLOR, false));
      }

      this.animateSkillPreview();
    },

    stopSkillAnimation() {
      // Stop animation loop
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
    },

    /**
     * Redraws while an ability is armed. It carries no clock of its own any more:
     * a counter climbing a fixed step per frame ran at whatever rate the display
     * happened to refresh at, and everything keyed to it moved faster on a faster
     * screen. Both moving parts - the field's rings and the motes on the firing
     * line - read wall time directly, so this only has to ask for the redraw.
     */
    animateSkillPreview() {
      this.render();

      if (this.selectedAbility) {
        this.animationFrameId = requestAnimationFrame(() => this.animateSkillPreview());
      }
    },

    rotateSkillPattern(degrees) {
      this.skillRotation = (this.skillRotation + degrees + 360) % 360;
      if (this.skillTargetPosition) {
        this.calculateSkillAffectedCells(this.skillTargetPosition.x, this.skillTargetPosition.y);
      }
      this.render();
      console.log('[Skill] Rotated to:', this.skillRotation);
    },

    async castAbility() {
      const ability = this.selectedSkill || this.selectedItem || this.selectedSpecial;
      if (!ability || !this.playerCharacter) {
        return;
      }

      const chargeCount = this.getAbilityChargeCount(ability);

      // Verify all charges are placed
      if (this.chargesPlaced !== chargeCount) {
        console.warn(`[MultiCharge] Need ${chargeCount} charges, only ${this.chargesPlaced} placed`);
        return;
      }

      // Always build tactical_aims array (even for single charge)
      const tacticalAims = this.chargeTargets.map(charge => ({
        x: charge.x,
        y: charge.y,
        rotation: charge.rotation
      }));

      console.log(`[Tactical] Casting ability with ${tacticalAims.length} charges:`, tacticalAims);

      // Emit with new tactical_aims format. `charges` rides along carrying the cells
      // the board actually painted, so a listener that wants to draw the cast — the
      // duel bench does — cannot resolve a different footprint than the player saw.
      this.$emit('ability-cast', {
        ability: ability,
        data: { tactical_aims: tacticalAims },
        charges: this.chargeTargets.map(charge => ({ ...charge }))
      });

      // Clean up
      this.cancelCharges();
    },

    calculateGridDistance(x1, y1, x2, y2) {
      // Chebyshev distance (chess distance) - max of dx and dy
      const dx = Math.abs(x1 - x2);
      const dy = Math.abs(y1 - y2);
      return Math.max(dx, dy);
    },

    /**
     * The one place this component turns a board cell into a pattern cell.
     *
     * `services/impact` speaks {col,row}; the board speaks {x,y} everywhere else.
     * The two conventions meet here and nowhere else — everything handed back to the
     * board is board coordinates again, so neither convention leaks into the other.
     *
     * The anchor and the rotation are the SERVER's, not the map's:
     * `SkillTargetingCalculator._apply_spray_pattern` pins the matrix by its geometric
     * centre (`len // 2`) and turns the offsets about that centre, ignoring the `x`/`o`
     * marker when it places the pattern. The marker survives only as "is this cell
     * hit" — `1` and `x` are hit, `0` and `o` are not.
     */
    resolveAbilityAim(targetX, targetY, rotation) {
      const config = this.scaledGridConfig;
      const aim = resolveAim({
        ability: this.selectedAbility,
        target: { col: targetX, row: targetY },
        rotation,
        columns: config?.columns ?? Infinity,
        rows: config?.rows ?? Infinity,
        // Per affected cell, not only the pivot: a blast has to stop at the wall it is
        // thrown against instead of covering the room behind it.
        passable: (col, row) => this.isCellPassable(col, row),
        anchor: this.aimAnchor || 'center',
      });
      if (aim.targeting || !this.selectedAbility) return aim;

      // No pattern at all is not "no cells": the server resolves a bare ability to the
      // single target cell (`if not spray_pattern: return [target_pos]`).
      const single = { col: targetX, row: targetY, isAnchor: true };
      const open = this.isCellPassable(targetX, targetY);
      return { ...aim, cells: open ? [single] : [], blocked: open ? [] : [single] };
    },

    /**
     * The cells one aim covers, in board coordinates.
     *
     * Both the hover preview and a placed charge come through here, so what the player
     * saw and what the player placed cannot disagree — they are the same call.
     *
     * Cells clipped away by the floor come back only when the pivot ITSELF is refused:
     * that is the case the aim already paints in the denied colour, and a pattern that
     * silently shrank to nothing beside a wall would read as the aim having been lost
     * rather than as the shot being impossible.
     */
    aimCellsFor(targetX, targetY, rotation) {
      const { cells, blocked } = this.resolveAbilityAim(targetX, targetY, rotation);
      const shown = this.isCellPassable(targetX, targetY) ? cells : cells.concat(blocked);
      return shown.map(cell => ({ x: cell.col, y: cell.row, isPivot: !!cell.isAnchor }));
    },

    calculateSkillAffectedCells(targetX, targetY) {
      const ability = this.selectedAbility;
      if (!ability || !this.playerCharacter) {
        this.skillAffectedCells = [];
        this.skillTargetDistance = null;
        this.clearAimPreview();
        return;
      }

      const skill = ability.spray_config ? ability : (ability.item?.skill || ability);
      const minRange = skill.min_range || 0;
      const maxRange = skill.max_range || Infinity;

      const playerX = this.tacticalCharacterInfo?.x ?? this.playerCharacter.x ?? 0;
      const playerY = this.tacticalCharacterInfo?.y ?? this.playerCharacter.y ?? 0;

      const distance = this.calculateGridDistance(playerX, playerY, targetX, targetY);
      this.skillTargetDistance = distance;
      const isInRange = distance >= minRange && distance <= maxRange;

      this.skillAffectedCells = this.aimCellsFor(targetX, targetY, this.skillRotation)
        .map(cell => {
          const objectAtCell = this.visibleObjects.find(obj =>
            footprintCoversCell(obj.x, obj.y, spanOf(obj), cell.x, cell.y)
          );
          let color = 'gold';
          if (objectAtCell) {
            color = objectAtCell.id === this.playerCharacter.id ? 'blue' : 'red';
          }
          return { ...cell, valid: isInRange, color };
        });

      this.updateAimPreview({ col: playerX, row: playerY }, { col: targetX, row: targetY });
    },

    getViolationTypeColor(ability) {
      // Get impact violation type from ability
      let violationType = '';

      // Extract from skill impact array
      if (ability?.impact && Array.isArray(ability.impact) && ability.impact.length > 0) {
        violationType = ability.impact[0].type; // Use first impact's type
      } else if (ability?.item?.skill?.impact && Array.isArray(ability.item.skill.impact) && ability.item.skill.impact.length > 0) {
        violationType = ability.item.skill.impact[0].type;
      }

      // Convert to lowercase for matching
      violationType = violationType?.toLowerCase() || '';

      // ImpactViolationType color mapping for beam (corresponds to backend enum)
      const violationColors = {
        'physical': { r: 180, g: 180, b: 180 },   // Silver-gray (kinetic)
        'mental': { r: 150, g: 0, b: 255 },       // Purple (psychic)
        'energy': { r: 0, g: 255, b: 255 },       // Cyan (pure energy)
        'heat': { r: 255, g: 80, b: 0 },          // Bright orange-red (fire)
        'cold': { r: 100, g: 200, b: 255 },       // Ice blue
        'light': { r: 255, g: 255, b: 220 },      // Bright yellow-white
        'darkness': { r: 80, g: 0, b: 120 },      // Deep purple-black
        'none': { r: 100, g: 255, b: 150 },       // Soft green (healing/buffing)
      };

      return violationColors[violationType] || { r: 0, g: 255, b: 200 }; // Default cyan
    },

    abilitySkillType(ability) {
      if (ability?.type) return ability.type.toLowerCase();
      if (ability?.item?.skill?.type) return ability.item.skill.type.toLowerCase();

      const actionType = ability?.action_type?.toLowerCase();
      if (!actionType) return '';
      if (actionType === 'inspect' || actionType === 'snatch_item') return 'utility';
      if (actionType === 'bargain') return 'utility';
      if (actionType === 'long_rest') return 'heal';
      return 'special';
    },

    aimIntent(ability) {
      const skillType = this.abilitySkillType(ability);
      if (skillType === 'attack' || skillType === 'debuff') return 'harm';
      if (skillType === 'heal' || skillType === 'buff' || skillType === 'defense') return 'boon';
      return 'neutral';
    },

    aimColor(ability) {
      return AIM_COLORS[this.aimIntent(ability)];
    },

    /**
     * The three stops the aim is drawn in: the skill's own material, pulled toward
     * what it is being used for.
     *
     * `aimColor` remains for the things that are NOT the field — the range boundary's
     * hairline and a charge's ordinal — because those are annotations rather than the
     * effect, and annotating a Heat skill's range in orange makes the whisper read as
     * part of the fire.
     */
    aimMaterial(ability, valid) {
      return aimPalette(ability, { valid });
    },

    // Multi-charge Helper Methods
    getAbilityChargeCount(ability) {
      // Extract charge_count from ability object
      if (!ability) return 1;
      return ability.charge_count || 1;
    },

    isWithinRange(targetX, targetY) {
      // Validate if target cell is within skill's min/max range
      if (!this.playerCharacter?.tactical_position && !this.tacticalCharacterInfo) {
        console.warn('[Range] No player position available');
        return false;
      }

      // Get caster position
      const casterX = this.tacticalCharacterInfo?.x ?? this.playerCharacter.tactical_position?.x ?? this.playerCharacter.x ?? 0;
      const casterY = this.tacticalCharacterInfo?.y ?? this.playerCharacter.tactical_position?.y ?? this.playerCharacter.y ?? 0;

      // Get ability and its range
      const ability = this.selectedSkill || this.selectedItem || this.selectedSpecial;
      if (!ability) return true; // If no ability selected, allow (shouldn't happen)

      let minRange = 0;
      let maxRange = Infinity;

      // Extract range from ability or nested item.skill structure
      if (ability.min_range !== undefined) {
        minRange = ability.min_range;
      } else if (ability.item?.skill?.min_range !== undefined) {
        minRange = ability.item.skill.min_range;
      }

      if (ability.max_range !== undefined) {
        maxRange = ability.max_range;
      } else if (ability.item?.skill?.max_range !== undefined) {
        maxRange = ability.item.skill.max_range;
      }

      // Calculate Chebyshev distance (max of dx, dy)
      const dx = Math.abs(targetX - casterX);
      const dy = Math.abs(targetY - casterY);
      const distance = Math.max(dx, dy);

      const inRange = distance >= minRange && distance <= maxRange;

      if (!inRange) {
        console.log(`[Range] Target (${targetX},${targetY}) out of range. Distance: ${distance}, Min: ${minRange}, Max: ${maxRange}`);
      }

      return inRange;
    },

    placeCharge(targetX, targetY) {
      // Validate range before placing charge
      if (!this.isWithinRange(targetX, targetY)) {
        const ability = this.selectedSkill || this.selectedItem || this.selectedSpecial;
        const abilityName = ability?.name || ability?.item?.name || 'This ability';
        const minRange = ability?.min_range ?? ability?.item?.skill?.min_range ?? 0;
        const maxRange = ability?.max_range ?? ability?.item?.skill?.max_range ?? '∞';

        this.showActionHint(`Out of range — ${abilityName} reaches ${minRange}–${maxRange} cells.`, 'warn');
        return;
      }

      // Place one charge at the specified position
      const affectedCells = this.calculateChargeCells(targetX, targetY, this.currentChargeRotation);

      this.chargeTargets.push({
        x: targetX,
        y: targetY,
        rotation: this.currentChargeRotation,
        affectedCells: affectedCells
      });

      this.chargesPlaced++;
      console.log(`[MultiCharge] Placed charge ${this.chargesPlaced}/${this.chargeCount}`);

      // Emit charge update to parent
      this.$emit('charges-updated', {
        chargesPlaced: this.chargesPlaced,
        chargeCount: this.chargeCount
      });

      // Auto-cast when all charges are placed
      if (this.chargesPlaced === this.chargeCount) {
        console.log('[MultiCharge] All charges placed, auto-casting ability');
        this.castAbility();
      } else {
        // Reset rotation for next charge
        this.currentChargeRotation = 0;
        this.render();
      }
    },

    /**
     * A committed charge covers exactly what the hover preview showed it would.
     *
     * The old reader here looked at `ability.spray_pattern` — a field the API has never
     * sent. `spray_pattern` is only a LOCAL NAME on the server for
     * `spray_config["spray"]` (targeting.py, tactical_target_resolver.py); there is one
     * field, and it is `spray_config.spray`. So every charge silently fell back to a
     * single cell while the preview drew the real pattern.
     */
    calculateChargeCells(targetX, targetY, rotation) {
      return this.aimCellsFor(targetX, targetY, rotation);
    },

    undoLastCharge() {
      // Remove the last placed charge
      if (this.chargeTargets.length > 0) {
        this.chargeTargets.pop();
        this.chargesPlaced--;
      }
    },

    cancelCharges() {
      // Clear all charges and reset selection
      this.chargeCount = 1;
      this.chargesPlaced = 0;
      this.chargeTargets = [];
      this.currentChargeRotation = 0;

      // Clear selections
      this.selectedSkill = null;
      this.selectedItem = null;
      this.selectedSpecial = null;
      this.targetCharacterId = null;
      this.clearAimPreview();
    },

    /**
     * The aim's second layer: the effect's own material, faintly, on the ground it
     * would cover.
     *
     * Held on the stage as one `preview`-phase effect under a fixed id, so a moving
     * aim replaces it instead of stacking. It is rebuilt only when the covered ground
     * changes — `setEffect` re-rasterises the whole topology, which is far too much
     * work to repeat on every pointer move — and it is masked to what the party can
     * see for the same reason a cast is: the fog is painted BELOW this layer and
     * cannot cover anything drawn on it.
     */
    updateAimPreview(caster, target) {
      if (!this.impactLayer) return;

      const ability = this.selectedAbility;
      const cells = maskCellsToVision(
        this.skillAffectedCells
          .filter(cell => cell.valid)
          .map(cell => ({ col: cell.x, row: cell.y })),
        this.visibleCellKeys
      );
      if (!ability || !cells.length) {
        this.clearAimPreview();
        return;
      }

      const signature = [
        ability.id || ability.action_type || ability.name,
        /* The caster is in the key because the trail is built from it: a figure that
           moved while an aim was held would otherwise keep firing from where it was. */
        `${caster.col},${caster.row}`,
        cells.map(cell => `${cell.col},${cell.row}`).join(';'),
      ].join('|');
      if (signature === this.aimPreviewSignature) return;
      this.aimPreviewSignature = signature;

      const cast = castPresentation(ability, { caster, target, cells }, {
        id: AIM_PREVIEW_ID,
        defaults: this.activeRenderDefaults || BATTLE_RENDER_DEFAULTS,
      });
      this.impactLayer.setEffect(previewEffect(cast, { id: AIM_PREVIEW_ID, cells }));
      this.impactLayer.setPhase('preview', AIM_PREVIEW_ID);
    },

    clearAimPreview() {
      if (!this.aimPreviewSignature) return;
      this.aimPreviewSignature = null;
      this.impactLayer?.removeEffect(AIM_PREVIEW_ID);
    }
  }
};
</script>

<style scoped>
.player-tactical-map {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #0a0a0a;
}

.tactical-canvas {
  transform: translate(var(--board-shake-x, 0px), var(--board-shake-y, 0px));
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  cursor: default;
  z-index: 1; /* Base layer within tactical map */
}

/* Token overlay - transparent to clicks except for tokens themselves */
.tokens-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none; /* Pass through clicks to canvas */
  z-index: 2; /* Above canvas but below HUD */
}

/* HUD elements - on top within tactical map context */
.hud-element {
  position: absolute;
  z-index: 10; /* Above tokens within tactical map */
}

.pedestal-stage {
  transform: translate(var(--board-shake-x, 0px), var(--board-shake-y, 0px));
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none; /* the floor answers clicks, never the props on it */
  z-index: 1;
}

/* Same contract as the stage above: laid over the board at its own size, and never
   scaled by CSS - a canvas stretched by the zoom is drawn once at 1x and blown up,
   which is exactly the pasted-decal look the effects exist to avoid. */
.impact-stage {
  transform: translate(var(--board-shake-x, 0px), var(--board-shake-y, 0px));
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.token-wrapper {
  position: absolute;
  pointer-events: auto; /* Tokens are clickable */
  cursor: pointer;
  transition: left 0.15s ease-out, top 0.15s ease-out, filter 0.15s ease, opacity 0.18s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1; /* Relative to tokens-overlay */
  /* The anchor differs per token type - a hologram hangs off its pedestal plate, an
     item off its own centre - so the offset is supplied inline and only composed here.
     The choreography shift composes AFTER the anchor and is the only part of this the
     frame loop writes, so a body that has been thrown about returns to its cell by the
     two variables going back to zero rather than by anything recomputing layout. */
  transform:
    translate(-50%, var(--token-anchor-y, -92%))
    translate(var(--token-shift-x, 0px), var(--token-shift-y, 0px));
}

/* The figure is deliberately laid out larger than the box that holds it — it is drawn
   at the zoom's real pixel count and scaled back down — so the flex container must not
   try to fit it. Without this the height is compressed to the wrapper's and the token's
   aspect ratio, which every one of its stage fractions is measured against, collapses. */
.token-wrapper > .holotok {
  flex: none;
}

/* The canvas repaints instantly while panning; easing left/top would leave every token
   trailing up to ~100px behind the map for the whole drag. */
.tokens-overlay--panning .token-wrapper {
  transition: none;
}

.token-wrapper:hover,
.token-hovered {
  /* translate first: scaling before the -50% centring would also shift the token. */
  z-index: 40 !important;
}

.token-selected {
  z-index: 50 !important;
}

/* Labels sit above the portrait so they never cover the ground base below it. */
.token-label {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform-origin: bottom center;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
}

.token-self-marker {
  font-family: 'Orbitron', sans-serif;
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #0d0d0d;
  background: #ffd147;
  border-radius: 2px;
  padding: 0 3px;
  line-height: 1.4;
  text-shadow: none;
}

.token-player {
}

/* Allegiance is carried by the canvas-drawn ground base and the name colour - the
   portraits themselves stay unringed so the map does not turn into a wall of circles. */
.token-relation-self .token-name {
  color: #ffd147;
}

.token-relation-ally .token-name {
  color: #a7f3c4;
}

.token-relation-enemy .token-name {
  color: #ffb3b3;
}

/* The arrival preview: present enough to place the move, faint enough that it is
   never mistaken for the character actually being there. */
.token-ghost {
  pointer-events: none;
  opacity: 0.55;
}

/* Item token styling - visually distinct from character tokens */
.token-item {
  opacity: 0.85;
  z-index: 0; /* Lower z-index than character tokens */
}

.token-item:hover {
  transform: translate(-50%, var(--token-anchor-y, -92%)) scale(1.15);
  filter: drop-shadow(0 0 0.4rem rgba(255, 215, 0, 0.7));
  opacity: 1;
  z-index: 10;
}

/* While aiming, the pointer itself is hidden: the impact core sits exactly under it
   and IS the cursor now, so leaving the arrow on top only puts a piece of desktop
   furniture over the one thing the player is reading. Tokens carry their own inline
   cursor - see getTokenStyle - because an inline property would beat this rule. */
.tactical-canvas--aiming {
  cursor: none;
}

/* Under the aim the figure itself is already re-lit by HoloToken; the wrapper only
   lifts it clear of its neighbours and puts its name in the same colour, so the
   name strip and the board agree on who is about to be hit. */
.token-aimed {
  z-index: 20;
}

.token-aimed .token-name {
  color: var(--token-aim-color, #fff);
  font-weight: 700;
  letter-spacing: 0.02em;
}

/* Out of the armed action's reach: dimmed and cooled, never hidden. A player still
   has to be able to read who is standing there - the token is saying "not this one,
   not with this", not "not there". The transition is short enough that arming and
   disarming a skill reads as a light coming on rather than a fade. */
.token-wrapper.token-out-of-reach {
  opacity: 0.42;
  filter: saturate(0.55) brightness(0.82);
}

.token-name {
  font-size: 10px;
  color: #fff;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
}

/* Item name label - different color scheme */
.item-name {
  color: #ffd700;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9), 0 0 8px rgba(255, 215, 0, 0.4);
  font-weight: 600;
}

.action-hint {
  left: 50%;
  top: 12rem;
  bottom: auto;
  z-index: 2100;
  transform: translateX(-50%);
  max-width: 26rem;
  text-align: center;
  padding: 0.45rem 0.9rem;
  border-radius: 0.375rem;
  background: rgba(13, 13, 13, 0.82);
  border: 1px solid rgba(0, 255, 255, 0.35);
  box-shadow: 0 0.125rem 0.75rem rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  font-family: 'Orbitron', sans-serif;
  font-size: 0.75rem;
  letter-spacing: 0.02em;
  color: rgba(255, 255, 255, 0.9);
}

.action-hint--warn {
  border-color: rgba(255, 180, 60, 0.55);
  color: #ffd9a0;
}

.reel-skip-btn {
  /* Above the zoom pill, never over the board: this sat at left:50% and covered
     the cell the replay was drawing — the one thing the player is looking at. */
  position: absolute;
  right: 1.5rem;
  bottom: 4.75rem;
  z-index: 2100;
  padding: 0.45rem 1rem;
  border-radius: 0.375rem;
  background: rgba(13, 13, 13, 0.82);
  border: 1px solid rgba(0, 255, 255, 0.35);
  color: rgba(0, 255, 255, 0.85);
  font-family: 'Orbitron', sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  cursor: pointer;
  transition: color 0.16s ease, border-color 0.16s ease;
}

.reel-skip-btn:hover,
.reel-skip-btn:focus-visible {
  color: #00ffff;
  border-color: rgba(0, 255, 255, 0.6);
  outline: none;
}

.hud-hint-enter-active,
.hud-hint-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.hud-hint-enter-from,
.hud-hint-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(0.4rem);
}

@media (prefers-reduced-motion: reduce) {
  .hud-hint-enter-active,
  .hud-hint-leave-active {
    transition: none;
  }
}

/* Zoom HUD */
.zoom-hud {
  bottom: 1.5rem;
  right: 1.5rem;
  background: rgba(13, 13, 13, 0.6);
  border: 1px solid rgba(0, 255, 255, 0.3);
  border-radius: 0.375rem;
  padding: 0.4rem 0.625rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 0.125rem 0.5rem rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  transition: border-color 0.3s ease;
}

.zoom-btn {
  background: transparent;
  border: none;
  color: rgba(0, 255, 255, 0.7);
  font-size: 1rem;
  font-weight: 600;
  font-family: 'Orbitron', sans-serif;
  width: 1.75rem;
  height: 1.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 0.25rem;
}

.zoom-btn:hover:not(:disabled) {
  background: rgba(0, 255, 255, 0.15);
  color: #00ffff;
  box-shadow: 0 0 6px rgba(0, 255, 255, 0.2);
  transform: scale(1.05);
}

.zoom-btn:active:not(:disabled) {
  transform: scale(0.95);
}

.zoom-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.zoom-reset {
  font-size: 1rem;
  margin-left: 0.25rem;
}

.zoom-display {
  color: #00ffff;
  font-size: 0.75rem;
  font-weight: 600;
  font-family: 'Orbitron', sans-serif;
  min-width: 2.8rem;
  text-align: center;
  user-select: none;
}

/* Grid Controls HUD (Top Left) */
.grid-controls-hud {
  top: calc(1.5rem + var(--hud-top-inset, 0rem));
  left: 1.5rem;
  background: rgba(13, 13, 13, 0.82);
  border: 1px solid rgba(0, 255, 255, 0.3);
  border-radius: 0.375rem;
  padding: 0.4rem;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  box-shadow: 0 0.125rem 0.5rem rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  transition: border-color 0.3s ease;
}

.control-btn {
  background: transparent;
  border: none;
  color: rgba(0, 255, 255, 0.6);
  font-size: 0.6875rem;
  font-weight: 600;
  font-family: 'Orbitron', sans-serif;
  padding: 0.4rem 0.625rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  min-width: 5.625rem;
  text-align: left;
  border-radius: 0.25rem;
}

.control-btn:hover {
  background: rgba(0, 255, 255, 0.1);
  color: rgba(0, 255, 255, 0.9);
  transform: translateX(2px);
}

.control-btn.active {
  background: rgba(0, 255, 255, 0.15);
  color: #00ffff;
  box-shadow: 0 0 6px rgba(0, 255, 255, 0.2);
}

.control-btn.passable-toggle.active {
  background: rgba(0, 255, 100, 0.15);
  color: #00ff88;
  box-shadow: 0 0 6px rgba(0, 255, 100, 0.2);
}

.control-icon {
  font-size: 0.875rem;
  line-height: 1;
}

.control-label {
  font-size: 0.625rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.loading-overlay {
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #00d4ff;
}

.loading-overlay p {
  margin: 0;
  font-size: 16px;
}

/* Charge Counter HUD */
.charge-counter-hud {
  position: absolute;
  top: 50%;
  right: 20px;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.85);
  border: 2px solid #00d4ff;
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 212, 255, 0.4);
}

.charge-progress {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.charge-label {
  color: #00d4ff;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.charge-value {
  color: #fff;
  font-size: 20px;
  font-weight: bold;
}

.charge-dots {
  display: flex;
  gap: 6px;
}

.charge-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid #00d4ff;
  background: transparent;
  transition: all 0.3s ease;
}

.charge-dot.placed {
  background: #00d4ff;
  box-shadow: 0 0 8px rgba(0, 212, 255, 0.8);
}

.charge-counter-hud button {
  padding: 8px 12px;
  background: rgba(0, 212, 255, 0.2);
  border: 1px solid #00d4ff;
  color: #00d4ff;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.charge-counter-hud button:hover {
  background: rgba(0, 212, 255, 0.4);
  box-shadow: 0 0 8px rgba(0, 212, 255, 0.6);
}

.confirm-button {
  background: rgba(0, 255, 100, 0.2) !important;
  border-color: #00ff64 !important;
  color: #00ff64 !important;
}
</style>
